function _extends() {
  _extends =
    Object.assign ||
    function (target) {
      for (var i = 1; i < arguments.length; i++) {
        var source = arguments[i];
        for (var key in source) {
          if (Object.prototype.hasOwnProperty.call(source, key)) {
            target[key] = source[key];
          }
        }
      }
      return target;
    };
  return _extends.apply(this, arguments);
}

import React from "react";
import PropTypes from "prop-types";
import StyledNavigationBar from "./navigation-bar.style";
import Logger from "../../__internal__/utils/logger";
let deprecatedWarnTriggered = false;
const NavigationBar = ({
  navigationType = "light",
  isLoading = false,
  children,
  ariaLabel,
  stickyOffset = "0",
  stickyPosition,
  position,
  offset = "0",
  orientation,
  ...props
}) => {
  if (!deprecatedWarnTriggered && stickyPosition) {
    deprecatedWarnTriggered = true;
    Logger.deprecate(
      // eslint-disable-next-line max-len
      "The `stickyPosition` and `stickyOffset` props are deprecated and will soon be removed. You should use the `position`, `offset` and `orientation` props to achieve the same layout. The following codemods are available to help with updating your code: https://github.com/Sage/carbon-codemod/tree/master/transforms/remove-prop and https://github.com/Sage/carbon-codemod/tree/master/transforms/add-prop"
    );
  }

  return /*#__PURE__*/ React.createElement(
    StyledNavigationBar,
    _extends(
      {
        role: "navigation",
        "aria-label": ariaLabel,
        navigationType: navigationType,
        "data-component": "navigation-bar",
        stickyOffset: stickyOffset,
        stickyPosition: stickyPosition,
        position: position,
        offset: offset,
        orientation: orientation,
      },
      props
    ),
    !isLoading && children
  );
};

NavigationBar.propTypes = {
  /**
   * The CSS align-content property sets how the browser distributes space between and around content items
   * along the cross-axis of a flexbox container, and the main-axis of a grid container.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/align-content)
   */
  alignContent: PropTypes.oneOfType([
    PropTypes.oneOf([
      "-moz-initial",
      "baseline",
      "center",
      "end",
      "flex-end",
      "flex-start",
      "inherit",
      "initial",
      "normal",
      "revert",
      "space-around",
      "space-between",
      "space-evenly",
      "start",
      "stretch",
      "unset",
    ]),
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([
          "-moz-initial",
          "baseline",
          "center",
          "end",
          "flex-end",
          "flex-start",
          "inherit",
          "initial",
          "normal",
          "revert",
          "space-around",
          "space-between",
          "space-evenly",
          "start",
          "stretch",
          "unset",
          null,
        ]),
        PropTypes.shape({
          /**
           * Iterator
           */
          "__@iterator": PropTypes.func.isRequired,
          /**
           * Returns an <a> HTML anchor element and sets the name attribute to the text value
           * @param name
           */
          anchor: PropTypes.func.isRequired,
          /**
           * Returns a <big> HTML element
           */
          big: PropTypes.func.isRequired,
          /**
           * Returns a <blink> HTML element
           */
          blink: PropTypes.func.isRequired,
          /**
           * Returns a <b> HTML element
           */
          bold: PropTypes.func.isRequired,
          /**
           * Returns the character at the specified index.
           * @param pos The zero-based index of the desired character.
           */
          charAt: PropTypes.func.isRequired,
          /**
           * Returns the Unicode value of the character at the specified location.
           * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
           */
          charCodeAt: PropTypes.func.isRequired,
          /**
           * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
           * value of the UTF-16 encoded code point starting at the string element at position pos in
           * the String resulting from converting this object to a String.
           * If there is no element at that position, the result is undefined.
           * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
           */
          codePointAt: PropTypes.func.isRequired,
          /**
           * Returns a string that contains the concatenation of two or more strings.
           * @param strings The strings to append to the end of the string.
           */
          concat: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * endPosition – length(this). Otherwise returns false.
           */
          endsWith: PropTypes.func.isRequired,
          /**
           * Returns a <tt> HTML element
           */
          fixed: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the color attribute value
           */
          fontcolor: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the size attribute value
           */
          fontsize: PropTypes.func.isRequired,
          /**
           * Returns true if searchString appears as a substring of the result of converting this
           * object to a String, at one or more positions that are
           * greater than or equal to position; otherwise, returns false.
           * @param searchString search string
           * @param position If position is undefined, 0 is assumed, so as to search all of the String.
           */
          includes: PropTypes.func.isRequired,
          /**
           * Returns the position of the first occurrence of a substring.
           * @param searchString The substring to search for in the string
           * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
           */
          indexOf: PropTypes.func.isRequired,
          /**
           * Returns an <i> HTML element
           */
          italics: PropTypes.func.isRequired,
          /**
           * Returns the last occurrence of a substring in the string.
           * @param searchString The substring to search for.
           * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
           */
          lastIndexOf: PropTypes.func.isRequired,
          /**
           * Returns the length of a String object.
           */
          length: PropTypes.number.isRequired,
          /**
           * Returns an <a> HTML element and sets the href attribute value
           */
          link: PropTypes.func.isRequired,
          /**
           * Determines whether two strings are equivalent in the current locale.
           * @param that String to compare to target string
           */
          localeCompare: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an array containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          match: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an iterable of matches
           * containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          matchAll: PropTypes.func.isRequired,
          /**
           * Returns the String value result of normalizing the string into the normalization form
           * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
           * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
           * is "NFC"
           */
          normalize: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the end (right) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padEnd: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the start (left) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padStart: PropTypes.func.isRequired,
          /**
           * Returns a String value that is made from count copies appended together. If count is 0,
           * the empty string is returned.
           * @param count number of copies to append
           */
          repeat: PropTypes.func.isRequired,
          /**
           * Replaces text in a string, using a regular expression or search string.
           * @param searchValue A string to search for.
           * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
           */
          replace: PropTypes.func.isRequired,
          /**
           * Finds the first substring match in a regular expression search.
           * @param regexp The regular expression pattern and applicable flags.
           */
          search: PropTypes.func.isRequired,
          /**
           * Returns a section of a string.
           * @param start The index to the beginning of the specified portion of stringObj.
           * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
           * If this value is not specified, the substring continues to the end of stringObj.
           */
          slice: PropTypes.func.isRequired,
          /**
           * Returns a <small> HTML element
           */
          small: PropTypes.func.isRequired,
          /**
           * Split a string into substrings using the specified separator and return them as an array.
           * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
           * @param limit A value used to limit the number of elements returned in the array.
           */
          split: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * position. Otherwise returns false.
           */
          startsWith: PropTypes.func.isRequired,
          /**
           * Returns a <strike> HTML element
           */
          strike: PropTypes.func.isRequired,
          /**
           * Returns a <sub> HTML element
           */
          sub: PropTypes.func.isRequired,
          /**
           * Gets a substring beginning at the specified location and having the specified length.
           * @param from The starting position of the desired substring. The index of the first character in the string is zero.
           * @param length The number of characters to include in the returned substring.
           */
          substr: PropTypes.func.isRequired,
          /**
           * Returns the substring at the specified location within a String object.
           * @param start The zero-based index number indicating the beginning of the substring.
           * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
           * If end is omitted, the characters from start through the end of the original string are returned.
           */
          substring: PropTypes.func.isRequired,
          /**
           * Returns a <sup> HTML element
           */
          sup: PropTypes.func.isRequired,
          /**
           * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
           */
          toLocaleLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
           */
          toLocaleUpperCase: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to lowercase.
           */
          toLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string representation of a string.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to uppercase.
           */
          toUpperCase: PropTypes.func.isRequired,
          /**
           * Removes the leading and trailing white space and line terminator characters from a string.
           */
          trim: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimEnd: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimLeft: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimRight: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimStart: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
      ])
    ),
    PropTypes.object,
    PropTypes.shape({
      /**
       * Iterator
       */
      "__@iterator": PropTypes.func.isRequired,
      /**
       * Returns an <a> HTML anchor element and sets the name attribute to the text value
       * @param name
       */
      anchor: PropTypes.func.isRequired,
      /**
       * Returns a <big> HTML element
       */
      big: PropTypes.func.isRequired,
      /**
       * Returns a <blink> HTML element
       */
      blink: PropTypes.func.isRequired,
      /**
       * Returns a <b> HTML element
       */
      bold: PropTypes.func.isRequired,
      /**
       * Returns the character at the specified index.
       * @param pos The zero-based index of the desired character.
       */
      charAt: PropTypes.func.isRequired,
      /**
       * Returns the Unicode value of the character at the specified location.
       * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
       */
      charCodeAt: PropTypes.func.isRequired,
      /**
       * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
       * value of the UTF-16 encoded code point starting at the string element at position pos in
       * the String resulting from converting this object to a String.
       * If there is no element at that position, the result is undefined.
       * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
       */
      codePointAt: PropTypes.func.isRequired,
      /**
       * Returns a string that contains the concatenation of two or more strings.
       * @param strings The strings to append to the end of the string.
       */
      concat: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * endPosition – length(this). Otherwise returns false.
       */
      endsWith: PropTypes.func.isRequired,
      /**
       * Returns a <tt> HTML element
       */
      fixed: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the color attribute value
       */
      fontcolor: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the size attribute value
       */
      fontsize: PropTypes.func.isRequired,
      /**
       * Returns true if searchString appears as a substring of the result of converting this
       * object to a String, at one or more positions that are
       * greater than or equal to position; otherwise, returns false.
       * @param searchString search string
       * @param position If position is undefined, 0 is assumed, so as to search all of the String.
       */
      includes: PropTypes.func.isRequired,
      /**
       * Returns the position of the first occurrence of a substring.
       * @param searchString The substring to search for in the string
       * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
       */
      indexOf: PropTypes.func.isRequired,
      /**
       * Returns an <i> HTML element
       */
      italics: PropTypes.func.isRequired,
      /**
       * Returns the last occurrence of a substring in the string.
       * @param searchString The substring to search for.
       * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
       */
      lastIndexOf: PropTypes.func.isRequired,
      /**
       * Returns the length of a String object.
       */
      length: PropTypes.number.isRequired,
      /**
       * Returns an <a> HTML element and sets the href attribute value
       */
      link: PropTypes.func.isRequired,
      /**
       * Determines whether two strings are equivalent in the current locale.
       * @param that String to compare to target string
       */
      localeCompare: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an array containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      match: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an iterable of matches
       * containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      matchAll: PropTypes.func.isRequired,
      /**
       * Returns the String value result of normalizing the string into the normalization form
       * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
       * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
       * is "NFC"
       */
      normalize: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the end (right) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padEnd: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the start (left) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padStart: PropTypes.func.isRequired,
      /**
       * Returns a String value that is made from count copies appended together. If count is 0,
       * the empty string is returned.
       * @param count number of copies to append
       */
      repeat: PropTypes.func.isRequired,
      /**
       * Replaces text in a string, using a regular expression or search string.
       * @param searchValue A string to search for.
       * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
       */
      replace: PropTypes.func.isRequired,
      /**
       * Finds the first substring match in a regular expression search.
       * @param regexp The regular expression pattern and applicable flags.
       */
      search: PropTypes.func.isRequired,
      /**
       * Returns a section of a string.
       * @param start The index to the beginning of the specified portion of stringObj.
       * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
       * If this value is not specified, the substring continues to the end of stringObj.
       */
      slice: PropTypes.func.isRequired,
      /**
       * Returns a <small> HTML element
       */
      small: PropTypes.func.isRequired,
      /**
       * Split a string into substrings using the specified separator and return them as an array.
       * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
       * @param limit A value used to limit the number of elements returned in the array.
       */
      split: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * position. Otherwise returns false.
       */
      startsWith: PropTypes.func.isRequired,
      /**
       * Returns a <strike> HTML element
       */
      strike: PropTypes.func.isRequired,
      /**
       * Returns a <sub> HTML element
       */
      sub: PropTypes.func.isRequired,
      /**
       * Gets a substring beginning at the specified location and having the specified length.
       * @param from The starting position of the desired substring. The index of the first character in the string is zero.
       * @param length The number of characters to include in the returned substring.
       */
      substr: PropTypes.func.isRequired,
      /**
       * Returns the substring at the specified location within a String object.
       * @param start The zero-based index number indicating the beginning of the substring.
       * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
       * If end is omitted, the characters from start through the end of the original string are returned.
       */
      substring: PropTypes.func.isRequired,
      /**
       * Returns a <sup> HTML element
       */
      sup: PropTypes.func.isRequired,
      /**
       * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
       */
      toLocaleLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
       */
      toLocaleUpperCase: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to lowercase.
       */
      toLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string representation of a string.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to uppercase.
       */
      toUpperCase: PropTypes.func.isRequired,
      /**
       * Removes the leading and trailing white space and line terminator characters from a string.
       */
      trim: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimEnd: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimLeft: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimRight: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimStart: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
  ]),
  /**
   * The CSS align-items property sets the align-self value on all direct children as a group. The align-self
   * property sets the alignment of an item within its containing block.
   *
   * In Flexbox it controls the alignment of items on the Cross Axis, in Grid Layout it controls the alignment
   * of items on the Block Axis within their grid area.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/align-items)
   */
  alignItems: PropTypes.oneOfType([
    PropTypes.oneOf([
      "-moz-initial",
      "baseline",
      "center",
      "end",
      "flex-end",
      "flex-start",
      "inherit",
      "initial",
      "normal",
      "revert",
      "self-end",
      "self-start",
      "start",
      "stretch",
      "unset",
    ]),
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([
          "-moz-initial",
          "baseline",
          "center",
          "end",
          "flex-end",
          "flex-start",
          "inherit",
          "initial",
          "normal",
          "revert",
          "self-end",
          "self-start",
          "start",
          "stretch",
          "unset",
          null,
        ]),
        PropTypes.shape({
          /**
           * Iterator
           */
          "__@iterator": PropTypes.func.isRequired,
          /**
           * Returns an <a> HTML anchor element and sets the name attribute to the text value
           * @param name
           */
          anchor: PropTypes.func.isRequired,
          /**
           * Returns a <big> HTML element
           */
          big: PropTypes.func.isRequired,
          /**
           * Returns a <blink> HTML element
           */
          blink: PropTypes.func.isRequired,
          /**
           * Returns a <b> HTML element
           */
          bold: PropTypes.func.isRequired,
          /**
           * Returns the character at the specified index.
           * @param pos The zero-based index of the desired character.
           */
          charAt: PropTypes.func.isRequired,
          /**
           * Returns the Unicode value of the character at the specified location.
           * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
           */
          charCodeAt: PropTypes.func.isRequired,
          /**
           * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
           * value of the UTF-16 encoded code point starting at the string element at position pos in
           * the String resulting from converting this object to a String.
           * If there is no element at that position, the result is undefined.
           * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
           */
          codePointAt: PropTypes.func.isRequired,
          /**
           * Returns a string that contains the concatenation of two or more strings.
           * @param strings The strings to append to the end of the string.
           */
          concat: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * endPosition – length(this). Otherwise returns false.
           */
          endsWith: PropTypes.func.isRequired,
          /**
           * Returns a <tt> HTML element
           */
          fixed: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the color attribute value
           */
          fontcolor: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the size attribute value
           */
          fontsize: PropTypes.func.isRequired,
          /**
           * Returns true if searchString appears as a substring of the result of converting this
           * object to a String, at one or more positions that are
           * greater than or equal to position; otherwise, returns false.
           * @param searchString search string
           * @param position If position is undefined, 0 is assumed, so as to search all of the String.
           */
          includes: PropTypes.func.isRequired,
          /**
           * Returns the position of the first occurrence of a substring.
           * @param searchString The substring to search for in the string
           * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
           */
          indexOf: PropTypes.func.isRequired,
          /**
           * Returns an <i> HTML element
           */
          italics: PropTypes.func.isRequired,
          /**
           * Returns the last occurrence of a substring in the string.
           * @param searchString The substring to search for.
           * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
           */
          lastIndexOf: PropTypes.func.isRequired,
          /**
           * Returns the length of a String object.
           */
          length: PropTypes.number.isRequired,
          /**
           * Returns an <a> HTML element and sets the href attribute value
           */
          link: PropTypes.func.isRequired,
          /**
           * Determines whether two strings are equivalent in the current locale.
           * @param that String to compare to target string
           */
          localeCompare: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an array containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          match: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an iterable of matches
           * containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          matchAll: PropTypes.func.isRequired,
          /**
           * Returns the String value result of normalizing the string into the normalization form
           * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
           * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
           * is "NFC"
           */
          normalize: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the end (right) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padEnd: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the start (left) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padStart: PropTypes.func.isRequired,
          /**
           * Returns a String value that is made from count copies appended together. If count is 0,
           * the empty string is returned.
           * @param count number of copies to append
           */
          repeat: PropTypes.func.isRequired,
          /**
           * Replaces text in a string, using a regular expression or search string.
           * @param searchValue A string to search for.
           * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
           */
          replace: PropTypes.func.isRequired,
          /**
           * Finds the first substring match in a regular expression search.
           * @param regexp The regular expression pattern and applicable flags.
           */
          search: PropTypes.func.isRequired,
          /**
           * Returns a section of a string.
           * @param start The index to the beginning of the specified portion of stringObj.
           * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
           * If this value is not specified, the substring continues to the end of stringObj.
           */
          slice: PropTypes.func.isRequired,
          /**
           * Returns a <small> HTML element
           */
          small: PropTypes.func.isRequired,
          /**
           * Split a string into substrings using the specified separator and return them as an array.
           * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
           * @param limit A value used to limit the number of elements returned in the array.
           */
          split: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * position. Otherwise returns false.
           */
          startsWith: PropTypes.func.isRequired,
          /**
           * Returns a <strike> HTML element
           */
          strike: PropTypes.func.isRequired,
          /**
           * Returns a <sub> HTML element
           */
          sub: PropTypes.func.isRequired,
          /**
           * Gets a substring beginning at the specified location and having the specified length.
           * @param from The starting position of the desired substring. The index of the first character in the string is zero.
           * @param length The number of characters to include in the returned substring.
           */
          substr: PropTypes.func.isRequired,
          /**
           * Returns the substring at the specified location within a String object.
           * @param start The zero-based index number indicating the beginning of the substring.
           * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
           * If end is omitted, the characters from start through the end of the original string are returned.
           */
          substring: PropTypes.func.isRequired,
          /**
           * Returns a <sup> HTML element
           */
          sup: PropTypes.func.isRequired,
          /**
           * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
           */
          toLocaleLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
           */
          toLocaleUpperCase: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to lowercase.
           */
          toLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string representation of a string.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to uppercase.
           */
          toUpperCase: PropTypes.func.isRequired,
          /**
           * Removes the leading and trailing white space and line terminator characters from a string.
           */
          trim: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimEnd: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimLeft: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimRight: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimStart: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
      ])
    ),
    PropTypes.object,
    PropTypes.shape({
      /**
       * Iterator
       */
      "__@iterator": PropTypes.func.isRequired,
      /**
       * Returns an <a> HTML anchor element and sets the name attribute to the text value
       * @param name
       */
      anchor: PropTypes.func.isRequired,
      /**
       * Returns a <big> HTML element
       */
      big: PropTypes.func.isRequired,
      /**
       * Returns a <blink> HTML element
       */
      blink: PropTypes.func.isRequired,
      /**
       * Returns a <b> HTML element
       */
      bold: PropTypes.func.isRequired,
      /**
       * Returns the character at the specified index.
       * @param pos The zero-based index of the desired character.
       */
      charAt: PropTypes.func.isRequired,
      /**
       * Returns the Unicode value of the character at the specified location.
       * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
       */
      charCodeAt: PropTypes.func.isRequired,
      /**
       * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
       * value of the UTF-16 encoded code point starting at the string element at position pos in
       * the String resulting from converting this object to a String.
       * If there is no element at that position, the result is undefined.
       * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
       */
      codePointAt: PropTypes.func.isRequired,
      /**
       * Returns a string that contains the concatenation of two or more strings.
       * @param strings The strings to append to the end of the string.
       */
      concat: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * endPosition – length(this). Otherwise returns false.
       */
      endsWith: PropTypes.func.isRequired,
      /**
       * Returns a <tt> HTML element
       */
      fixed: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the color attribute value
       */
      fontcolor: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the size attribute value
       */
      fontsize: PropTypes.func.isRequired,
      /**
       * Returns true if searchString appears as a substring of the result of converting this
       * object to a String, at one or more positions that are
       * greater than or equal to position; otherwise, returns false.
       * @param searchString search string
       * @param position If position is undefined, 0 is assumed, so as to search all of the String.
       */
      includes: PropTypes.func.isRequired,
      /**
       * Returns the position of the first occurrence of a substring.
       * @param searchString The substring to search for in the string
       * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
       */
      indexOf: PropTypes.func.isRequired,
      /**
       * Returns an <i> HTML element
       */
      italics: PropTypes.func.isRequired,
      /**
       * Returns the last occurrence of a substring in the string.
       * @param searchString The substring to search for.
       * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
       */
      lastIndexOf: PropTypes.func.isRequired,
      /**
       * Returns the length of a String object.
       */
      length: PropTypes.number.isRequired,
      /**
       * Returns an <a> HTML element and sets the href attribute value
       */
      link: PropTypes.func.isRequired,
      /**
       * Determines whether two strings are equivalent in the current locale.
       * @param that String to compare to target string
       */
      localeCompare: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an array containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      match: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an iterable of matches
       * containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      matchAll: PropTypes.func.isRequired,
      /**
       * Returns the String value result of normalizing the string into the normalization form
       * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
       * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
       * is "NFC"
       */
      normalize: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the end (right) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padEnd: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the start (left) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padStart: PropTypes.func.isRequired,
      /**
       * Returns a String value that is made from count copies appended together. If count is 0,
       * the empty string is returned.
       * @param count number of copies to append
       */
      repeat: PropTypes.func.isRequired,
      /**
       * Replaces text in a string, using a regular expression or search string.
       * @param searchValue A string to search for.
       * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
       */
      replace: PropTypes.func.isRequired,
      /**
       * Finds the first substring match in a regular expression search.
       * @param regexp The regular expression pattern and applicable flags.
       */
      search: PropTypes.func.isRequired,
      /**
       * Returns a section of a string.
       * @param start The index to the beginning of the specified portion of stringObj.
       * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
       * If this value is not specified, the substring continues to the end of stringObj.
       */
      slice: PropTypes.func.isRequired,
      /**
       * Returns a <small> HTML element
       */
      small: PropTypes.func.isRequired,
      /**
       * Split a string into substrings using the specified separator and return them as an array.
       * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
       * @param limit A value used to limit the number of elements returned in the array.
       */
      split: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * position. Otherwise returns false.
       */
      startsWith: PropTypes.func.isRequired,
      /**
       * Returns a <strike> HTML element
       */
      strike: PropTypes.func.isRequired,
      /**
       * Returns a <sub> HTML element
       */
      sub: PropTypes.func.isRequired,
      /**
       * Gets a substring beginning at the specified location and having the specified length.
       * @param from The starting position of the desired substring. The index of the first character in the string is zero.
       * @param length The number of characters to include in the returned substring.
       */
      substr: PropTypes.func.isRequired,
      /**
       * Returns the substring at the specified location within a String object.
       * @param start The zero-based index number indicating the beginning of the substring.
       * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
       * If end is omitted, the characters from start through the end of the original string are returned.
       */
      substring: PropTypes.func.isRequired,
      /**
       * Returns a <sup> HTML element
       */
      sup: PropTypes.func.isRequired,
      /**
       * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
       */
      toLocaleLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
       */
      toLocaleUpperCase: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to lowercase.
       */
      toLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string representation of a string.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to uppercase.
       */
      toUpperCase: PropTypes.func.isRequired,
      /**
       * Removes the leading and trailing white space and line terminator characters from a string.
       */
      trim: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimEnd: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimLeft: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimRight: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimStart: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
  ]),
  /**
   * The align-self CSS property aligns flex items of the current flex line overriding the align-items value.
   *
   * If any of the item's cross-axis margin is set to auto, then align-self is ignored. In Grid layout align-self
   * aligns the item inside the grid area.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/align-self)
   */
  alignSelf: PropTypes.oneOfType([
    PropTypes.oneOf([
      "-moz-initial",
      "auto",
      "baseline",
      "center",
      "end",
      "flex-end",
      "flex-start",
      "inherit",
      "initial",
      "normal",
      "revert",
      "self-end",
      "self-start",
      "start",
      "stretch",
      "unset",
    ]),
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([
          "-moz-initial",
          "auto",
          "baseline",
          "center",
          "end",
          "flex-end",
          "flex-start",
          "inherit",
          "initial",
          "normal",
          "revert",
          "self-end",
          "self-start",
          "start",
          "stretch",
          "unset",
          null,
        ]),
        PropTypes.shape({
          /**
           * Iterator
           */
          "__@iterator": PropTypes.func.isRequired,
          /**
           * Returns an <a> HTML anchor element and sets the name attribute to the text value
           * @param name
           */
          anchor: PropTypes.func.isRequired,
          /**
           * Returns a <big> HTML element
           */
          big: PropTypes.func.isRequired,
          /**
           * Returns a <blink> HTML element
           */
          blink: PropTypes.func.isRequired,
          /**
           * Returns a <b> HTML element
           */
          bold: PropTypes.func.isRequired,
          /**
           * Returns the character at the specified index.
           * @param pos The zero-based index of the desired character.
           */
          charAt: PropTypes.func.isRequired,
          /**
           * Returns the Unicode value of the character at the specified location.
           * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
           */
          charCodeAt: PropTypes.func.isRequired,
          /**
           * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
           * value of the UTF-16 encoded code point starting at the string element at position pos in
           * the String resulting from converting this object to a String.
           * If there is no element at that position, the result is undefined.
           * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
           */
          codePointAt: PropTypes.func.isRequired,
          /**
           * Returns a string that contains the concatenation of two or more strings.
           * @param strings The strings to append to the end of the string.
           */
          concat: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * endPosition – length(this). Otherwise returns false.
           */
          endsWith: PropTypes.func.isRequired,
          /**
           * Returns a <tt> HTML element
           */
          fixed: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the color attribute value
           */
          fontcolor: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the size attribute value
           */
          fontsize: PropTypes.func.isRequired,
          /**
           * Returns true if searchString appears as a substring of the result of converting this
           * object to a String, at one or more positions that are
           * greater than or equal to position; otherwise, returns false.
           * @param searchString search string
           * @param position If position is undefined, 0 is assumed, so as to search all of the String.
           */
          includes: PropTypes.func.isRequired,
          /**
           * Returns the position of the first occurrence of a substring.
           * @param searchString The substring to search for in the string
           * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
           */
          indexOf: PropTypes.func.isRequired,
          /**
           * Returns an <i> HTML element
           */
          italics: PropTypes.func.isRequired,
          /**
           * Returns the last occurrence of a substring in the string.
           * @param searchString The substring to search for.
           * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
           */
          lastIndexOf: PropTypes.func.isRequired,
          /**
           * Returns the length of a String object.
           */
          length: PropTypes.number.isRequired,
          /**
           * Returns an <a> HTML element and sets the href attribute value
           */
          link: PropTypes.func.isRequired,
          /**
           * Determines whether two strings are equivalent in the current locale.
           * @param that String to compare to target string
           */
          localeCompare: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an array containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          match: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an iterable of matches
           * containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          matchAll: PropTypes.func.isRequired,
          /**
           * Returns the String value result of normalizing the string into the normalization form
           * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
           * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
           * is "NFC"
           */
          normalize: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the end (right) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padEnd: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the start (left) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padStart: PropTypes.func.isRequired,
          /**
           * Returns a String value that is made from count copies appended together. If count is 0,
           * the empty string is returned.
           * @param count number of copies to append
           */
          repeat: PropTypes.func.isRequired,
          /**
           * Replaces text in a string, using a regular expression or search string.
           * @param searchValue A string to search for.
           * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
           */
          replace: PropTypes.func.isRequired,
          /**
           * Finds the first substring match in a regular expression search.
           * @param regexp The regular expression pattern and applicable flags.
           */
          search: PropTypes.func.isRequired,
          /**
           * Returns a section of a string.
           * @param start The index to the beginning of the specified portion of stringObj.
           * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
           * If this value is not specified, the substring continues to the end of stringObj.
           */
          slice: PropTypes.func.isRequired,
          /**
           * Returns a <small> HTML element
           */
          small: PropTypes.func.isRequired,
          /**
           * Split a string into substrings using the specified separator and return them as an array.
           * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
           * @param limit A value used to limit the number of elements returned in the array.
           */
          split: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * position. Otherwise returns false.
           */
          startsWith: PropTypes.func.isRequired,
          /**
           * Returns a <strike> HTML element
           */
          strike: PropTypes.func.isRequired,
          /**
           * Returns a <sub> HTML element
           */
          sub: PropTypes.func.isRequired,
          /**
           * Gets a substring beginning at the specified location and having the specified length.
           * @param from The starting position of the desired substring. The index of the first character in the string is zero.
           * @param length The number of characters to include in the returned substring.
           */
          substr: PropTypes.func.isRequired,
          /**
           * Returns the substring at the specified location within a String object.
           * @param start The zero-based index number indicating the beginning of the substring.
           * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
           * If end is omitted, the characters from start through the end of the original string are returned.
           */
          substring: PropTypes.func.isRequired,
          /**
           * Returns a <sup> HTML element
           */
          sup: PropTypes.func.isRequired,
          /**
           * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
           */
          toLocaleLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
           */
          toLocaleUpperCase: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to lowercase.
           */
          toLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string representation of a string.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to uppercase.
           */
          toUpperCase: PropTypes.func.isRequired,
          /**
           * Removes the leading and trailing white space and line terminator characters from a string.
           */
          trim: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimEnd: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimLeft: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimRight: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimStart: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
      ])
    ),
    PropTypes.object,
    PropTypes.shape({
      /**
       * Iterator
       */
      "__@iterator": PropTypes.func.isRequired,
      /**
       * Returns an <a> HTML anchor element and sets the name attribute to the text value
       * @param name
       */
      anchor: PropTypes.func.isRequired,
      /**
       * Returns a <big> HTML element
       */
      big: PropTypes.func.isRequired,
      /**
       * Returns a <blink> HTML element
       */
      blink: PropTypes.func.isRequired,
      /**
       * Returns a <b> HTML element
       */
      bold: PropTypes.func.isRequired,
      /**
       * Returns the character at the specified index.
       * @param pos The zero-based index of the desired character.
       */
      charAt: PropTypes.func.isRequired,
      /**
       * Returns the Unicode value of the character at the specified location.
       * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
       */
      charCodeAt: PropTypes.func.isRequired,
      /**
       * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
       * value of the UTF-16 encoded code point starting at the string element at position pos in
       * the String resulting from converting this object to a String.
       * If there is no element at that position, the result is undefined.
       * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
       */
      codePointAt: PropTypes.func.isRequired,
      /**
       * Returns a string that contains the concatenation of two or more strings.
       * @param strings The strings to append to the end of the string.
       */
      concat: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * endPosition – length(this). Otherwise returns false.
       */
      endsWith: PropTypes.func.isRequired,
      /**
       * Returns a <tt> HTML element
       */
      fixed: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the color attribute value
       */
      fontcolor: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the size attribute value
       */
      fontsize: PropTypes.func.isRequired,
      /**
       * Returns true if searchString appears as a substring of the result of converting this
       * object to a String, at one or more positions that are
       * greater than or equal to position; otherwise, returns false.
       * @param searchString search string
       * @param position If position is undefined, 0 is assumed, so as to search all of the String.
       */
      includes: PropTypes.func.isRequired,
      /**
       * Returns the position of the first occurrence of a substring.
       * @param searchString The substring to search for in the string
       * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
       */
      indexOf: PropTypes.func.isRequired,
      /**
       * Returns an <i> HTML element
       */
      italics: PropTypes.func.isRequired,
      /**
       * Returns the last occurrence of a substring in the string.
       * @param searchString The substring to search for.
       * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
       */
      lastIndexOf: PropTypes.func.isRequired,
      /**
       * Returns the length of a String object.
       */
      length: PropTypes.number.isRequired,
      /**
       * Returns an <a> HTML element and sets the href attribute value
       */
      link: PropTypes.func.isRequired,
      /**
       * Determines whether two strings are equivalent in the current locale.
       * @param that String to compare to target string
       */
      localeCompare: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an array containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      match: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an iterable of matches
       * containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      matchAll: PropTypes.func.isRequired,
      /**
       * Returns the String value result of normalizing the string into the normalization form
       * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
       * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
       * is "NFC"
       */
      normalize: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the end (right) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padEnd: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the start (left) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padStart: PropTypes.func.isRequired,
      /**
       * Returns a String value that is made from count copies appended together. If count is 0,
       * the empty string is returned.
       * @param count number of copies to append
       */
      repeat: PropTypes.func.isRequired,
      /**
       * Replaces text in a string, using a regular expression or search string.
       * @param searchValue A string to search for.
       * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
       */
      replace: PropTypes.func.isRequired,
      /**
       * Finds the first substring match in a regular expression search.
       * @param regexp The regular expression pattern and applicable flags.
       */
      search: PropTypes.func.isRequired,
      /**
       * Returns a section of a string.
       * @param start The index to the beginning of the specified portion of stringObj.
       * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
       * If this value is not specified, the substring continues to the end of stringObj.
       */
      slice: PropTypes.func.isRequired,
      /**
       * Returns a <small> HTML element
       */
      small: PropTypes.func.isRequired,
      /**
       * Split a string into substrings using the specified separator and return them as an array.
       * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
       * @param limit A value used to limit the number of elements returned in the array.
       */
      split: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * position. Otherwise returns false.
       */
      startsWith: PropTypes.func.isRequired,
      /**
       * Returns a <strike> HTML element
       */
      strike: PropTypes.func.isRequired,
      /**
       * Returns a <sub> HTML element
       */
      sub: PropTypes.func.isRequired,
      /**
       * Gets a substring beginning at the specified location and having the specified length.
       * @param from The starting position of the desired substring. The index of the first character in the string is zero.
       * @param length The number of characters to include in the returned substring.
       */
      substr: PropTypes.func.isRequired,
      /**
       * Returns the substring at the specified location within a String object.
       * @param start The zero-based index number indicating the beginning of the substring.
       * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
       * If end is omitted, the characters from start through the end of the original string are returned.
       */
      substring: PropTypes.func.isRequired,
      /**
       * Returns a <sup> HTML element
       */
      sup: PropTypes.func.isRequired,
      /**
       * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
       */
      toLocaleLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
       */
      toLocaleUpperCase: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to lowercase.
       */
      toLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string representation of a string.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to uppercase.
       */
      toUpperCase: PropTypes.func.isRequired,
      /**
       * Removes the leading and trailing white space and line terminator characters from a string.
       */
      trim: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimEnd: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimLeft: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimRight: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimStart: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
  ]),
  ariaLabel: PropTypes.string,
  children: PropTypes.node,
  /**
   * The flex CSS property specifies how a flex item will grow or shrink so as to fit the space available in
   * its flex container. This is a shorthand property that sets flex-grow, flex-shrink, and flex-basis.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/flex)
   */
  flex: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          /**
           * Iterator
           */
          "__@iterator": PropTypes.func.isRequired,
          /**
           * Returns an <a> HTML anchor element and sets the name attribute to the text value
           * @param name
           */
          anchor: PropTypes.func.isRequired,
          /**
           * Returns a <big> HTML element
           */
          big: PropTypes.func.isRequired,
          /**
           * Returns a <blink> HTML element
           */
          blink: PropTypes.func.isRequired,
          /**
           * Returns a <b> HTML element
           */
          bold: PropTypes.func.isRequired,
          /**
           * Returns the character at the specified index.
           * @param pos The zero-based index of the desired character.
           */
          charAt: PropTypes.func.isRequired,
          /**
           * Returns the Unicode value of the character at the specified location.
           * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
           */
          charCodeAt: PropTypes.func.isRequired,
          /**
           * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
           * value of the UTF-16 encoded code point starting at the string element at position pos in
           * the String resulting from converting this object to a String.
           * If there is no element at that position, the result is undefined.
           * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
           */
          codePointAt: PropTypes.func.isRequired,
          /**
           * Returns a string that contains the concatenation of two or more strings.
           * @param strings The strings to append to the end of the string.
           */
          concat: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * endPosition – length(this). Otherwise returns false.
           */
          endsWith: PropTypes.func.isRequired,
          /**
           * Returns a <tt> HTML element
           */
          fixed: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the color attribute value
           */
          fontcolor: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the size attribute value
           */
          fontsize: PropTypes.func.isRequired,
          /**
           * Returns true if searchString appears as a substring of the result of converting this
           * object to a String, at one or more positions that are
           * greater than or equal to position; otherwise, returns false.
           * @param searchString search string
           * @param position If position is undefined, 0 is assumed, so as to search all of the String.
           */
          includes: PropTypes.func.isRequired,
          /**
           * Returns the position of the first occurrence of a substring.
           * @param searchString The substring to search for in the string
           * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
           */
          indexOf: PropTypes.func.isRequired,
          /**
           * Returns an <i> HTML element
           */
          italics: PropTypes.func.isRequired,
          /**
           * Returns the last occurrence of a substring in the string.
           * @param searchString The substring to search for.
           * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
           */
          lastIndexOf: PropTypes.func.isRequired,
          /**
           * Returns the length of a String object.
           */
          length: PropTypes.number.isRequired,
          /**
           * Returns an <a> HTML element and sets the href attribute value
           */
          link: PropTypes.func.isRequired,
          /**
           * Determines whether two strings are equivalent in the current locale.
           * @param that String to compare to target string
           */
          localeCompare: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an array containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          match: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an iterable of matches
           * containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          matchAll: PropTypes.func.isRequired,
          /**
           * Returns the String value result of normalizing the string into the normalization form
           * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
           * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
           * is "NFC"
           */
          normalize: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the end (right) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padEnd: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the start (left) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padStart: PropTypes.func.isRequired,
          /**
           * Returns a String value that is made from count copies appended together. If count is 0,
           * the empty string is returned.
           * @param count number of copies to append
           */
          repeat: PropTypes.func.isRequired,
          /**
           * Replaces text in a string, using a regular expression or search string.
           * @param searchValue A string to search for.
           * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
           */
          replace: PropTypes.func.isRequired,
          /**
           * Finds the first substring match in a regular expression search.
           * @param regexp The regular expression pattern and applicable flags.
           */
          search: PropTypes.func.isRequired,
          /**
           * Returns a section of a string.
           * @param start The index to the beginning of the specified portion of stringObj.
           * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
           * If this value is not specified, the substring continues to the end of stringObj.
           */
          slice: PropTypes.func.isRequired,
          /**
           * Returns a <small> HTML element
           */
          small: PropTypes.func.isRequired,
          /**
           * Split a string into substrings using the specified separator and return them as an array.
           * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
           * @param limit A value used to limit the number of elements returned in the array.
           */
          split: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * position. Otherwise returns false.
           */
          startsWith: PropTypes.func.isRequired,
          /**
           * Returns a <strike> HTML element
           */
          strike: PropTypes.func.isRequired,
          /**
           * Returns a <sub> HTML element
           */
          sub: PropTypes.func.isRequired,
          /**
           * Gets a substring beginning at the specified location and having the specified length.
           * @param from The starting position of the desired substring. The index of the first character in the string is zero.
           * @param length The number of characters to include in the returned substring.
           */
          substr: PropTypes.func.isRequired,
          /**
           * Returns the substring at the specified location within a String object.
           * @param start The zero-based index number indicating the beginning of the substring.
           * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
           * If end is omitted, the characters from start through the end of the original string are returned.
           */
          substring: PropTypes.func.isRequired,
          /**
           * Returns a <sup> HTML element
           */
          sup: PropTypes.func.isRequired,
          /**
           * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
           */
          toLocaleLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
           */
          toLocaleUpperCase: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to lowercase.
           */
          toLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string representation of a string.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to uppercase.
           */
          toUpperCase: PropTypes.func.isRequired,
          /**
           * Removes the leading and trailing white space and line terminator characters from a string.
           */
          trim: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimEnd: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimLeft: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimRight: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimStart: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      /**
       * Iterator
       */
      "__@iterator": PropTypes.func.isRequired,
      /**
       * Returns an <a> HTML anchor element and sets the name attribute to the text value
       * @param name
       */
      anchor: PropTypes.func.isRequired,
      /**
       * Returns a <big> HTML element
       */
      big: PropTypes.func.isRequired,
      /**
       * Returns a <blink> HTML element
       */
      blink: PropTypes.func.isRequired,
      /**
       * Returns a <b> HTML element
       */
      bold: PropTypes.func.isRequired,
      /**
       * Returns the character at the specified index.
       * @param pos The zero-based index of the desired character.
       */
      charAt: PropTypes.func.isRequired,
      /**
       * Returns the Unicode value of the character at the specified location.
       * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
       */
      charCodeAt: PropTypes.func.isRequired,
      /**
       * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
       * value of the UTF-16 encoded code point starting at the string element at position pos in
       * the String resulting from converting this object to a String.
       * If there is no element at that position, the result is undefined.
       * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
       */
      codePointAt: PropTypes.func.isRequired,
      /**
       * Returns a string that contains the concatenation of two or more strings.
       * @param strings The strings to append to the end of the string.
       */
      concat: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * endPosition – length(this). Otherwise returns false.
       */
      endsWith: PropTypes.func.isRequired,
      /**
       * Returns a <tt> HTML element
       */
      fixed: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the color attribute value
       */
      fontcolor: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the size attribute value
       */
      fontsize: PropTypes.func.isRequired,
      /**
       * Returns true if searchString appears as a substring of the result of converting this
       * object to a String, at one or more positions that are
       * greater than or equal to position; otherwise, returns false.
       * @param searchString search string
       * @param position If position is undefined, 0 is assumed, so as to search all of the String.
       */
      includes: PropTypes.func.isRequired,
      /**
       * Returns the position of the first occurrence of a substring.
       * @param searchString The substring to search for in the string
       * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
       */
      indexOf: PropTypes.func.isRequired,
      /**
       * Returns an <i> HTML element
       */
      italics: PropTypes.func.isRequired,
      /**
       * Returns the last occurrence of a substring in the string.
       * @param searchString The substring to search for.
       * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
       */
      lastIndexOf: PropTypes.func.isRequired,
      /**
       * Returns the length of a String object.
       */
      length: PropTypes.number.isRequired,
      /**
       * Returns an <a> HTML element and sets the href attribute value
       */
      link: PropTypes.func.isRequired,
      /**
       * Determines whether two strings are equivalent in the current locale.
       * @param that String to compare to target string
       */
      localeCompare: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an array containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      match: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an iterable of matches
       * containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      matchAll: PropTypes.func.isRequired,
      /**
       * Returns the String value result of normalizing the string into the normalization form
       * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
       * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
       * is "NFC"
       */
      normalize: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the end (right) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padEnd: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the start (left) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padStart: PropTypes.func.isRequired,
      /**
       * Returns a String value that is made from count copies appended together. If count is 0,
       * the empty string is returned.
       * @param count number of copies to append
       */
      repeat: PropTypes.func.isRequired,
      /**
       * Replaces text in a string, using a regular expression or search string.
       * @param searchValue A string to search for.
       * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
       */
      replace: PropTypes.func.isRequired,
      /**
       * Finds the first substring match in a regular expression search.
       * @param regexp The regular expression pattern and applicable flags.
       */
      search: PropTypes.func.isRequired,
      /**
       * Returns a section of a string.
       * @param start The index to the beginning of the specified portion of stringObj.
       * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
       * If this value is not specified, the substring continues to the end of stringObj.
       */
      slice: PropTypes.func.isRequired,
      /**
       * Returns a <small> HTML element
       */
      small: PropTypes.func.isRequired,
      /**
       * Split a string into substrings using the specified separator and return them as an array.
       * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
       * @param limit A value used to limit the number of elements returned in the array.
       */
      split: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * position. Otherwise returns false.
       */
      startsWith: PropTypes.func.isRequired,
      /**
       * Returns a <strike> HTML element
       */
      strike: PropTypes.func.isRequired,
      /**
       * Returns a <sub> HTML element
       */
      sub: PropTypes.func.isRequired,
      /**
       * Gets a substring beginning at the specified location and having the specified length.
       * @param from The starting position of the desired substring. The index of the first character in the string is zero.
       * @param length The number of characters to include in the returned substring.
       */
      substr: PropTypes.func.isRequired,
      /**
       * Returns the substring at the specified location within a String object.
       * @param start The zero-based index number indicating the beginning of the substring.
       * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
       * If end is omitted, the characters from start through the end of the original string are returned.
       */
      substring: PropTypes.func.isRequired,
      /**
       * Returns a <sup> HTML element
       */
      sup: PropTypes.func.isRequired,
      /**
       * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
       */
      toLocaleLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
       */
      toLocaleUpperCase: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to lowercase.
       */
      toLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string representation of a string.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to uppercase.
       */
      toUpperCase: PropTypes.func.isRequired,
      /**
       * Removes the leading and trailing white space and line terminator characters from a string.
       */
      trim: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimEnd: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimLeft: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimRight: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimStart: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  flexBasis: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          /**
           * Iterator
           */
          "__@iterator": PropTypes.func.isRequired,
          /**
           * Returns an <a> HTML anchor element and sets the name attribute to the text value
           * @param name
           */
          anchor: PropTypes.func.isRequired,
          /**
           * Returns a <big> HTML element
           */
          big: PropTypes.func.isRequired,
          /**
           * Returns a <blink> HTML element
           */
          blink: PropTypes.func.isRequired,
          /**
           * Returns a <b> HTML element
           */
          bold: PropTypes.func.isRequired,
          /**
           * Returns the character at the specified index.
           * @param pos The zero-based index of the desired character.
           */
          charAt: PropTypes.func.isRequired,
          /**
           * Returns the Unicode value of the character at the specified location.
           * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
           */
          charCodeAt: PropTypes.func.isRequired,
          /**
           * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
           * value of the UTF-16 encoded code point starting at the string element at position pos in
           * the String resulting from converting this object to a String.
           * If there is no element at that position, the result is undefined.
           * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
           */
          codePointAt: PropTypes.func.isRequired,
          /**
           * Returns a string that contains the concatenation of two or more strings.
           * @param strings The strings to append to the end of the string.
           */
          concat: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * endPosition – length(this). Otherwise returns false.
           */
          endsWith: PropTypes.func.isRequired,
          /**
           * Returns a <tt> HTML element
           */
          fixed: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the color attribute value
           */
          fontcolor: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the size attribute value
           */
          fontsize: PropTypes.func.isRequired,
          /**
           * Returns true if searchString appears as a substring of the result of converting this
           * object to a String, at one or more positions that are
           * greater than or equal to position; otherwise, returns false.
           * @param searchString search string
           * @param position If position is undefined, 0 is assumed, so as to search all of the String.
           */
          includes: PropTypes.func.isRequired,
          /**
           * Returns the position of the first occurrence of a substring.
           * @param searchString The substring to search for in the string
           * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
           */
          indexOf: PropTypes.func.isRequired,
          /**
           * Returns an <i> HTML element
           */
          italics: PropTypes.func.isRequired,
          /**
           * Returns the last occurrence of a substring in the string.
           * @param searchString The substring to search for.
           * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
           */
          lastIndexOf: PropTypes.func.isRequired,
          /**
           * Returns the length of a String object.
           */
          length: PropTypes.number.isRequired,
          /**
           * Returns an <a> HTML element and sets the href attribute value
           */
          link: PropTypes.func.isRequired,
          /**
           * Determines whether two strings are equivalent in the current locale.
           * @param that String to compare to target string
           */
          localeCompare: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an array containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          match: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an iterable of matches
           * containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          matchAll: PropTypes.func.isRequired,
          /**
           * Returns the String value result of normalizing the string into the normalization form
           * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
           * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
           * is "NFC"
           */
          normalize: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the end (right) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padEnd: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the start (left) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padStart: PropTypes.func.isRequired,
          /**
           * Returns a String value that is made from count copies appended together. If count is 0,
           * the empty string is returned.
           * @param count number of copies to append
           */
          repeat: PropTypes.func.isRequired,
          /**
           * Replaces text in a string, using a regular expression or search string.
           * @param searchValue A string to search for.
           * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
           */
          replace: PropTypes.func.isRequired,
          /**
           * Finds the first substring match in a regular expression search.
           * @param regexp The regular expression pattern and applicable flags.
           */
          search: PropTypes.func.isRequired,
          /**
           * Returns a section of a string.
           * @param start The index to the beginning of the specified portion of stringObj.
           * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
           * If this value is not specified, the substring continues to the end of stringObj.
           */
          slice: PropTypes.func.isRequired,
          /**
           * Returns a <small> HTML element
           */
          small: PropTypes.func.isRequired,
          /**
           * Split a string into substrings using the specified separator and return them as an array.
           * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
           * @param limit A value used to limit the number of elements returned in the array.
           */
          split: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * position. Otherwise returns false.
           */
          startsWith: PropTypes.func.isRequired,
          /**
           * Returns a <strike> HTML element
           */
          strike: PropTypes.func.isRequired,
          /**
           * Returns a <sub> HTML element
           */
          sub: PropTypes.func.isRequired,
          /**
           * Gets a substring beginning at the specified location and having the specified length.
           * @param from The starting position of the desired substring. The index of the first character in the string is zero.
           * @param length The number of characters to include in the returned substring.
           */
          substr: PropTypes.func.isRequired,
          /**
           * Returns the substring at the specified location within a String object.
           * @param start The zero-based index number indicating the beginning of the substring.
           * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
           * If end is omitted, the characters from start through the end of the original string are returned.
           */
          substring: PropTypes.func.isRequired,
          /**
           * Returns a <sup> HTML element
           */
          sup: PropTypes.func.isRequired,
          /**
           * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
           */
          toLocaleLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
           */
          toLocaleUpperCase: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to lowercase.
           */
          toLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string representation of a string.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to uppercase.
           */
          toUpperCase: PropTypes.func.isRequired,
          /**
           * Removes the leading and trailing white space and line terminator characters from a string.
           */
          trim: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimEnd: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimLeft: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimRight: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimStart: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      /**
       * Iterator
       */
      "__@iterator": PropTypes.func.isRequired,
      /**
       * Returns an <a> HTML anchor element and sets the name attribute to the text value
       * @param name
       */
      anchor: PropTypes.func.isRequired,
      /**
       * Returns a <big> HTML element
       */
      big: PropTypes.func.isRequired,
      /**
       * Returns a <blink> HTML element
       */
      blink: PropTypes.func.isRequired,
      /**
       * Returns a <b> HTML element
       */
      bold: PropTypes.func.isRequired,
      /**
       * Returns the character at the specified index.
       * @param pos The zero-based index of the desired character.
       */
      charAt: PropTypes.func.isRequired,
      /**
       * Returns the Unicode value of the character at the specified location.
       * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
       */
      charCodeAt: PropTypes.func.isRequired,
      /**
       * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
       * value of the UTF-16 encoded code point starting at the string element at position pos in
       * the String resulting from converting this object to a String.
       * If there is no element at that position, the result is undefined.
       * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
       */
      codePointAt: PropTypes.func.isRequired,
      /**
       * Returns a string that contains the concatenation of two or more strings.
       * @param strings The strings to append to the end of the string.
       */
      concat: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * endPosition – length(this). Otherwise returns false.
       */
      endsWith: PropTypes.func.isRequired,
      /**
       * Returns a <tt> HTML element
       */
      fixed: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the color attribute value
       */
      fontcolor: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the size attribute value
       */
      fontsize: PropTypes.func.isRequired,
      /**
       * Returns true if searchString appears as a substring of the result of converting this
       * object to a String, at one or more positions that are
       * greater than or equal to position; otherwise, returns false.
       * @param searchString search string
       * @param position If position is undefined, 0 is assumed, so as to search all of the String.
       */
      includes: PropTypes.func.isRequired,
      /**
       * Returns the position of the first occurrence of a substring.
       * @param searchString The substring to search for in the string
       * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
       */
      indexOf: PropTypes.func.isRequired,
      /**
       * Returns an <i> HTML element
       */
      italics: PropTypes.func.isRequired,
      /**
       * Returns the last occurrence of a substring in the string.
       * @param searchString The substring to search for.
       * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
       */
      lastIndexOf: PropTypes.func.isRequired,
      /**
       * Returns the length of a String object.
       */
      length: PropTypes.number.isRequired,
      /**
       * Returns an <a> HTML element and sets the href attribute value
       */
      link: PropTypes.func.isRequired,
      /**
       * Determines whether two strings are equivalent in the current locale.
       * @param that String to compare to target string
       */
      localeCompare: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an array containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      match: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an iterable of matches
       * containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      matchAll: PropTypes.func.isRequired,
      /**
       * Returns the String value result of normalizing the string into the normalization form
       * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
       * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
       * is "NFC"
       */
      normalize: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the end (right) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padEnd: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the start (left) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padStart: PropTypes.func.isRequired,
      /**
       * Returns a String value that is made from count copies appended together. If count is 0,
       * the empty string is returned.
       * @param count number of copies to append
       */
      repeat: PropTypes.func.isRequired,
      /**
       * Replaces text in a string, using a regular expression or search string.
       * @param searchValue A string to search for.
       * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
       */
      replace: PropTypes.func.isRequired,
      /**
       * Finds the first substring match in a regular expression search.
       * @param regexp The regular expression pattern and applicable flags.
       */
      search: PropTypes.func.isRequired,
      /**
       * Returns a section of a string.
       * @param start The index to the beginning of the specified portion of stringObj.
       * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
       * If this value is not specified, the substring continues to the end of stringObj.
       */
      slice: PropTypes.func.isRequired,
      /**
       * Returns a <small> HTML element
       */
      small: PropTypes.func.isRequired,
      /**
       * Split a string into substrings using the specified separator and return them as an array.
       * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
       * @param limit A value used to limit the number of elements returned in the array.
       */
      split: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * position. Otherwise returns false.
       */
      startsWith: PropTypes.func.isRequired,
      /**
       * Returns a <strike> HTML element
       */
      strike: PropTypes.func.isRequired,
      /**
       * Returns a <sub> HTML element
       */
      sub: PropTypes.func.isRequired,
      /**
       * Gets a substring beginning at the specified location and having the specified length.
       * @param from The starting position of the desired substring. The index of the first character in the string is zero.
       * @param length The number of characters to include in the returned substring.
       */
      substr: PropTypes.func.isRequired,
      /**
       * Returns the substring at the specified location within a String object.
       * @param start The zero-based index number indicating the beginning of the substring.
       * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
       * If end is omitted, the characters from start through the end of the original string are returned.
       */
      substring: PropTypes.func.isRequired,
      /**
       * Returns a <sup> HTML element
       */
      sup: PropTypes.func.isRequired,
      /**
       * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
       */
      toLocaleLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
       */
      toLocaleUpperCase: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to lowercase.
       */
      toLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string representation of a string.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to uppercase.
       */
      toUpperCase: PropTypes.func.isRequired,
      /**
       * Removes the leading and trailing white space and line terminator characters from a string.
       */
      trim: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimEnd: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimLeft: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimRight: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimStart: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  /**
   * The flex-direction CSS property specifies how flex items are placed in the flex container defining the main
   * axis and the direction (normal or reversed).
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/flex-direction)
   */
  flexDirection: PropTypes.oneOfType([
    PropTypes.oneOf([
      "-moz-initial",
      "column-reverse",
      "column",
      "inherit",
      "initial",
      "revert",
      "row-reverse",
      "row",
      "unset",
    ]),
    PropTypes.arrayOf(
      PropTypes.oneOf([
        "-moz-initial",
        "column-reverse",
        "column",
        "inherit",
        "initial",
        "revert",
        "row-reverse",
        "row",
        "unset",
        null,
      ])
    ),
    PropTypes.object,
  ]),
  /**
   * The flex-grow CSS property sets the flex grow factor of a flex item main size. It specifies how much of the
   * remaining space in the flex container should be assigned to the item (the flex grow factor).
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/flex-grow)
   */
  flexGrow: PropTypes.oneOfType([
    PropTypes.oneOf(["-moz-initial", "inherit", "initial", "revert", "unset"]),
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([
          "-moz-initial",
          "inherit",
          "initial",
          "revert",
          "unset",
          null,
        ]),
        PropTypes.shape({
          /**
           * Returns a string containing a number represented in exponential notation.
           * @param fractionDigits Number of digits after the decimal point. Must be in the range 0 - 20, inclusive.
           */
          toExponential: PropTypes.func.isRequired,
          /**
           * Returns a string representing a number in fixed-point notation.
           * @param fractionDigits Number of digits after the decimal point. Must be in the range 0 - 20, inclusive.
           */
          toFixed: PropTypes.func.isRequired,
          /**
           * Converts a number to a string by using the current or specified locale.
           * @param locales A locale string or array of locale strings that contain one or more language or locale tags. If you include more than one locale string, list them in descending order of priority so that the first entry is the preferred locale. If you omit this parameter, the default locale of the JavaScript runtime is used.
           * @param options An object that contains one or more properties that specify comparison options.
           */
          toLocaleString: PropTypes.func.isRequired,
          /**
           * Returns a string containing a number represented either in exponential or fixed-point notation with a specified number of digits.
           * @param precision Number of significant digits. Must be in the range 1 - 21, inclusive.
           */
          toPrecision: PropTypes.func.isRequired,
          /**
           * Returns a string representation of an object.
           * @param radix Specifies a radix for converting numeric values to strings. This value is only used for numbers.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
      ])
    ),
    PropTypes.object,
    PropTypes.shape({
      /**
       * Returns a string containing a number represented in exponential notation.
       * @param fractionDigits Number of digits after the decimal point. Must be in the range 0 - 20, inclusive.
       */
      toExponential: PropTypes.func.isRequired,
      /**
       * Returns a string representing a number in fixed-point notation.
       * @param fractionDigits Number of digits after the decimal point. Must be in the range 0 - 20, inclusive.
       */
      toFixed: PropTypes.func.isRequired,
      /**
       * Converts a number to a string by using the current or specified locale.
       * @param locales A locale string or array of locale strings that contain one or more language or locale tags. If you include more than one locale string, list them in descending order of priority so that the first entry is the preferred locale. If you omit this parameter, the default locale of the JavaScript runtime is used.
       * @param options An object that contains one or more properties that specify comparison options.
       */
      toLocaleString: PropTypes.func.isRequired,
      /**
       * Returns a string containing a number represented either in exponential or fixed-point notation with a specified number of digits.
       * @param precision Number of significant digits. Must be in the range 1 - 21, inclusive.
       */
      toPrecision: PropTypes.func.isRequired,
      /**
       * Returns a string representation of an object.
       * @param radix Specifies a radix for converting numeric values to strings. This value is only used for numbers.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
  ]),
  /**
   * The flex-shrink CSS property sets the flex shrink factor of a flex item. If the size of all flex items is larger
   * than the flex container, items shrink to fit according to flex-shrink.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/flex-shrink)
   */
  flexShrink: PropTypes.oneOfType([
    PropTypes.oneOf(["-moz-initial", "inherit", "initial", "revert", "unset"]),
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([
          "-moz-initial",
          "inherit",
          "initial",
          "revert",
          "unset",
          null,
        ]),
        PropTypes.shape({
          /**
           * Returns a string containing a number represented in exponential notation.
           * @param fractionDigits Number of digits after the decimal point. Must be in the range 0 - 20, inclusive.
           */
          toExponential: PropTypes.func.isRequired,
          /**
           * Returns a string representing a number in fixed-point notation.
           * @param fractionDigits Number of digits after the decimal point. Must be in the range 0 - 20, inclusive.
           */
          toFixed: PropTypes.func.isRequired,
          /**
           * Converts a number to a string by using the current or specified locale.
           * @param locales A locale string or array of locale strings that contain one or more language or locale tags. If you include more than one locale string, list them in descending order of priority so that the first entry is the preferred locale. If you omit this parameter, the default locale of the JavaScript runtime is used.
           * @param options An object that contains one or more properties that specify comparison options.
           */
          toLocaleString: PropTypes.func.isRequired,
          /**
           * Returns a string containing a number represented either in exponential or fixed-point notation with a specified number of digits.
           * @param precision Number of significant digits. Must be in the range 1 - 21, inclusive.
           */
          toPrecision: PropTypes.func.isRequired,
          /**
           * Returns a string representation of an object.
           * @param radix Specifies a radix for converting numeric values to strings. This value is only used for numbers.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
      ])
    ),
    PropTypes.object,
    PropTypes.shape({
      /**
       * Returns a string containing a number represented in exponential notation.
       * @param fractionDigits Number of digits after the decimal point. Must be in the range 0 - 20, inclusive.
       */
      toExponential: PropTypes.func.isRequired,
      /**
       * Returns a string representing a number in fixed-point notation.
       * @param fractionDigits Number of digits after the decimal point. Must be in the range 0 - 20, inclusive.
       */
      toFixed: PropTypes.func.isRequired,
      /**
       * Converts a number to a string by using the current or specified locale.
       * @param locales A locale string or array of locale strings that contain one or more language or locale tags. If you include more than one locale string, list them in descending order of priority so that the first entry is the preferred locale. If you omit this parameter, the default locale of the JavaScript runtime is used.
       * @param options An object that contains one or more properties that specify comparison options.
       */
      toLocaleString: PropTypes.func.isRequired,
      /**
       * Returns a string containing a number represented either in exponential or fixed-point notation with a specified number of digits.
       * @param precision Number of significant digits. Must be in the range 1 - 21, inclusive.
       */
      toPrecision: PropTypes.func.isRequired,
      /**
       * Returns a string representation of an object.
       * @param radix Specifies a radix for converting numeric values to strings. This value is only used for numbers.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
  ]),
  /**
   * The flex-wrap CSS property sets whether flex items are forced onto one line or can wrap onto multiple lines.
   * If wrapping is allowed, it sets the direction that lines are stacked.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/flex-wrap)
   */
  flexWrap: PropTypes.oneOfType([
    PropTypes.oneOf([
      "-moz-initial",
      "inherit",
      "initial",
      "nowrap",
      "revert",
      "unset",
      "wrap-reverse",
      "wrap",
    ]),
    PropTypes.arrayOf(
      PropTypes.oneOf([
        "-moz-initial",
        "inherit",
        "initial",
        "nowrap",
        "revert",
        "unset",
        "wrap-reverse",
        "wrap",
        null,
      ])
    ),
    PropTypes.object,
  ]),
  /**
   * If 'true' the children will not be visible
   */
  isLoading: PropTypes.bool,
  /**
   * The CSS justify-content property defines how the browser distributes space between and around content items
   * along the main-axis of a flex container, and the inline axis of a grid container.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/justify-content)
   */
  justifyContent: PropTypes.oneOfType([
    PropTypes.oneOf([
      "-moz-initial",
      "center",
      "end",
      "flex-end",
      "flex-start",
      "inherit",
      "initial",
      "left",
      "normal",
      "revert",
      "right",
      "space-around",
      "space-between",
      "space-evenly",
      "start",
      "stretch",
      "unset",
    ]),
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([
          "-moz-initial",
          "center",
          "end",
          "flex-end",
          "flex-start",
          "inherit",
          "initial",
          "left",
          "normal",
          "revert",
          "right",
          "space-around",
          "space-between",
          "space-evenly",
          "start",
          "stretch",
          "unset",
          null,
        ]),
        PropTypes.shape({
          /**
           * Iterator
           */
          "__@iterator": PropTypes.func.isRequired,
          /**
           * Returns an <a> HTML anchor element and sets the name attribute to the text value
           * @param name
           */
          anchor: PropTypes.func.isRequired,
          /**
           * Returns a <big> HTML element
           */
          big: PropTypes.func.isRequired,
          /**
           * Returns a <blink> HTML element
           */
          blink: PropTypes.func.isRequired,
          /**
           * Returns a <b> HTML element
           */
          bold: PropTypes.func.isRequired,
          /**
           * Returns the character at the specified index.
           * @param pos The zero-based index of the desired character.
           */
          charAt: PropTypes.func.isRequired,
          /**
           * Returns the Unicode value of the character at the specified location.
           * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
           */
          charCodeAt: PropTypes.func.isRequired,
          /**
           * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
           * value of the UTF-16 encoded code point starting at the string element at position pos in
           * the String resulting from converting this object to a String.
           * If there is no element at that position, the result is undefined.
           * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
           */
          codePointAt: PropTypes.func.isRequired,
          /**
           * Returns a string that contains the concatenation of two or more strings.
           * @param strings The strings to append to the end of the string.
           */
          concat: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * endPosition – length(this). Otherwise returns false.
           */
          endsWith: PropTypes.func.isRequired,
          /**
           * Returns a <tt> HTML element
           */
          fixed: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the color attribute value
           */
          fontcolor: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the size attribute value
           */
          fontsize: PropTypes.func.isRequired,
          /**
           * Returns true if searchString appears as a substring of the result of converting this
           * object to a String, at one or more positions that are
           * greater than or equal to position; otherwise, returns false.
           * @param searchString search string
           * @param position If position is undefined, 0 is assumed, so as to search all of the String.
           */
          includes: PropTypes.func.isRequired,
          /**
           * Returns the position of the first occurrence of a substring.
           * @param searchString The substring to search for in the string
           * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
           */
          indexOf: PropTypes.func.isRequired,
          /**
           * Returns an <i> HTML element
           */
          italics: PropTypes.func.isRequired,
          /**
           * Returns the last occurrence of a substring in the string.
           * @param searchString The substring to search for.
           * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
           */
          lastIndexOf: PropTypes.func.isRequired,
          /**
           * Returns the length of a String object.
           */
          length: PropTypes.number.isRequired,
          /**
           * Returns an <a> HTML element and sets the href attribute value
           */
          link: PropTypes.func.isRequired,
          /**
           * Determines whether two strings are equivalent in the current locale.
           * @param that String to compare to target string
           */
          localeCompare: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an array containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          match: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an iterable of matches
           * containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          matchAll: PropTypes.func.isRequired,
          /**
           * Returns the String value result of normalizing the string into the normalization form
           * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
           * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
           * is "NFC"
           */
          normalize: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the end (right) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padEnd: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the start (left) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padStart: PropTypes.func.isRequired,
          /**
           * Returns a String value that is made from count copies appended together. If count is 0,
           * the empty string is returned.
           * @param count number of copies to append
           */
          repeat: PropTypes.func.isRequired,
          /**
           * Replaces text in a string, using a regular expression or search string.
           * @param searchValue A string to search for.
           * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
           */
          replace: PropTypes.func.isRequired,
          /**
           * Finds the first substring match in a regular expression search.
           * @param regexp The regular expression pattern and applicable flags.
           */
          search: PropTypes.func.isRequired,
          /**
           * Returns a section of a string.
           * @param start The index to the beginning of the specified portion of stringObj.
           * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
           * If this value is not specified, the substring continues to the end of stringObj.
           */
          slice: PropTypes.func.isRequired,
          /**
           * Returns a <small> HTML element
           */
          small: PropTypes.func.isRequired,
          /**
           * Split a string into substrings using the specified separator and return them as an array.
           * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
           * @param limit A value used to limit the number of elements returned in the array.
           */
          split: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * position. Otherwise returns false.
           */
          startsWith: PropTypes.func.isRequired,
          /**
           * Returns a <strike> HTML element
           */
          strike: PropTypes.func.isRequired,
          /**
           * Returns a <sub> HTML element
           */
          sub: PropTypes.func.isRequired,
          /**
           * Gets a substring beginning at the specified location and having the specified length.
           * @param from The starting position of the desired substring. The index of the first character in the string is zero.
           * @param length The number of characters to include in the returned substring.
           */
          substr: PropTypes.func.isRequired,
          /**
           * Returns the substring at the specified location within a String object.
           * @param start The zero-based index number indicating the beginning of the substring.
           * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
           * If end is omitted, the characters from start through the end of the original string are returned.
           */
          substring: PropTypes.func.isRequired,
          /**
           * Returns a <sup> HTML element
           */
          sup: PropTypes.func.isRequired,
          /**
           * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
           */
          toLocaleLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
           */
          toLocaleUpperCase: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to lowercase.
           */
          toLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string representation of a string.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to uppercase.
           */
          toUpperCase: PropTypes.func.isRequired,
          /**
           * Removes the leading and trailing white space and line terminator characters from a string.
           */
          trim: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimEnd: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimLeft: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimRight: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimStart: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
      ])
    ),
    PropTypes.object,
    PropTypes.shape({
      /**
       * Iterator
       */
      "__@iterator": PropTypes.func.isRequired,
      /**
       * Returns an <a> HTML anchor element and sets the name attribute to the text value
       * @param name
       */
      anchor: PropTypes.func.isRequired,
      /**
       * Returns a <big> HTML element
       */
      big: PropTypes.func.isRequired,
      /**
       * Returns a <blink> HTML element
       */
      blink: PropTypes.func.isRequired,
      /**
       * Returns a <b> HTML element
       */
      bold: PropTypes.func.isRequired,
      /**
       * Returns the character at the specified index.
       * @param pos The zero-based index of the desired character.
       */
      charAt: PropTypes.func.isRequired,
      /**
       * Returns the Unicode value of the character at the specified location.
       * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
       */
      charCodeAt: PropTypes.func.isRequired,
      /**
       * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
       * value of the UTF-16 encoded code point starting at the string element at position pos in
       * the String resulting from converting this object to a String.
       * If there is no element at that position, the result is undefined.
       * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
       */
      codePointAt: PropTypes.func.isRequired,
      /**
       * Returns a string that contains the concatenation of two or more strings.
       * @param strings The strings to append to the end of the string.
       */
      concat: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * endPosition – length(this). Otherwise returns false.
       */
      endsWith: PropTypes.func.isRequired,
      /**
       * Returns a <tt> HTML element
       */
      fixed: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the color attribute value
       */
      fontcolor: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the size attribute value
       */
      fontsize: PropTypes.func.isRequired,
      /**
       * Returns true if searchString appears as a substring of the result of converting this
       * object to a String, at one or more positions that are
       * greater than or equal to position; otherwise, returns false.
       * @param searchString search string
       * @param position If position is undefined, 0 is assumed, so as to search all of the String.
       */
      includes: PropTypes.func.isRequired,
      /**
       * Returns the position of the first occurrence of a substring.
       * @param searchString The substring to search for in the string
       * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
       */
      indexOf: PropTypes.func.isRequired,
      /**
       * Returns an <i> HTML element
       */
      italics: PropTypes.func.isRequired,
      /**
       * Returns the last occurrence of a substring in the string.
       * @param searchString The substring to search for.
       * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
       */
      lastIndexOf: PropTypes.func.isRequired,
      /**
       * Returns the length of a String object.
       */
      length: PropTypes.number.isRequired,
      /**
       * Returns an <a> HTML element and sets the href attribute value
       */
      link: PropTypes.func.isRequired,
      /**
       * Determines whether two strings are equivalent in the current locale.
       * @param that String to compare to target string
       */
      localeCompare: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an array containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      match: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an iterable of matches
       * containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      matchAll: PropTypes.func.isRequired,
      /**
       * Returns the String value result of normalizing the string into the normalization form
       * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
       * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
       * is "NFC"
       */
      normalize: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the end (right) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padEnd: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the start (left) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padStart: PropTypes.func.isRequired,
      /**
       * Returns a String value that is made from count copies appended together. If count is 0,
       * the empty string is returned.
       * @param count number of copies to append
       */
      repeat: PropTypes.func.isRequired,
      /**
       * Replaces text in a string, using a regular expression or search string.
       * @param searchValue A string to search for.
       * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
       */
      replace: PropTypes.func.isRequired,
      /**
       * Finds the first substring match in a regular expression search.
       * @param regexp The regular expression pattern and applicable flags.
       */
      search: PropTypes.func.isRequired,
      /**
       * Returns a section of a string.
       * @param start The index to the beginning of the specified portion of stringObj.
       * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
       * If this value is not specified, the substring continues to the end of stringObj.
       */
      slice: PropTypes.func.isRequired,
      /**
       * Returns a <small> HTML element
       */
      small: PropTypes.func.isRequired,
      /**
       * Split a string into substrings using the specified separator and return them as an array.
       * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
       * @param limit A value used to limit the number of elements returned in the array.
       */
      split: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * position. Otherwise returns false.
       */
      startsWith: PropTypes.func.isRequired,
      /**
       * Returns a <strike> HTML element
       */
      strike: PropTypes.func.isRequired,
      /**
       * Returns a <sub> HTML element
       */
      sub: PropTypes.func.isRequired,
      /**
       * Gets a substring beginning at the specified location and having the specified length.
       * @param from The starting position of the desired substring. The index of the first character in the string is zero.
       * @param length The number of characters to include in the returned substring.
       */
      substr: PropTypes.func.isRequired,
      /**
       * Returns the substring at the specified location within a String object.
       * @param start The zero-based index number indicating the beginning of the substring.
       * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
       * If end is omitted, the characters from start through the end of the original string are returned.
       */
      substring: PropTypes.func.isRequired,
      /**
       * Returns a <sup> HTML element
       */
      sup: PropTypes.func.isRequired,
      /**
       * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
       */
      toLocaleLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
       */
      toLocaleUpperCase: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to lowercase.
       */
      toLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string representation of a string.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to uppercase.
       */
      toUpperCase: PropTypes.func.isRequired,
      /**
       * Removes the leading and trailing white space and line terminator characters from a string.
       */
      trim: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimEnd: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimLeft: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimRight: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimStart: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
  ]),
  /**
   * The CSS justify-items property defines the default justify-self for all items of the box, giving them all
   * a default way of justifying each box along the appropriate axis.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/justify-items)
   */
  justifyItems: PropTypes.oneOfType([
    PropTypes.oneOf([
      "-moz-initial",
      "baseline",
      "center",
      "end",
      "flex-end",
      "flex-start",
      "inherit",
      "initial",
      "left",
      "legacy",
      "normal",
      "revert",
      "right",
      "self-end",
      "self-start",
      "start",
      "stretch",
      "unset",
    ]),
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([
          "-moz-initial",
          "baseline",
          "center",
          "end",
          "flex-end",
          "flex-start",
          "inherit",
          "initial",
          "left",
          "legacy",
          "normal",
          "revert",
          "right",
          "self-end",
          "self-start",
          "start",
          "stretch",
          "unset",
          null,
        ]),
        PropTypes.shape({
          /**
           * Iterator
           */
          "__@iterator": PropTypes.func.isRequired,
          /**
           * Returns an <a> HTML anchor element and sets the name attribute to the text value
           * @param name
           */
          anchor: PropTypes.func.isRequired,
          /**
           * Returns a <big> HTML element
           */
          big: PropTypes.func.isRequired,
          /**
           * Returns a <blink> HTML element
           */
          blink: PropTypes.func.isRequired,
          /**
           * Returns a <b> HTML element
           */
          bold: PropTypes.func.isRequired,
          /**
           * Returns the character at the specified index.
           * @param pos The zero-based index of the desired character.
           */
          charAt: PropTypes.func.isRequired,
          /**
           * Returns the Unicode value of the character at the specified location.
           * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
           */
          charCodeAt: PropTypes.func.isRequired,
          /**
           * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
           * value of the UTF-16 encoded code point starting at the string element at position pos in
           * the String resulting from converting this object to a String.
           * If there is no element at that position, the result is undefined.
           * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
           */
          codePointAt: PropTypes.func.isRequired,
          /**
           * Returns a string that contains the concatenation of two or more strings.
           * @param strings The strings to append to the end of the string.
           */
          concat: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * endPosition – length(this). Otherwise returns false.
           */
          endsWith: PropTypes.func.isRequired,
          /**
           * Returns a <tt> HTML element
           */
          fixed: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the color attribute value
           */
          fontcolor: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the size attribute value
           */
          fontsize: PropTypes.func.isRequired,
          /**
           * Returns true if searchString appears as a substring of the result of converting this
           * object to a String, at one or more positions that are
           * greater than or equal to position; otherwise, returns false.
           * @param searchString search string
           * @param position If position is undefined, 0 is assumed, so as to search all of the String.
           */
          includes: PropTypes.func.isRequired,
          /**
           * Returns the position of the first occurrence of a substring.
           * @param searchString The substring to search for in the string
           * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
           */
          indexOf: PropTypes.func.isRequired,
          /**
           * Returns an <i> HTML element
           */
          italics: PropTypes.func.isRequired,
          /**
           * Returns the last occurrence of a substring in the string.
           * @param searchString The substring to search for.
           * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
           */
          lastIndexOf: PropTypes.func.isRequired,
          /**
           * Returns the length of a String object.
           */
          length: PropTypes.number.isRequired,
          /**
           * Returns an <a> HTML element and sets the href attribute value
           */
          link: PropTypes.func.isRequired,
          /**
           * Determines whether two strings are equivalent in the current locale.
           * @param that String to compare to target string
           */
          localeCompare: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an array containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          match: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an iterable of matches
           * containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          matchAll: PropTypes.func.isRequired,
          /**
           * Returns the String value result of normalizing the string into the normalization form
           * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
           * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
           * is "NFC"
           */
          normalize: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the end (right) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padEnd: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the start (left) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padStart: PropTypes.func.isRequired,
          /**
           * Returns a String value that is made from count copies appended together. If count is 0,
           * the empty string is returned.
           * @param count number of copies to append
           */
          repeat: PropTypes.func.isRequired,
          /**
           * Replaces text in a string, using a regular expression or search string.
           * @param searchValue A string to search for.
           * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
           */
          replace: PropTypes.func.isRequired,
          /**
           * Finds the first substring match in a regular expression search.
           * @param regexp The regular expression pattern and applicable flags.
           */
          search: PropTypes.func.isRequired,
          /**
           * Returns a section of a string.
           * @param start The index to the beginning of the specified portion of stringObj.
           * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
           * If this value is not specified, the substring continues to the end of stringObj.
           */
          slice: PropTypes.func.isRequired,
          /**
           * Returns a <small> HTML element
           */
          small: PropTypes.func.isRequired,
          /**
           * Split a string into substrings using the specified separator and return them as an array.
           * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
           * @param limit A value used to limit the number of elements returned in the array.
           */
          split: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * position. Otherwise returns false.
           */
          startsWith: PropTypes.func.isRequired,
          /**
           * Returns a <strike> HTML element
           */
          strike: PropTypes.func.isRequired,
          /**
           * Returns a <sub> HTML element
           */
          sub: PropTypes.func.isRequired,
          /**
           * Gets a substring beginning at the specified location and having the specified length.
           * @param from The starting position of the desired substring. The index of the first character in the string is zero.
           * @param length The number of characters to include in the returned substring.
           */
          substr: PropTypes.func.isRequired,
          /**
           * Returns the substring at the specified location within a String object.
           * @param start The zero-based index number indicating the beginning of the substring.
           * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
           * If end is omitted, the characters from start through the end of the original string are returned.
           */
          substring: PropTypes.func.isRequired,
          /**
           * Returns a <sup> HTML element
           */
          sup: PropTypes.func.isRequired,
          /**
           * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
           */
          toLocaleLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
           */
          toLocaleUpperCase: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to lowercase.
           */
          toLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string representation of a string.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to uppercase.
           */
          toUpperCase: PropTypes.func.isRequired,
          /**
           * Removes the leading and trailing white space and line terminator characters from a string.
           */
          trim: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimEnd: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimLeft: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimRight: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimStart: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
      ])
    ),
    PropTypes.object,
    PropTypes.shape({
      /**
       * Iterator
       */
      "__@iterator": PropTypes.func.isRequired,
      /**
       * Returns an <a> HTML anchor element and sets the name attribute to the text value
       * @param name
       */
      anchor: PropTypes.func.isRequired,
      /**
       * Returns a <big> HTML element
       */
      big: PropTypes.func.isRequired,
      /**
       * Returns a <blink> HTML element
       */
      blink: PropTypes.func.isRequired,
      /**
       * Returns a <b> HTML element
       */
      bold: PropTypes.func.isRequired,
      /**
       * Returns the character at the specified index.
       * @param pos The zero-based index of the desired character.
       */
      charAt: PropTypes.func.isRequired,
      /**
       * Returns the Unicode value of the character at the specified location.
       * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
       */
      charCodeAt: PropTypes.func.isRequired,
      /**
       * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
       * value of the UTF-16 encoded code point starting at the string element at position pos in
       * the String resulting from converting this object to a String.
       * If there is no element at that position, the result is undefined.
       * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
       */
      codePointAt: PropTypes.func.isRequired,
      /**
       * Returns a string that contains the concatenation of two or more strings.
       * @param strings The strings to append to the end of the string.
       */
      concat: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * endPosition – length(this). Otherwise returns false.
       */
      endsWith: PropTypes.func.isRequired,
      /**
       * Returns a <tt> HTML element
       */
      fixed: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the color attribute value
       */
      fontcolor: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the size attribute value
       */
      fontsize: PropTypes.func.isRequired,
      /**
       * Returns true if searchString appears as a substring of the result of converting this
       * object to a String, at one or more positions that are
       * greater than or equal to position; otherwise, returns false.
       * @param searchString search string
       * @param position If position is undefined, 0 is assumed, so as to search all of the String.
       */
      includes: PropTypes.func.isRequired,
      /**
       * Returns the position of the first occurrence of a substring.
       * @param searchString The substring to search for in the string
       * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
       */
      indexOf: PropTypes.func.isRequired,
      /**
       * Returns an <i> HTML element
       */
      italics: PropTypes.func.isRequired,
      /**
       * Returns the last occurrence of a substring in the string.
       * @param searchString The substring to search for.
       * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
       */
      lastIndexOf: PropTypes.func.isRequired,
      /**
       * Returns the length of a String object.
       */
      length: PropTypes.number.isRequired,
      /**
       * Returns an <a> HTML element and sets the href attribute value
       */
      link: PropTypes.func.isRequired,
      /**
       * Determines whether two strings are equivalent in the current locale.
       * @param that String to compare to target string
       */
      localeCompare: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an array containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      match: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an iterable of matches
       * containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      matchAll: PropTypes.func.isRequired,
      /**
       * Returns the String value result of normalizing the string into the normalization form
       * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
       * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
       * is "NFC"
       */
      normalize: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the end (right) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padEnd: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the start (left) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padStart: PropTypes.func.isRequired,
      /**
       * Returns a String value that is made from count copies appended together. If count is 0,
       * the empty string is returned.
       * @param count number of copies to append
       */
      repeat: PropTypes.func.isRequired,
      /**
       * Replaces text in a string, using a regular expression or search string.
       * @param searchValue A string to search for.
       * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
       */
      replace: PropTypes.func.isRequired,
      /**
       * Finds the first substring match in a regular expression search.
       * @param regexp The regular expression pattern and applicable flags.
       */
      search: PropTypes.func.isRequired,
      /**
       * Returns a section of a string.
       * @param start The index to the beginning of the specified portion of stringObj.
       * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
       * If this value is not specified, the substring continues to the end of stringObj.
       */
      slice: PropTypes.func.isRequired,
      /**
       * Returns a <small> HTML element
       */
      small: PropTypes.func.isRequired,
      /**
       * Split a string into substrings using the specified separator and return them as an array.
       * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
       * @param limit A value used to limit the number of elements returned in the array.
       */
      split: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * position. Otherwise returns false.
       */
      startsWith: PropTypes.func.isRequired,
      /**
       * Returns a <strike> HTML element
       */
      strike: PropTypes.func.isRequired,
      /**
       * Returns a <sub> HTML element
       */
      sub: PropTypes.func.isRequired,
      /**
       * Gets a substring beginning at the specified location and having the specified length.
       * @param from The starting position of the desired substring. The index of the first character in the string is zero.
       * @param length The number of characters to include in the returned substring.
       */
      substr: PropTypes.func.isRequired,
      /**
       * Returns the substring at the specified location within a String object.
       * @param start The zero-based index number indicating the beginning of the substring.
       * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
       * If end is omitted, the characters from start through the end of the original string are returned.
       */
      substring: PropTypes.func.isRequired,
      /**
       * Returns a <sup> HTML element
       */
      sup: PropTypes.func.isRequired,
      /**
       * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
       */
      toLocaleLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
       */
      toLocaleUpperCase: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to lowercase.
       */
      toLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string representation of a string.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to uppercase.
       */
      toUpperCase: PropTypes.func.isRequired,
      /**
       * Removes the leading and trailing white space and line terminator characters from a string.
       */
      trim: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimEnd: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimLeft: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimRight: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimStart: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
  ]),
  /**
   * The CSS justify-self property set the way a box is justified inside its alignment container along
   * the appropriate axis.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/justify-self)
   */
  justifySelf: PropTypes.oneOfType([
    PropTypes.oneOf([
      "-moz-initial",
      "auto",
      "baseline",
      "center",
      "end",
      "flex-end",
      "flex-start",
      "inherit",
      "initial",
      "left",
      "normal",
      "revert",
      "right",
      "self-end",
      "self-start",
      "start",
      "stretch",
      "unset",
    ]),
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([
          "-moz-initial",
          "auto",
          "baseline",
          "center",
          "end",
          "flex-end",
          "flex-start",
          "inherit",
          "initial",
          "left",
          "normal",
          "revert",
          "right",
          "self-end",
          "self-start",
          "start",
          "stretch",
          "unset",
          null,
        ]),
        PropTypes.shape({
          /**
           * Iterator
           */
          "__@iterator": PropTypes.func.isRequired,
          /**
           * Returns an <a> HTML anchor element and sets the name attribute to the text value
           * @param name
           */
          anchor: PropTypes.func.isRequired,
          /**
           * Returns a <big> HTML element
           */
          big: PropTypes.func.isRequired,
          /**
           * Returns a <blink> HTML element
           */
          blink: PropTypes.func.isRequired,
          /**
           * Returns a <b> HTML element
           */
          bold: PropTypes.func.isRequired,
          /**
           * Returns the character at the specified index.
           * @param pos The zero-based index of the desired character.
           */
          charAt: PropTypes.func.isRequired,
          /**
           * Returns the Unicode value of the character at the specified location.
           * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
           */
          charCodeAt: PropTypes.func.isRequired,
          /**
           * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
           * value of the UTF-16 encoded code point starting at the string element at position pos in
           * the String resulting from converting this object to a String.
           * If there is no element at that position, the result is undefined.
           * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
           */
          codePointAt: PropTypes.func.isRequired,
          /**
           * Returns a string that contains the concatenation of two or more strings.
           * @param strings The strings to append to the end of the string.
           */
          concat: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * endPosition – length(this). Otherwise returns false.
           */
          endsWith: PropTypes.func.isRequired,
          /**
           * Returns a <tt> HTML element
           */
          fixed: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the color attribute value
           */
          fontcolor: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the size attribute value
           */
          fontsize: PropTypes.func.isRequired,
          /**
           * Returns true if searchString appears as a substring of the result of converting this
           * object to a String, at one or more positions that are
           * greater than or equal to position; otherwise, returns false.
           * @param searchString search string
           * @param position If position is undefined, 0 is assumed, so as to search all of the String.
           */
          includes: PropTypes.func.isRequired,
          /**
           * Returns the position of the first occurrence of a substring.
           * @param searchString The substring to search for in the string
           * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
           */
          indexOf: PropTypes.func.isRequired,
          /**
           * Returns an <i> HTML element
           */
          italics: PropTypes.func.isRequired,
          /**
           * Returns the last occurrence of a substring in the string.
           * @param searchString The substring to search for.
           * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
           */
          lastIndexOf: PropTypes.func.isRequired,
          /**
           * Returns the length of a String object.
           */
          length: PropTypes.number.isRequired,
          /**
           * Returns an <a> HTML element and sets the href attribute value
           */
          link: PropTypes.func.isRequired,
          /**
           * Determines whether two strings are equivalent in the current locale.
           * @param that String to compare to target string
           */
          localeCompare: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an array containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          match: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an iterable of matches
           * containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          matchAll: PropTypes.func.isRequired,
          /**
           * Returns the String value result of normalizing the string into the normalization form
           * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
           * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
           * is "NFC"
           */
          normalize: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the end (right) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padEnd: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the start (left) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padStart: PropTypes.func.isRequired,
          /**
           * Returns a String value that is made from count copies appended together. If count is 0,
           * the empty string is returned.
           * @param count number of copies to append
           */
          repeat: PropTypes.func.isRequired,
          /**
           * Replaces text in a string, using a regular expression or search string.
           * @param searchValue A string to search for.
           * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
           */
          replace: PropTypes.func.isRequired,
          /**
           * Finds the first substring match in a regular expression search.
           * @param regexp The regular expression pattern and applicable flags.
           */
          search: PropTypes.func.isRequired,
          /**
           * Returns a section of a string.
           * @param start The index to the beginning of the specified portion of stringObj.
           * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
           * If this value is not specified, the substring continues to the end of stringObj.
           */
          slice: PropTypes.func.isRequired,
          /**
           * Returns a <small> HTML element
           */
          small: PropTypes.func.isRequired,
          /**
           * Split a string into substrings using the specified separator and return them as an array.
           * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
           * @param limit A value used to limit the number of elements returned in the array.
           */
          split: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * position. Otherwise returns false.
           */
          startsWith: PropTypes.func.isRequired,
          /**
           * Returns a <strike> HTML element
           */
          strike: PropTypes.func.isRequired,
          /**
           * Returns a <sub> HTML element
           */
          sub: PropTypes.func.isRequired,
          /**
           * Gets a substring beginning at the specified location and having the specified length.
           * @param from The starting position of the desired substring. The index of the first character in the string is zero.
           * @param length The number of characters to include in the returned substring.
           */
          substr: PropTypes.func.isRequired,
          /**
           * Returns the substring at the specified location within a String object.
           * @param start The zero-based index number indicating the beginning of the substring.
           * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
           * If end is omitted, the characters from start through the end of the original string are returned.
           */
          substring: PropTypes.func.isRequired,
          /**
           * Returns a <sup> HTML element
           */
          sup: PropTypes.func.isRequired,
          /**
           * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
           */
          toLocaleLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
           */
          toLocaleUpperCase: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to lowercase.
           */
          toLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string representation of a string.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to uppercase.
           */
          toUpperCase: PropTypes.func.isRequired,
          /**
           * Removes the leading and trailing white space and line terminator characters from a string.
           */
          trim: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimEnd: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimLeft: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimRight: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimStart: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
      ])
    ),
    PropTypes.object,
    PropTypes.shape({
      /**
       * Iterator
       */
      "__@iterator": PropTypes.func.isRequired,
      /**
       * Returns an <a> HTML anchor element and sets the name attribute to the text value
       * @param name
       */
      anchor: PropTypes.func.isRequired,
      /**
       * Returns a <big> HTML element
       */
      big: PropTypes.func.isRequired,
      /**
       * Returns a <blink> HTML element
       */
      blink: PropTypes.func.isRequired,
      /**
       * Returns a <b> HTML element
       */
      bold: PropTypes.func.isRequired,
      /**
       * Returns the character at the specified index.
       * @param pos The zero-based index of the desired character.
       */
      charAt: PropTypes.func.isRequired,
      /**
       * Returns the Unicode value of the character at the specified location.
       * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
       */
      charCodeAt: PropTypes.func.isRequired,
      /**
       * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
       * value of the UTF-16 encoded code point starting at the string element at position pos in
       * the String resulting from converting this object to a String.
       * If there is no element at that position, the result is undefined.
       * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
       */
      codePointAt: PropTypes.func.isRequired,
      /**
       * Returns a string that contains the concatenation of two or more strings.
       * @param strings The strings to append to the end of the string.
       */
      concat: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * endPosition – length(this). Otherwise returns false.
       */
      endsWith: PropTypes.func.isRequired,
      /**
       * Returns a <tt> HTML element
       */
      fixed: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the color attribute value
       */
      fontcolor: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the size attribute value
       */
      fontsize: PropTypes.func.isRequired,
      /**
       * Returns true if searchString appears as a substring of the result of converting this
       * object to a String, at one or more positions that are
       * greater than or equal to position; otherwise, returns false.
       * @param searchString search string
       * @param position If position is undefined, 0 is assumed, so as to search all of the String.
       */
      includes: PropTypes.func.isRequired,
      /**
       * Returns the position of the first occurrence of a substring.
       * @param searchString The substring to search for in the string
       * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
       */
      indexOf: PropTypes.func.isRequired,
      /**
       * Returns an <i> HTML element
       */
      italics: PropTypes.func.isRequired,
      /**
       * Returns the last occurrence of a substring in the string.
       * @param searchString The substring to search for.
       * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
       */
      lastIndexOf: PropTypes.func.isRequired,
      /**
       * Returns the length of a String object.
       */
      length: PropTypes.number.isRequired,
      /**
       * Returns an <a> HTML element and sets the href attribute value
       */
      link: PropTypes.func.isRequired,
      /**
       * Determines whether two strings are equivalent in the current locale.
       * @param that String to compare to target string
       */
      localeCompare: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an array containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      match: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an iterable of matches
       * containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      matchAll: PropTypes.func.isRequired,
      /**
       * Returns the String value result of normalizing the string into the normalization form
       * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
       * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
       * is "NFC"
       */
      normalize: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the end (right) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padEnd: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the start (left) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padStart: PropTypes.func.isRequired,
      /**
       * Returns a String value that is made from count copies appended together. If count is 0,
       * the empty string is returned.
       * @param count number of copies to append
       */
      repeat: PropTypes.func.isRequired,
      /**
       * Replaces text in a string, using a regular expression or search string.
       * @param searchValue A string to search for.
       * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
       */
      replace: PropTypes.func.isRequired,
      /**
       * Finds the first substring match in a regular expression search.
       * @param regexp The regular expression pattern and applicable flags.
       */
      search: PropTypes.func.isRequired,
      /**
       * Returns a section of a string.
       * @param start The index to the beginning of the specified portion of stringObj.
       * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
       * If this value is not specified, the substring continues to the end of stringObj.
       */
      slice: PropTypes.func.isRequired,
      /**
       * Returns a <small> HTML element
       */
      small: PropTypes.func.isRequired,
      /**
       * Split a string into substrings using the specified separator and return them as an array.
       * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
       * @param limit A value used to limit the number of elements returned in the array.
       */
      split: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * position. Otherwise returns false.
       */
      startsWith: PropTypes.func.isRequired,
      /**
       * Returns a <strike> HTML element
       */
      strike: PropTypes.func.isRequired,
      /**
       * Returns a <sub> HTML element
       */
      sub: PropTypes.func.isRequired,
      /**
       * Gets a substring beginning at the specified location and having the specified length.
       * @param from The starting position of the desired substring. The index of the first character in the string is zero.
       * @param length The number of characters to include in the returned substring.
       */
      substr: PropTypes.func.isRequired,
      /**
       * Returns the substring at the specified location within a String object.
       * @param start The zero-based index number indicating the beginning of the substring.
       * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
       * If end is omitted, the characters from start through the end of the original string are returned.
       */
      substring: PropTypes.func.isRequired,
      /**
       * Returns a <sup> HTML element
       */
      sup: PropTypes.func.isRequired,
      /**
       * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
       */
      toLocaleLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
       */
      toLocaleUpperCase: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to lowercase.
       */
      toLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string representation of a string.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to uppercase.
       */
      toUpperCase: PropTypes.func.isRequired,
      /**
       * Removes the leading and trailing white space and line terminator characters from a string.
       */
      trim: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimEnd: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimLeft: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimRight: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimStart: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
  ]),
  /**
   * Color scheme of navigation component
   */
  navigationType: PropTypes.oneOf(["black", "dark", "light", "white"]),
  /**
   * Defines the offset of navigation bar
   */
  offset: PropTypes.string,
  /**
   * The order CSS property sets the order to lay out an item in a flex or grid container. Items in a container
   * are sorted by ascending order value and then by their source code order.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/order)
   */
  order: PropTypes.oneOfType([
    PropTypes.oneOf(["-moz-initial", "inherit", "initial", "revert", "unset"]),
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([
          "-moz-initial",
          "inherit",
          "initial",
          "revert",
          "unset",
          null,
        ]),
        PropTypes.shape({
          /**
           * Returns a string containing a number represented in exponential notation.
           * @param fractionDigits Number of digits after the decimal point. Must be in the range 0 - 20, inclusive.
           */
          toExponential: PropTypes.func.isRequired,
          /**
           * Returns a string representing a number in fixed-point notation.
           * @param fractionDigits Number of digits after the decimal point. Must be in the range 0 - 20, inclusive.
           */
          toFixed: PropTypes.func.isRequired,
          /**
           * Converts a number to a string by using the current or specified locale.
           * @param locales A locale string or array of locale strings that contain one or more language or locale tags. If you include more than one locale string, list them in descending order of priority so that the first entry is the preferred locale. If you omit this parameter, the default locale of the JavaScript runtime is used.
           * @param options An object that contains one or more properties that specify comparison options.
           */
          toLocaleString: PropTypes.func.isRequired,
          /**
           * Returns a string containing a number represented either in exponential or fixed-point notation with a specified number of digits.
           * @param precision Number of significant digits. Must be in the range 1 - 21, inclusive.
           */
          toPrecision: PropTypes.func.isRequired,
          /**
           * Returns a string representation of an object.
           * @param radix Specifies a radix for converting numeric values to strings. This value is only used for numbers.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
      ])
    ),
    PropTypes.object,
    PropTypes.shape({
      /**
       * Returns a string containing a number represented in exponential notation.
       * @param fractionDigits Number of digits after the decimal point. Must be in the range 0 - 20, inclusive.
       */
      toExponential: PropTypes.func.isRequired,
      /**
       * Returns a string representing a number in fixed-point notation.
       * @param fractionDigits Number of digits after the decimal point. Must be in the range 0 - 20, inclusive.
       */
      toFixed: PropTypes.func.isRequired,
      /**
       * Converts a number to a string by using the current or specified locale.
       * @param locales A locale string or array of locale strings that contain one or more language or locale tags. If you include more than one locale string, list them in descending order of priority so that the first entry is the preferred locale. If you omit this parameter, the default locale of the JavaScript runtime is used.
       * @param options An object that contains one or more properties that specify comparison options.
       */
      toLocaleString: PropTypes.func.isRequired,
      /**
       * Returns a string containing a number represented either in exponential or fixed-point notation with a specified number of digits.
       * @param precision Number of significant digits. Must be in the range 1 - 21, inclusive.
       */
      toPrecision: PropTypes.func.isRequired,
      /**
       * Returns a string representation of an object.
       * @param radix Specifies a radix for converting numeric values to strings. This value is only used for numbers.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
  ]),
  /**
   * Defines whether the navigation bar should be positioned top or bottom
   */
  orientation: PropTypes.oneOf(["bottom", "top"]),
  /**
   * Padding on top, left, bottom and right
   */
  p: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          "__@toStringTag": PropTypes.string.isRequired,
          /**
           * Expose the [[Description]] internal slot of a symbol directly.
           */
          description: PropTypes.string,
          /**
           * Returns a string representation of an object.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      "__@toStringTag": PropTypes.string.isRequired,
      /**
       * Expose the [[Description]] internal slot of a symbol directly.
       */
      description: PropTypes.string,
      /**
       * Returns a string representation of an object.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  /**
   * Padding on top, left, bottom and right
   */
  padding: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          "__@toStringTag": PropTypes.string.isRequired,
          /**
           * Expose the [[Description]] internal slot of a symbol directly.
           */
          description: PropTypes.string,
          /**
           * Returns a string representation of an object.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      "__@toStringTag": PropTypes.string.isRequired,
      /**
       * Expose the [[Description]] internal slot of a symbol directly.
       */
      description: PropTypes.string,
      /**
       * Returns a string representation of an object.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  /**
   * Padding on bottom
   */
  paddingBottom: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          "__@toStringTag": PropTypes.string.isRequired,
          /**
           * Expose the [[Description]] internal slot of a symbol directly.
           */
          description: PropTypes.string,
          /**
           * Returns a string representation of an object.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      "__@toStringTag": PropTypes.string.isRequired,
      /**
       * Expose the [[Description]] internal slot of a symbol directly.
       */
      description: PropTypes.string,
      /**
       * Returns a string representation of an object.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  /**
   * Padding on left
   */
  paddingLeft: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          "__@toStringTag": PropTypes.string.isRequired,
          /**
           * Expose the [[Description]] internal slot of a symbol directly.
           */
          description: PropTypes.string,
          /**
           * Returns a string representation of an object.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      "__@toStringTag": PropTypes.string.isRequired,
      /**
       * Expose the [[Description]] internal slot of a symbol directly.
       */
      description: PropTypes.string,
      /**
       * Returns a string representation of an object.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  /**
   * Padding on right
   */
  paddingRight: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          "__@toStringTag": PropTypes.string.isRequired,
          /**
           * Expose the [[Description]] internal slot of a symbol directly.
           */
          description: PropTypes.string,
          /**
           * Returns a string representation of an object.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      "__@toStringTag": PropTypes.string.isRequired,
      /**
       * Expose the [[Description]] internal slot of a symbol directly.
       */
      description: PropTypes.string,
      /**
       * Returns a string representation of an object.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  /**
   * Padding on top
   */
  paddingTop: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          "__@toStringTag": PropTypes.string.isRequired,
          /**
           * Expose the [[Description]] internal slot of a symbol directly.
           */
          description: PropTypes.string,
          /**
           * Returns a string representation of an object.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      "__@toStringTag": PropTypes.string.isRequired,
      /**
       * Expose the [[Description]] internal slot of a symbol directly.
       */
      description: PropTypes.string,
      /**
       * Returns a string representation of an object.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  /**
   * Padding on left and right
   */
  paddingX: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          "__@toStringTag": PropTypes.string.isRequired,
          /**
           * Expose the [[Description]] internal slot of a symbol directly.
           */
          description: PropTypes.string,
          /**
           * Returns a string representation of an object.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      "__@toStringTag": PropTypes.string.isRequired,
      /**
       * Expose the [[Description]] internal slot of a symbol directly.
       */
      description: PropTypes.string,
      /**
       * Returns a string representation of an object.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  /**
   * Padding on top and bottom
   */
  paddingY: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          "__@toStringTag": PropTypes.string.isRequired,
          /**
           * Expose the [[Description]] internal slot of a symbol directly.
           */
          description: PropTypes.string,
          /**
           * Returns a string representation of an object.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      "__@toStringTag": PropTypes.string.isRequired,
      /**
       * Expose the [[Description]] internal slot of a symbol directly.
       */
      description: PropTypes.string,
      /**
       * Returns a string representation of an object.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  /**
   * Padding on bottom
   */
  pb: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          "__@toStringTag": PropTypes.string.isRequired,
          /**
           * Expose the [[Description]] internal slot of a symbol directly.
           */
          description: PropTypes.string,
          /**
           * Returns a string representation of an object.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      "__@toStringTag": PropTypes.string.isRequired,
      /**
       * Expose the [[Description]] internal slot of a symbol directly.
       */
      description: PropTypes.string,
      /**
       * Returns a string representation of an object.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  /**
   * Padding on left
   */
  pl: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          "__@toStringTag": PropTypes.string.isRequired,
          /**
           * Expose the [[Description]] internal slot of a symbol directly.
           */
          description: PropTypes.string,
          /**
           * Returns a string representation of an object.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      "__@toStringTag": PropTypes.string.isRequired,
      /**
       * Expose the [[Description]] internal slot of a symbol directly.
       */
      description: PropTypes.string,
      /**
       * Returns a string representation of an object.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  /**
   * Defines whether the navigation bar should be positioned fixed or sticky
   */
  position: PropTypes.oneOf(["fixed", "sticky"]),
  /**
   * Padding on right
   */
  pr: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          "__@toStringTag": PropTypes.string.isRequired,
          /**
           * Expose the [[Description]] internal slot of a symbol directly.
           */
          description: PropTypes.string,
          /**
           * Returns a string representation of an object.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      "__@toStringTag": PropTypes.string.isRequired,
      /**
       * Expose the [[Description]] internal slot of a symbol directly.
       */
      description: PropTypes.string,
      /**
       * Returns a string representation of an object.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  /**
   * Padding on top
   */
  pt: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          "__@toStringTag": PropTypes.string.isRequired,
          /**
           * Expose the [[Description]] internal slot of a symbol directly.
           */
          description: PropTypes.string,
          /**
           * Returns a string representation of an object.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      "__@toStringTag": PropTypes.string.isRequired,
      /**
       * Expose the [[Description]] internal slot of a symbol directly.
       */
      description: PropTypes.string,
      /**
       * Returns a string representation of an object.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  /**
   * Padding on left and right
   */
  px: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          "__@toStringTag": PropTypes.string.isRequired,
          /**
           * Expose the [[Description]] internal slot of a symbol directly.
           */
          description: PropTypes.string,
          /**
           * Returns a string representation of an object.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      "__@toStringTag": PropTypes.string.isRequired,
      /**
       * Expose the [[Description]] internal slot of a symbol directly.
       */
      description: PropTypes.string,
      /**
       * Returns a string representation of an object.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  /**
   * Padding on top and bottom
   */
  py: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          "__@toStringTag": PropTypes.string.isRequired,
          /**
           * Expose the [[Description]] internal slot of a symbol directly.
           */
          description: PropTypes.string,
          /**
           * Returns a string representation of an object.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      "__@toStringTag": PropTypes.string.isRequired,
      /**
       * Expose the [[Description]] internal slot of a symbol directly.
       */
      description: PropTypes.string,
      /**
       * Returns a string representation of an object.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  /**
   * Defines the offset of sticky navigation bar
   */
  stickyOffset: PropTypes.string,
  /**
   * Defines the position of sticky navigation bar
   */
  stickyPosition: PropTypes.oneOf(["bottom", "top"]),
};

NavigationBar.propTypes = {
  /**
   * The CSS align-content property sets how the browser distributes space between and around content items
   * along the cross-axis of a flexbox container, and the main-axis of a grid container.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/align-content)
   */
  alignContent: PropTypes.oneOfType([
    PropTypes.oneOf([
      "-moz-initial",
      "baseline",
      "center",
      "end",
      "flex-end",
      "flex-start",
      "inherit",
      "initial",
      "normal",
      "revert",
      "space-around",
      "space-between",
      "space-evenly",
      "start",
      "stretch",
      "unset",
    ]),
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([
          "-moz-initial",
          "baseline",
          "center",
          "end",
          "flex-end",
          "flex-start",
          "inherit",
          "initial",
          "normal",
          "revert",
          "space-around",
          "space-between",
          "space-evenly",
          "start",
          "stretch",
          "unset",
          null,
        ]),
        PropTypes.shape({
          /**
           * Iterator
           */
          "__@iterator": PropTypes.func.isRequired,
          /**
           * Returns an <a> HTML anchor element and sets the name attribute to the text value
           * @param name
           */
          anchor: PropTypes.func.isRequired,
          /**
           * Returns a <big> HTML element
           */
          big: PropTypes.func.isRequired,
          /**
           * Returns a <blink> HTML element
           */
          blink: PropTypes.func.isRequired,
          /**
           * Returns a <b> HTML element
           */
          bold: PropTypes.func.isRequired,
          /**
           * Returns the character at the specified index.
           * @param pos The zero-based index of the desired character.
           */
          charAt: PropTypes.func.isRequired,
          /**
           * Returns the Unicode value of the character at the specified location.
           * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
           */
          charCodeAt: PropTypes.func.isRequired,
          /**
           * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
           * value of the UTF-16 encoded code point starting at the string element at position pos in
           * the String resulting from converting this object to a String.
           * If there is no element at that position, the result is undefined.
           * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
           */
          codePointAt: PropTypes.func.isRequired,
          /**
           * Returns a string that contains the concatenation of two or more strings.
           * @param strings The strings to append to the end of the string.
           */
          concat: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * endPosition – length(this). Otherwise returns false.
           */
          endsWith: PropTypes.func.isRequired,
          /**
           * Returns a <tt> HTML element
           */
          fixed: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the color attribute value
           */
          fontcolor: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the size attribute value
           */
          fontsize: PropTypes.func.isRequired,
          /**
           * Returns true if searchString appears as a substring of the result of converting this
           * object to a String, at one or more positions that are
           * greater than or equal to position; otherwise, returns false.
           * @param searchString search string
           * @param position If position is undefined, 0 is assumed, so as to search all of the String.
           */
          includes: PropTypes.func.isRequired,
          /**
           * Returns the position of the first occurrence of a substring.
           * @param searchString The substring to search for in the string
           * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
           */
          indexOf: PropTypes.func.isRequired,
          /**
           * Returns an <i> HTML element
           */
          italics: PropTypes.func.isRequired,
          /**
           * Returns the last occurrence of a substring in the string.
           * @param searchString The substring to search for.
           * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
           */
          lastIndexOf: PropTypes.func.isRequired,
          /**
           * Returns the length of a String object.
           */
          length: PropTypes.number.isRequired,
          /**
           * Returns an <a> HTML element and sets the href attribute value
           */
          link: PropTypes.func.isRequired,
          /**
           * Determines whether two strings are equivalent in the current locale.
           * @param that String to compare to target string
           */
          localeCompare: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an array containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          match: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an iterable of matches
           * containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          matchAll: PropTypes.func.isRequired,
          /**
           * Returns the String value result of normalizing the string into the normalization form
           * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
           * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
           * is "NFC"
           */
          normalize: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the end (right) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padEnd: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the start (left) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padStart: PropTypes.func.isRequired,
          /**
           * Returns a String value that is made from count copies appended together. If count is 0,
           * the empty string is returned.
           * @param count number of copies to append
           */
          repeat: PropTypes.func.isRequired,
          /**
           * Replaces text in a string, using a regular expression or search string.
           * @param searchValue A string to search for.
           * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
           */
          replace: PropTypes.func.isRequired,
          /**
           * Finds the first substring match in a regular expression search.
           * @param regexp The regular expression pattern and applicable flags.
           */
          search: PropTypes.func.isRequired,
          /**
           * Returns a section of a string.
           * @param start The index to the beginning of the specified portion of stringObj.
           * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
           * If this value is not specified, the substring continues to the end of stringObj.
           */
          slice: PropTypes.func.isRequired,
          /**
           * Returns a <small> HTML element
           */
          small: PropTypes.func.isRequired,
          /**
           * Split a string into substrings using the specified separator and return them as an array.
           * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
           * @param limit A value used to limit the number of elements returned in the array.
           */
          split: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * position. Otherwise returns false.
           */
          startsWith: PropTypes.func.isRequired,
          /**
           * Returns a <strike> HTML element
           */
          strike: PropTypes.func.isRequired,
          /**
           * Returns a <sub> HTML element
           */
          sub: PropTypes.func.isRequired,
          /**
           * Gets a substring beginning at the specified location and having the specified length.
           * @param from The starting position of the desired substring. The index of the first character in the string is zero.
           * @param length The number of characters to include in the returned substring.
           */
          substr: PropTypes.func.isRequired,
          /**
           * Returns the substring at the specified location within a String object.
           * @param start The zero-based index number indicating the beginning of the substring.
           * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
           * If end is omitted, the characters from start through the end of the original string are returned.
           */
          substring: PropTypes.func.isRequired,
          /**
           * Returns a <sup> HTML element
           */
          sup: PropTypes.func.isRequired,
          /**
           * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
           */
          toLocaleLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
           */
          toLocaleUpperCase: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to lowercase.
           */
          toLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string representation of a string.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to uppercase.
           */
          toUpperCase: PropTypes.func.isRequired,
          /**
           * Removes the leading and trailing white space and line terminator characters from a string.
           */
          trim: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimEnd: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimLeft: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimRight: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimStart: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
      ])
    ),
    PropTypes.object,
    PropTypes.shape({
      /**
       * Iterator
       */
      "__@iterator": PropTypes.func.isRequired,
      /**
       * Returns an <a> HTML anchor element and sets the name attribute to the text value
       * @param name
       */
      anchor: PropTypes.func.isRequired,
      /**
       * Returns a <big> HTML element
       */
      big: PropTypes.func.isRequired,
      /**
       * Returns a <blink> HTML element
       */
      blink: PropTypes.func.isRequired,
      /**
       * Returns a <b> HTML element
       */
      bold: PropTypes.func.isRequired,
      /**
       * Returns the character at the specified index.
       * @param pos The zero-based index of the desired character.
       */
      charAt: PropTypes.func.isRequired,
      /**
       * Returns the Unicode value of the character at the specified location.
       * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
       */
      charCodeAt: PropTypes.func.isRequired,
      /**
       * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
       * value of the UTF-16 encoded code point starting at the string element at position pos in
       * the String resulting from converting this object to a String.
       * If there is no element at that position, the result is undefined.
       * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
       */
      codePointAt: PropTypes.func.isRequired,
      /**
       * Returns a string that contains the concatenation of two or more strings.
       * @param strings The strings to append to the end of the string.
       */
      concat: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * endPosition – length(this). Otherwise returns false.
       */
      endsWith: PropTypes.func.isRequired,
      /**
       * Returns a <tt> HTML element
       */
      fixed: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the color attribute value
       */
      fontcolor: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the size attribute value
       */
      fontsize: PropTypes.func.isRequired,
      /**
       * Returns true if searchString appears as a substring of the result of converting this
       * object to a String, at one or more positions that are
       * greater than or equal to position; otherwise, returns false.
       * @param searchString search string
       * @param position If position is undefined, 0 is assumed, so as to search all of the String.
       */
      includes: PropTypes.func.isRequired,
      /**
       * Returns the position of the first occurrence of a substring.
       * @param searchString The substring to search for in the string
       * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
       */
      indexOf: PropTypes.func.isRequired,
      /**
       * Returns an <i> HTML element
       */
      italics: PropTypes.func.isRequired,
      /**
       * Returns the last occurrence of a substring in the string.
       * @param searchString The substring to search for.
       * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
       */
      lastIndexOf: PropTypes.func.isRequired,
      /**
       * Returns the length of a String object.
       */
      length: PropTypes.number.isRequired,
      /**
       * Returns an <a> HTML element and sets the href attribute value
       */
      link: PropTypes.func.isRequired,
      /**
       * Determines whether two strings are equivalent in the current locale.
       * @param that String to compare to target string
       */
      localeCompare: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an array containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      match: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an iterable of matches
       * containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      matchAll: PropTypes.func.isRequired,
      /**
       * Returns the String value result of normalizing the string into the normalization form
       * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
       * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
       * is "NFC"
       */
      normalize: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the end (right) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padEnd: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the start (left) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padStart: PropTypes.func.isRequired,
      /**
       * Returns a String value that is made from count copies appended together. If count is 0,
       * the empty string is returned.
       * @param count number of copies to append
       */
      repeat: PropTypes.func.isRequired,
      /**
       * Replaces text in a string, using a regular expression or search string.
       * @param searchValue A string to search for.
       * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
       */
      replace: PropTypes.func.isRequired,
      /**
       * Finds the first substring match in a regular expression search.
       * @param regexp The regular expression pattern and applicable flags.
       */
      search: PropTypes.func.isRequired,
      /**
       * Returns a section of a string.
       * @param start The index to the beginning of the specified portion of stringObj.
       * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
       * If this value is not specified, the substring continues to the end of stringObj.
       */
      slice: PropTypes.func.isRequired,
      /**
       * Returns a <small> HTML element
       */
      small: PropTypes.func.isRequired,
      /**
       * Split a string into substrings using the specified separator and return them as an array.
       * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
       * @param limit A value used to limit the number of elements returned in the array.
       */
      split: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * position. Otherwise returns false.
       */
      startsWith: PropTypes.func.isRequired,
      /**
       * Returns a <strike> HTML element
       */
      strike: PropTypes.func.isRequired,
      /**
       * Returns a <sub> HTML element
       */
      sub: PropTypes.func.isRequired,
      /**
       * Gets a substring beginning at the specified location and having the specified length.
       * @param from The starting position of the desired substring. The index of the first character in the string is zero.
       * @param length The number of characters to include in the returned substring.
       */
      substr: PropTypes.func.isRequired,
      /**
       * Returns the substring at the specified location within a String object.
       * @param start The zero-based index number indicating the beginning of the substring.
       * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
       * If end is omitted, the characters from start through the end of the original string are returned.
       */
      substring: PropTypes.func.isRequired,
      /**
       * Returns a <sup> HTML element
       */
      sup: PropTypes.func.isRequired,
      /**
       * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
       */
      toLocaleLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
       */
      toLocaleUpperCase: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to lowercase.
       */
      toLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string representation of a string.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to uppercase.
       */
      toUpperCase: PropTypes.func.isRequired,
      /**
       * Removes the leading and trailing white space and line terminator characters from a string.
       */
      trim: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimEnd: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimLeft: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimRight: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimStart: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
  ]),
  /**
   * The CSS align-items property sets the align-self value on all direct children as a group. The align-self
   * property sets the alignment of an item within its containing block.
   *
   * In Flexbox it controls the alignment of items on the Cross Axis, in Grid Layout it controls the alignment
   * of items on the Block Axis within their grid area.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/align-items)
   */
  alignItems: PropTypes.oneOfType([
    PropTypes.oneOf([
      "-moz-initial",
      "baseline",
      "center",
      "end",
      "flex-end",
      "flex-start",
      "inherit",
      "initial",
      "normal",
      "revert",
      "self-end",
      "self-start",
      "start",
      "stretch",
      "unset",
    ]),
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([
          "-moz-initial",
          "baseline",
          "center",
          "end",
          "flex-end",
          "flex-start",
          "inherit",
          "initial",
          "normal",
          "revert",
          "self-end",
          "self-start",
          "start",
          "stretch",
          "unset",
          null,
        ]),
        PropTypes.shape({
          /**
           * Iterator
           */
          "__@iterator": PropTypes.func.isRequired,
          /**
           * Returns an <a> HTML anchor element and sets the name attribute to the text value
           * @param name
           */
          anchor: PropTypes.func.isRequired,
          /**
           * Returns a <big> HTML element
           */
          big: PropTypes.func.isRequired,
          /**
           * Returns a <blink> HTML element
           */
          blink: PropTypes.func.isRequired,
          /**
           * Returns a <b> HTML element
           */
          bold: PropTypes.func.isRequired,
          /**
           * Returns the character at the specified index.
           * @param pos The zero-based index of the desired character.
           */
          charAt: PropTypes.func.isRequired,
          /**
           * Returns the Unicode value of the character at the specified location.
           * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
           */
          charCodeAt: PropTypes.func.isRequired,
          /**
           * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
           * value of the UTF-16 encoded code point starting at the string element at position pos in
           * the String resulting from converting this object to a String.
           * If there is no element at that position, the result is undefined.
           * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
           */
          codePointAt: PropTypes.func.isRequired,
          /**
           * Returns a string that contains the concatenation of two or more strings.
           * @param strings The strings to append to the end of the string.
           */
          concat: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * endPosition – length(this). Otherwise returns false.
           */
          endsWith: PropTypes.func.isRequired,
          /**
           * Returns a <tt> HTML element
           */
          fixed: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the color attribute value
           */
          fontcolor: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the size attribute value
           */
          fontsize: PropTypes.func.isRequired,
          /**
           * Returns true if searchString appears as a substring of the result of converting this
           * object to a String, at one or more positions that are
           * greater than or equal to position; otherwise, returns false.
           * @param searchString search string
           * @param position If position is undefined, 0 is assumed, so as to search all of the String.
           */
          includes: PropTypes.func.isRequired,
          /**
           * Returns the position of the first occurrence of a substring.
           * @param searchString The substring to search for in the string
           * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
           */
          indexOf: PropTypes.func.isRequired,
          /**
           * Returns an <i> HTML element
           */
          italics: PropTypes.func.isRequired,
          /**
           * Returns the last occurrence of a substring in the string.
           * @param searchString The substring to search for.
           * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
           */
          lastIndexOf: PropTypes.func.isRequired,
          /**
           * Returns the length of a String object.
           */
          length: PropTypes.number.isRequired,
          /**
           * Returns an <a> HTML element and sets the href attribute value
           */
          link: PropTypes.func.isRequired,
          /**
           * Determines whether two strings are equivalent in the current locale.
           * @param that String to compare to target string
           */
          localeCompare: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an array containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          match: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an iterable of matches
           * containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          matchAll: PropTypes.func.isRequired,
          /**
           * Returns the String value result of normalizing the string into the normalization form
           * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
           * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
           * is "NFC"
           */
          normalize: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the end (right) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padEnd: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the start (left) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padStart: PropTypes.func.isRequired,
          /**
           * Returns a String value that is made from count copies appended together. If count is 0,
           * the empty string is returned.
           * @param count number of copies to append
           */
          repeat: PropTypes.func.isRequired,
          /**
           * Replaces text in a string, using a regular expression or search string.
           * @param searchValue A string to search for.
           * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
           */
          replace: PropTypes.func.isRequired,
          /**
           * Finds the first substring match in a regular expression search.
           * @param regexp The regular expression pattern and applicable flags.
           */
          search: PropTypes.func.isRequired,
          /**
           * Returns a section of a string.
           * @param start The index to the beginning of the specified portion of stringObj.
           * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
           * If this value is not specified, the substring continues to the end of stringObj.
           */
          slice: PropTypes.func.isRequired,
          /**
           * Returns a <small> HTML element
           */
          small: PropTypes.func.isRequired,
          /**
           * Split a string into substrings using the specified separator and return them as an array.
           * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
           * @param limit A value used to limit the number of elements returned in the array.
           */
          split: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * position. Otherwise returns false.
           */
          startsWith: PropTypes.func.isRequired,
          /**
           * Returns a <strike> HTML element
           */
          strike: PropTypes.func.isRequired,
          /**
           * Returns a <sub> HTML element
           */
          sub: PropTypes.func.isRequired,
          /**
           * Gets a substring beginning at the specified location and having the specified length.
           * @param from The starting position of the desired substring. The index of the first character in the string is zero.
           * @param length The number of characters to include in the returned substring.
           */
          substr: PropTypes.func.isRequired,
          /**
           * Returns the substring at the specified location within a String object.
           * @param start The zero-based index number indicating the beginning of the substring.
           * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
           * If end is omitted, the characters from start through the end of the original string are returned.
           */
          substring: PropTypes.func.isRequired,
          /**
           * Returns a <sup> HTML element
           */
          sup: PropTypes.func.isRequired,
          /**
           * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
           */
          toLocaleLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
           */
          toLocaleUpperCase: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to lowercase.
           */
          toLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string representation of a string.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to uppercase.
           */
          toUpperCase: PropTypes.func.isRequired,
          /**
           * Removes the leading and trailing white space and line terminator characters from a string.
           */
          trim: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimEnd: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimLeft: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimRight: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimStart: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
      ])
    ),
    PropTypes.object,
    PropTypes.shape({
      /**
       * Iterator
       */
      "__@iterator": PropTypes.func.isRequired,
      /**
       * Returns an <a> HTML anchor element and sets the name attribute to the text value
       * @param name
       */
      anchor: PropTypes.func.isRequired,
      /**
       * Returns a <big> HTML element
       */
      big: PropTypes.func.isRequired,
      /**
       * Returns a <blink> HTML element
       */
      blink: PropTypes.func.isRequired,
      /**
       * Returns a <b> HTML element
       */
      bold: PropTypes.func.isRequired,
      /**
       * Returns the character at the specified index.
       * @param pos The zero-based index of the desired character.
       */
      charAt: PropTypes.func.isRequired,
      /**
       * Returns the Unicode value of the character at the specified location.
       * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
       */
      charCodeAt: PropTypes.func.isRequired,
      /**
       * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
       * value of the UTF-16 encoded code point starting at the string element at position pos in
       * the String resulting from converting this object to a String.
       * If there is no element at that position, the result is undefined.
       * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
       */
      codePointAt: PropTypes.func.isRequired,
      /**
       * Returns a string that contains the concatenation of two or more strings.
       * @param strings The strings to append to the end of the string.
       */
      concat: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * endPosition – length(this). Otherwise returns false.
       */
      endsWith: PropTypes.func.isRequired,
      /**
       * Returns a <tt> HTML element
       */
      fixed: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the color attribute value
       */
      fontcolor: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the size attribute value
       */
      fontsize: PropTypes.func.isRequired,
      /**
       * Returns true if searchString appears as a substring of the result of converting this
       * object to a String, at one or more positions that are
       * greater than or equal to position; otherwise, returns false.
       * @param searchString search string
       * @param position If position is undefined, 0 is assumed, so as to search all of the String.
       */
      includes: PropTypes.func.isRequired,
      /**
       * Returns the position of the first occurrence of a substring.
       * @param searchString The substring to search for in the string
       * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
       */
      indexOf: PropTypes.func.isRequired,
      /**
       * Returns an <i> HTML element
       */
      italics: PropTypes.func.isRequired,
      /**
       * Returns the last occurrence of a substring in the string.
       * @param searchString The substring to search for.
       * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
       */
      lastIndexOf: PropTypes.func.isRequired,
      /**
       * Returns the length of a String object.
       */
      length: PropTypes.number.isRequired,
      /**
       * Returns an <a> HTML element and sets the href attribute value
       */
      link: PropTypes.func.isRequired,
      /**
       * Determines whether two strings are equivalent in the current locale.
       * @param that String to compare to target string
       */
      localeCompare: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an array containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      match: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an iterable of matches
       * containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      matchAll: PropTypes.func.isRequired,
      /**
       * Returns the String value result of normalizing the string into the normalization form
       * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
       * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
       * is "NFC"
       */
      normalize: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the end (right) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padEnd: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the start (left) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padStart: PropTypes.func.isRequired,
      /**
       * Returns a String value that is made from count copies appended together. If count is 0,
       * the empty string is returned.
       * @param count number of copies to append
       */
      repeat: PropTypes.func.isRequired,
      /**
       * Replaces text in a string, using a regular expression or search string.
       * @param searchValue A string to search for.
       * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
       */
      replace: PropTypes.func.isRequired,
      /**
       * Finds the first substring match in a regular expression search.
       * @param regexp The regular expression pattern and applicable flags.
       */
      search: PropTypes.func.isRequired,
      /**
       * Returns a section of a string.
       * @param start The index to the beginning of the specified portion of stringObj.
       * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
       * If this value is not specified, the substring continues to the end of stringObj.
       */
      slice: PropTypes.func.isRequired,
      /**
       * Returns a <small> HTML element
       */
      small: PropTypes.func.isRequired,
      /**
       * Split a string into substrings using the specified separator and return them as an array.
       * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
       * @param limit A value used to limit the number of elements returned in the array.
       */
      split: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * position. Otherwise returns false.
       */
      startsWith: PropTypes.func.isRequired,
      /**
       * Returns a <strike> HTML element
       */
      strike: PropTypes.func.isRequired,
      /**
       * Returns a <sub> HTML element
       */
      sub: PropTypes.func.isRequired,
      /**
       * Gets a substring beginning at the specified location and having the specified length.
       * @param from The starting position of the desired substring. The index of the first character in the string is zero.
       * @param length The number of characters to include in the returned substring.
       */
      substr: PropTypes.func.isRequired,
      /**
       * Returns the substring at the specified location within a String object.
       * @param start The zero-based index number indicating the beginning of the substring.
       * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
       * If end is omitted, the characters from start through the end of the original string are returned.
       */
      substring: PropTypes.func.isRequired,
      /**
       * Returns a <sup> HTML element
       */
      sup: PropTypes.func.isRequired,
      /**
       * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
       */
      toLocaleLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
       */
      toLocaleUpperCase: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to lowercase.
       */
      toLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string representation of a string.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to uppercase.
       */
      toUpperCase: PropTypes.func.isRequired,
      /**
       * Removes the leading and trailing white space and line terminator characters from a string.
       */
      trim: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimEnd: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimLeft: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimRight: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimStart: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
  ]),
  /**
   * The align-self CSS property aligns flex items of the current flex line overriding the align-items value.
   *
   * If any of the item's cross-axis margin is set to auto, then align-self is ignored. In Grid layout align-self
   * aligns the item inside the grid area.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/align-self)
   */
  alignSelf: PropTypes.oneOfType([
    PropTypes.oneOf([
      "-moz-initial",
      "auto",
      "baseline",
      "center",
      "end",
      "flex-end",
      "flex-start",
      "inherit",
      "initial",
      "normal",
      "revert",
      "self-end",
      "self-start",
      "start",
      "stretch",
      "unset",
    ]),
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([
          "-moz-initial",
          "auto",
          "baseline",
          "center",
          "end",
          "flex-end",
          "flex-start",
          "inherit",
          "initial",
          "normal",
          "revert",
          "self-end",
          "self-start",
          "start",
          "stretch",
          "unset",
          null,
        ]),
        PropTypes.shape({
          /**
           * Iterator
           */
          "__@iterator": PropTypes.func.isRequired,
          /**
           * Returns an <a> HTML anchor element and sets the name attribute to the text value
           * @param name
           */
          anchor: PropTypes.func.isRequired,
          /**
           * Returns a <big> HTML element
           */
          big: PropTypes.func.isRequired,
          /**
           * Returns a <blink> HTML element
           */
          blink: PropTypes.func.isRequired,
          /**
           * Returns a <b> HTML element
           */
          bold: PropTypes.func.isRequired,
          /**
           * Returns the character at the specified index.
           * @param pos The zero-based index of the desired character.
           */
          charAt: PropTypes.func.isRequired,
          /**
           * Returns the Unicode value of the character at the specified location.
           * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
           */
          charCodeAt: PropTypes.func.isRequired,
          /**
           * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
           * value of the UTF-16 encoded code point starting at the string element at position pos in
           * the String resulting from converting this object to a String.
           * If there is no element at that position, the result is undefined.
           * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
           */
          codePointAt: PropTypes.func.isRequired,
          /**
           * Returns a string that contains the concatenation of two or more strings.
           * @param strings The strings to append to the end of the string.
           */
          concat: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * endPosition – length(this). Otherwise returns false.
           */
          endsWith: PropTypes.func.isRequired,
          /**
           * Returns a <tt> HTML element
           */
          fixed: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the color attribute value
           */
          fontcolor: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the size attribute value
           */
          fontsize: PropTypes.func.isRequired,
          /**
           * Returns true if searchString appears as a substring of the result of converting this
           * object to a String, at one or more positions that are
           * greater than or equal to position; otherwise, returns false.
           * @param searchString search string
           * @param position If position is undefined, 0 is assumed, so as to search all of the String.
           */
          includes: PropTypes.func.isRequired,
          /**
           * Returns the position of the first occurrence of a substring.
           * @param searchString The substring to search for in the string
           * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
           */
          indexOf: PropTypes.func.isRequired,
          /**
           * Returns an <i> HTML element
           */
          italics: PropTypes.func.isRequired,
          /**
           * Returns the last occurrence of a substring in the string.
           * @param searchString The substring to search for.
           * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
           */
          lastIndexOf: PropTypes.func.isRequired,
          /**
           * Returns the length of a String object.
           */
          length: PropTypes.number.isRequired,
          /**
           * Returns an <a> HTML element and sets the href attribute value
           */
          link: PropTypes.func.isRequired,
          /**
           * Determines whether two strings are equivalent in the current locale.
           * @param that String to compare to target string
           */
          localeCompare: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an array containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          match: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an iterable of matches
           * containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          matchAll: PropTypes.func.isRequired,
          /**
           * Returns the String value result of normalizing the string into the normalization form
           * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
           * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
           * is "NFC"
           */
          normalize: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the end (right) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padEnd: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the start (left) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padStart: PropTypes.func.isRequired,
          /**
           * Returns a String value that is made from count copies appended together. If count is 0,
           * the empty string is returned.
           * @param count number of copies to append
           */
          repeat: PropTypes.func.isRequired,
          /**
           * Replaces text in a string, using a regular expression or search string.
           * @param searchValue A string to search for.
           * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
           */
          replace: PropTypes.func.isRequired,
          /**
           * Finds the first substring match in a regular expression search.
           * @param regexp The regular expression pattern and applicable flags.
           */
          search: PropTypes.func.isRequired,
          /**
           * Returns a section of a string.
           * @param start The index to the beginning of the specified portion of stringObj.
           * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
           * If this value is not specified, the substring continues to the end of stringObj.
           */
          slice: PropTypes.func.isRequired,
          /**
           * Returns a <small> HTML element
           */
          small: PropTypes.func.isRequired,
          /**
           * Split a string into substrings using the specified separator and return them as an array.
           * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
           * @param limit A value used to limit the number of elements returned in the array.
           */
          split: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * position. Otherwise returns false.
           */
          startsWith: PropTypes.func.isRequired,
          /**
           * Returns a <strike> HTML element
           */
          strike: PropTypes.func.isRequired,
          /**
           * Returns a <sub> HTML element
           */
          sub: PropTypes.func.isRequired,
          /**
           * Gets a substring beginning at the specified location and having the specified length.
           * @param from The starting position of the desired substring. The index of the first character in the string is zero.
           * @param length The number of characters to include in the returned substring.
           */
          substr: PropTypes.func.isRequired,
          /**
           * Returns the substring at the specified location within a String object.
           * @param start The zero-based index number indicating the beginning of the substring.
           * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
           * If end is omitted, the characters from start through the end of the original string are returned.
           */
          substring: PropTypes.func.isRequired,
          /**
           * Returns a <sup> HTML element
           */
          sup: PropTypes.func.isRequired,
          /**
           * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
           */
          toLocaleLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
           */
          toLocaleUpperCase: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to lowercase.
           */
          toLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string representation of a string.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to uppercase.
           */
          toUpperCase: PropTypes.func.isRequired,
          /**
           * Removes the leading and trailing white space and line terminator characters from a string.
           */
          trim: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimEnd: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimLeft: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimRight: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimStart: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
      ])
    ),
    PropTypes.object,
    PropTypes.shape({
      /**
       * Iterator
       */
      "__@iterator": PropTypes.func.isRequired,
      /**
       * Returns an <a> HTML anchor element and sets the name attribute to the text value
       * @param name
       */
      anchor: PropTypes.func.isRequired,
      /**
       * Returns a <big> HTML element
       */
      big: PropTypes.func.isRequired,
      /**
       * Returns a <blink> HTML element
       */
      blink: PropTypes.func.isRequired,
      /**
       * Returns a <b> HTML element
       */
      bold: PropTypes.func.isRequired,
      /**
       * Returns the character at the specified index.
       * @param pos The zero-based index of the desired character.
       */
      charAt: PropTypes.func.isRequired,
      /**
       * Returns the Unicode value of the character at the specified location.
       * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
       */
      charCodeAt: PropTypes.func.isRequired,
      /**
       * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
       * value of the UTF-16 encoded code point starting at the string element at position pos in
       * the String resulting from converting this object to a String.
       * If there is no element at that position, the result is undefined.
       * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
       */
      codePointAt: PropTypes.func.isRequired,
      /**
       * Returns a string that contains the concatenation of two or more strings.
       * @param strings The strings to append to the end of the string.
       */
      concat: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * endPosition – length(this). Otherwise returns false.
       */
      endsWith: PropTypes.func.isRequired,
      /**
       * Returns a <tt> HTML element
       */
      fixed: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the color attribute value
       */
      fontcolor: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the size attribute value
       */
      fontsize: PropTypes.func.isRequired,
      /**
       * Returns true if searchString appears as a substring of the result of converting this
       * object to a String, at one or more positions that are
       * greater than or equal to position; otherwise, returns false.
       * @param searchString search string
       * @param position If position is undefined, 0 is assumed, so as to search all of the String.
       */
      includes: PropTypes.func.isRequired,
      /**
       * Returns the position of the first occurrence of a substring.
       * @param searchString The substring to search for in the string
       * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
       */
      indexOf: PropTypes.func.isRequired,
      /**
       * Returns an <i> HTML element
       */
      italics: PropTypes.func.isRequired,
      /**
       * Returns the last occurrence of a substring in the string.
       * @param searchString The substring to search for.
       * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
       */
      lastIndexOf: PropTypes.func.isRequired,
      /**
       * Returns the length of a String object.
       */
      length: PropTypes.number.isRequired,
      /**
       * Returns an <a> HTML element and sets the href attribute value
       */
      link: PropTypes.func.isRequired,
      /**
       * Determines whether two strings are equivalent in the current locale.
       * @param that String to compare to target string
       */
      localeCompare: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an array containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      match: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an iterable of matches
       * containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      matchAll: PropTypes.func.isRequired,
      /**
       * Returns the String value result of normalizing the string into the normalization form
       * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
       * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
       * is "NFC"
       */
      normalize: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the end (right) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padEnd: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the start (left) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padStart: PropTypes.func.isRequired,
      /**
       * Returns a String value that is made from count copies appended together. If count is 0,
       * the empty string is returned.
       * @param count number of copies to append
       */
      repeat: PropTypes.func.isRequired,
      /**
       * Replaces text in a string, using a regular expression or search string.
       * @param searchValue A string to search for.
       * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
       */
      replace: PropTypes.func.isRequired,
      /**
       * Finds the first substring match in a regular expression search.
       * @param regexp The regular expression pattern and applicable flags.
       */
      search: PropTypes.func.isRequired,
      /**
       * Returns a section of a string.
       * @param start The index to the beginning of the specified portion of stringObj.
       * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
       * If this value is not specified, the substring continues to the end of stringObj.
       */
      slice: PropTypes.func.isRequired,
      /**
       * Returns a <small> HTML element
       */
      small: PropTypes.func.isRequired,
      /**
       * Split a string into substrings using the specified separator and return them as an array.
       * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
       * @param limit A value used to limit the number of elements returned in the array.
       */
      split: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * position. Otherwise returns false.
       */
      startsWith: PropTypes.func.isRequired,
      /**
       * Returns a <strike> HTML element
       */
      strike: PropTypes.func.isRequired,
      /**
       * Returns a <sub> HTML element
       */
      sub: PropTypes.func.isRequired,
      /**
       * Gets a substring beginning at the specified location and having the specified length.
       * @param from The starting position of the desired substring. The index of the first character in the string is zero.
       * @param length The number of characters to include in the returned substring.
       */
      substr: PropTypes.func.isRequired,
      /**
       * Returns the substring at the specified location within a String object.
       * @param start The zero-based index number indicating the beginning of the substring.
       * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
       * If end is omitted, the characters from start through the end of the original string are returned.
       */
      substring: PropTypes.func.isRequired,
      /**
       * Returns a <sup> HTML element
       */
      sup: PropTypes.func.isRequired,
      /**
       * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
       */
      toLocaleLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
       */
      toLocaleUpperCase: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to lowercase.
       */
      toLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string representation of a string.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to uppercase.
       */
      toUpperCase: PropTypes.func.isRequired,
      /**
       * Removes the leading and trailing white space and line terminator characters from a string.
       */
      trim: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimEnd: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimLeft: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimRight: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimStart: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
  ]),
  ariaLabel: PropTypes.string,
  children: PropTypes.node,
  /**
   * The flex CSS property specifies how a flex item will grow or shrink so as to fit the space available in
   * its flex container. This is a shorthand property that sets flex-grow, flex-shrink, and flex-basis.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/flex)
   */
  flex: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          /**
           * Iterator
           */
          "__@iterator": PropTypes.func.isRequired,
          /**
           * Returns an <a> HTML anchor element and sets the name attribute to the text value
           * @param name
           */
          anchor: PropTypes.func.isRequired,
          /**
           * Returns a <big> HTML element
           */
          big: PropTypes.func.isRequired,
          /**
           * Returns a <blink> HTML element
           */
          blink: PropTypes.func.isRequired,
          /**
           * Returns a <b> HTML element
           */
          bold: PropTypes.func.isRequired,
          /**
           * Returns the character at the specified index.
           * @param pos The zero-based index of the desired character.
           */
          charAt: PropTypes.func.isRequired,
          /**
           * Returns the Unicode value of the character at the specified location.
           * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
           */
          charCodeAt: PropTypes.func.isRequired,
          /**
           * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
           * value of the UTF-16 encoded code point starting at the string element at position pos in
           * the String resulting from converting this object to a String.
           * If there is no element at that position, the result is undefined.
           * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
           */
          codePointAt: PropTypes.func.isRequired,
          /**
           * Returns a string that contains the concatenation of two or more strings.
           * @param strings The strings to append to the end of the string.
           */
          concat: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * endPosition – length(this). Otherwise returns false.
           */
          endsWith: PropTypes.func.isRequired,
          /**
           * Returns a <tt> HTML element
           */
          fixed: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the color attribute value
           */
          fontcolor: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the size attribute value
           */
          fontsize: PropTypes.func.isRequired,
          /**
           * Returns true if searchString appears as a substring of the result of converting this
           * object to a String, at one or more positions that are
           * greater than or equal to position; otherwise, returns false.
           * @param searchString search string
           * @param position If position is undefined, 0 is assumed, so as to search all of the String.
           */
          includes: PropTypes.func.isRequired,
          /**
           * Returns the position of the first occurrence of a substring.
           * @param searchString The substring to search for in the string
           * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
           */
          indexOf: PropTypes.func.isRequired,
          /**
           * Returns an <i> HTML element
           */
          italics: PropTypes.func.isRequired,
          /**
           * Returns the last occurrence of a substring in the string.
           * @param searchString The substring to search for.
           * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
           */
          lastIndexOf: PropTypes.func.isRequired,
          /**
           * Returns the length of a String object.
           */
          length: PropTypes.number.isRequired,
          /**
           * Returns an <a> HTML element and sets the href attribute value
           */
          link: PropTypes.func.isRequired,
          /**
           * Determines whether two strings are equivalent in the current locale.
           * @param that String to compare to target string
           */
          localeCompare: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an array containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          match: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an iterable of matches
           * containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          matchAll: PropTypes.func.isRequired,
          /**
           * Returns the String value result of normalizing the string into the normalization form
           * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
           * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
           * is "NFC"
           */
          normalize: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the end (right) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padEnd: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the start (left) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padStart: PropTypes.func.isRequired,
          /**
           * Returns a String value that is made from count copies appended together. If count is 0,
           * the empty string is returned.
           * @param count number of copies to append
           */
          repeat: PropTypes.func.isRequired,
          /**
           * Replaces text in a string, using a regular expression or search string.
           * @param searchValue A string to search for.
           * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
           */
          replace: PropTypes.func.isRequired,
          /**
           * Finds the first substring match in a regular expression search.
           * @param regexp The regular expression pattern and applicable flags.
           */
          search: PropTypes.func.isRequired,
          /**
           * Returns a section of a string.
           * @param start The index to the beginning of the specified portion of stringObj.
           * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
           * If this value is not specified, the substring continues to the end of stringObj.
           */
          slice: PropTypes.func.isRequired,
          /**
           * Returns a <small> HTML element
           */
          small: PropTypes.func.isRequired,
          /**
           * Split a string into substrings using the specified separator and return them as an array.
           * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
           * @param limit A value used to limit the number of elements returned in the array.
           */
          split: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * position. Otherwise returns false.
           */
          startsWith: PropTypes.func.isRequired,
          /**
           * Returns a <strike> HTML element
           */
          strike: PropTypes.func.isRequired,
          /**
           * Returns a <sub> HTML element
           */
          sub: PropTypes.func.isRequired,
          /**
           * Gets a substring beginning at the specified location and having the specified length.
           * @param from The starting position of the desired substring. The index of the first character in the string is zero.
           * @param length The number of characters to include in the returned substring.
           */
          substr: PropTypes.func.isRequired,
          /**
           * Returns the substring at the specified location within a String object.
           * @param start The zero-based index number indicating the beginning of the substring.
           * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
           * If end is omitted, the characters from start through the end of the original string are returned.
           */
          substring: PropTypes.func.isRequired,
          /**
           * Returns a <sup> HTML element
           */
          sup: PropTypes.func.isRequired,
          /**
           * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
           */
          toLocaleLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
           */
          toLocaleUpperCase: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to lowercase.
           */
          toLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string representation of a string.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to uppercase.
           */
          toUpperCase: PropTypes.func.isRequired,
          /**
           * Removes the leading and trailing white space and line terminator characters from a string.
           */
          trim: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimEnd: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimLeft: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimRight: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimStart: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      /**
       * Iterator
       */
      "__@iterator": PropTypes.func.isRequired,
      /**
       * Returns an <a> HTML anchor element and sets the name attribute to the text value
       * @param name
       */
      anchor: PropTypes.func.isRequired,
      /**
       * Returns a <big> HTML element
       */
      big: PropTypes.func.isRequired,
      /**
       * Returns a <blink> HTML element
       */
      blink: PropTypes.func.isRequired,
      /**
       * Returns a <b> HTML element
       */
      bold: PropTypes.func.isRequired,
      /**
       * Returns the character at the specified index.
       * @param pos The zero-based index of the desired character.
       */
      charAt: PropTypes.func.isRequired,
      /**
       * Returns the Unicode value of the character at the specified location.
       * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
       */
      charCodeAt: PropTypes.func.isRequired,
      /**
       * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
       * value of the UTF-16 encoded code point starting at the string element at position pos in
       * the String resulting from converting this object to a String.
       * If there is no element at that position, the result is undefined.
       * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
       */
      codePointAt: PropTypes.func.isRequired,
      /**
       * Returns a string that contains the concatenation of two or more strings.
       * @param strings The strings to append to the end of the string.
       */
      concat: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * endPosition – length(this). Otherwise returns false.
       */
      endsWith: PropTypes.func.isRequired,
      /**
       * Returns a <tt> HTML element
       */
      fixed: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the color attribute value
       */
      fontcolor: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the size attribute value
       */
      fontsize: PropTypes.func.isRequired,
      /**
       * Returns true if searchString appears as a substring of the result of converting this
       * object to a String, at one or more positions that are
       * greater than or equal to position; otherwise, returns false.
       * @param searchString search string
       * @param position If position is undefined, 0 is assumed, so as to search all of the String.
       */
      includes: PropTypes.func.isRequired,
      /**
       * Returns the position of the first occurrence of a substring.
       * @param searchString The substring to search for in the string
       * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
       */
      indexOf: PropTypes.func.isRequired,
      /**
       * Returns an <i> HTML element
       */
      italics: PropTypes.func.isRequired,
      /**
       * Returns the last occurrence of a substring in the string.
       * @param searchString The substring to search for.
       * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
       */
      lastIndexOf: PropTypes.func.isRequired,
      /**
       * Returns the length of a String object.
       */
      length: PropTypes.number.isRequired,
      /**
       * Returns an <a> HTML element and sets the href attribute value
       */
      link: PropTypes.func.isRequired,
      /**
       * Determines whether two strings are equivalent in the current locale.
       * @param that String to compare to target string
       */
      localeCompare: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an array containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      match: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an iterable of matches
       * containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      matchAll: PropTypes.func.isRequired,
      /**
       * Returns the String value result of normalizing the string into the normalization form
       * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
       * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
       * is "NFC"
       */
      normalize: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the end (right) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padEnd: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the start (left) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padStart: PropTypes.func.isRequired,
      /**
       * Returns a String value that is made from count copies appended together. If count is 0,
       * the empty string is returned.
       * @param count number of copies to append
       */
      repeat: PropTypes.func.isRequired,
      /**
       * Replaces text in a string, using a regular expression or search string.
       * @param searchValue A string to search for.
       * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
       */
      replace: PropTypes.func.isRequired,
      /**
       * Finds the first substring match in a regular expression search.
       * @param regexp The regular expression pattern and applicable flags.
       */
      search: PropTypes.func.isRequired,
      /**
       * Returns a section of a string.
       * @param start The index to the beginning of the specified portion of stringObj.
       * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
       * If this value is not specified, the substring continues to the end of stringObj.
       */
      slice: PropTypes.func.isRequired,
      /**
       * Returns a <small> HTML element
       */
      small: PropTypes.func.isRequired,
      /**
       * Split a string into substrings using the specified separator and return them as an array.
       * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
       * @param limit A value used to limit the number of elements returned in the array.
       */
      split: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * position. Otherwise returns false.
       */
      startsWith: PropTypes.func.isRequired,
      /**
       * Returns a <strike> HTML element
       */
      strike: PropTypes.func.isRequired,
      /**
       * Returns a <sub> HTML element
       */
      sub: PropTypes.func.isRequired,
      /**
       * Gets a substring beginning at the specified location and having the specified length.
       * @param from The starting position of the desired substring. The index of the first character in the string is zero.
       * @param length The number of characters to include in the returned substring.
       */
      substr: PropTypes.func.isRequired,
      /**
       * Returns the substring at the specified location within a String object.
       * @param start The zero-based index number indicating the beginning of the substring.
       * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
       * If end is omitted, the characters from start through the end of the original string are returned.
       */
      substring: PropTypes.func.isRequired,
      /**
       * Returns a <sup> HTML element
       */
      sup: PropTypes.func.isRequired,
      /**
       * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
       */
      toLocaleLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
       */
      toLocaleUpperCase: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to lowercase.
       */
      toLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string representation of a string.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to uppercase.
       */
      toUpperCase: PropTypes.func.isRequired,
      /**
       * Removes the leading and trailing white space and line terminator characters from a string.
       */
      trim: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimEnd: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimLeft: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimRight: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimStart: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  flexBasis: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          /**
           * Iterator
           */
          "__@iterator": PropTypes.func.isRequired,
          /**
           * Returns an <a> HTML anchor element and sets the name attribute to the text value
           * @param name
           */
          anchor: PropTypes.func.isRequired,
          /**
           * Returns a <big> HTML element
           */
          big: PropTypes.func.isRequired,
          /**
           * Returns a <blink> HTML element
           */
          blink: PropTypes.func.isRequired,
          /**
           * Returns a <b> HTML element
           */
          bold: PropTypes.func.isRequired,
          /**
           * Returns the character at the specified index.
           * @param pos The zero-based index of the desired character.
           */
          charAt: PropTypes.func.isRequired,
          /**
           * Returns the Unicode value of the character at the specified location.
           * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
           */
          charCodeAt: PropTypes.func.isRequired,
          /**
           * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
           * value of the UTF-16 encoded code point starting at the string element at position pos in
           * the String resulting from converting this object to a String.
           * If there is no element at that position, the result is undefined.
           * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
           */
          codePointAt: PropTypes.func.isRequired,
          /**
           * Returns a string that contains the concatenation of two or more strings.
           * @param strings The strings to append to the end of the string.
           */
          concat: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * endPosition – length(this). Otherwise returns false.
           */
          endsWith: PropTypes.func.isRequired,
          /**
           * Returns a <tt> HTML element
           */
          fixed: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the color attribute value
           */
          fontcolor: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the size attribute value
           */
          fontsize: PropTypes.func.isRequired,
          /**
           * Returns true if searchString appears as a substring of the result of converting this
           * object to a String, at one or more positions that are
           * greater than or equal to position; otherwise, returns false.
           * @param searchString search string
           * @param position If position is undefined, 0 is assumed, so as to search all of the String.
           */
          includes: PropTypes.func.isRequired,
          /**
           * Returns the position of the first occurrence of a substring.
           * @param searchString The substring to search for in the string
           * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
           */
          indexOf: PropTypes.func.isRequired,
          /**
           * Returns an <i> HTML element
           */
          italics: PropTypes.func.isRequired,
          /**
           * Returns the last occurrence of a substring in the string.
           * @param searchString The substring to search for.
           * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
           */
          lastIndexOf: PropTypes.func.isRequired,
          /**
           * Returns the length of a String object.
           */
          length: PropTypes.number.isRequired,
          /**
           * Returns an <a> HTML element and sets the href attribute value
           */
          link: PropTypes.func.isRequired,
          /**
           * Determines whether two strings are equivalent in the current locale.
           * @param that String to compare to target string
           */
          localeCompare: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an array containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          match: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an iterable of matches
           * containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          matchAll: PropTypes.func.isRequired,
          /**
           * Returns the String value result of normalizing the string into the normalization form
           * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
           * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
           * is "NFC"
           */
          normalize: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the end (right) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padEnd: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the start (left) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padStart: PropTypes.func.isRequired,
          /**
           * Returns a String value that is made from count copies appended together. If count is 0,
           * the empty string is returned.
           * @param count number of copies to append
           */
          repeat: PropTypes.func.isRequired,
          /**
           * Replaces text in a string, using a regular expression or search string.
           * @param searchValue A string to search for.
           * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
           */
          replace: PropTypes.func.isRequired,
          /**
           * Finds the first substring match in a regular expression search.
           * @param regexp The regular expression pattern and applicable flags.
           */
          search: PropTypes.func.isRequired,
          /**
           * Returns a section of a string.
           * @param start The index to the beginning of the specified portion of stringObj.
           * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
           * If this value is not specified, the substring continues to the end of stringObj.
           */
          slice: PropTypes.func.isRequired,
          /**
           * Returns a <small> HTML element
           */
          small: PropTypes.func.isRequired,
          /**
           * Split a string into substrings using the specified separator and return them as an array.
           * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
           * @param limit A value used to limit the number of elements returned in the array.
           */
          split: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * position. Otherwise returns false.
           */
          startsWith: PropTypes.func.isRequired,
          /**
           * Returns a <strike> HTML element
           */
          strike: PropTypes.func.isRequired,
          /**
           * Returns a <sub> HTML element
           */
          sub: PropTypes.func.isRequired,
          /**
           * Gets a substring beginning at the specified location and having the specified length.
           * @param from The starting position of the desired substring. The index of the first character in the string is zero.
           * @param length The number of characters to include in the returned substring.
           */
          substr: PropTypes.func.isRequired,
          /**
           * Returns the substring at the specified location within a String object.
           * @param start The zero-based index number indicating the beginning of the substring.
           * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
           * If end is omitted, the characters from start through the end of the original string are returned.
           */
          substring: PropTypes.func.isRequired,
          /**
           * Returns a <sup> HTML element
           */
          sup: PropTypes.func.isRequired,
          /**
           * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
           */
          toLocaleLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
           */
          toLocaleUpperCase: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to lowercase.
           */
          toLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string representation of a string.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to uppercase.
           */
          toUpperCase: PropTypes.func.isRequired,
          /**
           * Removes the leading and trailing white space and line terminator characters from a string.
           */
          trim: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimEnd: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimLeft: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimRight: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimStart: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      /**
       * Iterator
       */
      "__@iterator": PropTypes.func.isRequired,
      /**
       * Returns an <a> HTML anchor element and sets the name attribute to the text value
       * @param name
       */
      anchor: PropTypes.func.isRequired,
      /**
       * Returns a <big> HTML element
       */
      big: PropTypes.func.isRequired,
      /**
       * Returns a <blink> HTML element
       */
      blink: PropTypes.func.isRequired,
      /**
       * Returns a <b> HTML element
       */
      bold: PropTypes.func.isRequired,
      /**
       * Returns the character at the specified index.
       * @param pos The zero-based index of the desired character.
       */
      charAt: PropTypes.func.isRequired,
      /**
       * Returns the Unicode value of the character at the specified location.
       * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
       */
      charCodeAt: PropTypes.func.isRequired,
      /**
       * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
       * value of the UTF-16 encoded code point starting at the string element at position pos in
       * the String resulting from converting this object to a String.
       * If there is no element at that position, the result is undefined.
       * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
       */
      codePointAt: PropTypes.func.isRequired,
      /**
       * Returns a string that contains the concatenation of two or more strings.
       * @param strings The strings to append to the end of the string.
       */
      concat: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * endPosition – length(this). Otherwise returns false.
       */
      endsWith: PropTypes.func.isRequired,
      /**
       * Returns a <tt> HTML element
       */
      fixed: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the color attribute value
       */
      fontcolor: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the size attribute value
       */
      fontsize: PropTypes.func.isRequired,
      /**
       * Returns true if searchString appears as a substring of the result of converting this
       * object to a String, at one or more positions that are
       * greater than or equal to position; otherwise, returns false.
       * @param searchString search string
       * @param position If position is undefined, 0 is assumed, so as to search all of the String.
       */
      includes: PropTypes.func.isRequired,
      /**
       * Returns the position of the first occurrence of a substring.
       * @param searchString The substring to search for in the string
       * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
       */
      indexOf: PropTypes.func.isRequired,
      /**
       * Returns an <i> HTML element
       */
      italics: PropTypes.func.isRequired,
      /**
       * Returns the last occurrence of a substring in the string.
       * @param searchString The substring to search for.
       * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
       */
      lastIndexOf: PropTypes.func.isRequired,
      /**
       * Returns the length of a String object.
       */
      length: PropTypes.number.isRequired,
      /**
       * Returns an <a> HTML element and sets the href attribute value
       */
      link: PropTypes.func.isRequired,
      /**
       * Determines whether two strings are equivalent in the current locale.
       * @param that String to compare to target string
       */
      localeCompare: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an array containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      match: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an iterable of matches
       * containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      matchAll: PropTypes.func.isRequired,
      /**
       * Returns the String value result of normalizing the string into the normalization form
       * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
       * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
       * is "NFC"
       */
      normalize: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the end (right) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padEnd: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the start (left) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padStart: PropTypes.func.isRequired,
      /**
       * Returns a String value that is made from count copies appended together. If count is 0,
       * the empty string is returned.
       * @param count number of copies to append
       */
      repeat: PropTypes.func.isRequired,
      /**
       * Replaces text in a string, using a regular expression or search string.
       * @param searchValue A string to search for.
       * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
       */
      replace: PropTypes.func.isRequired,
      /**
       * Finds the first substring match in a regular expression search.
       * @param regexp The regular expression pattern and applicable flags.
       */
      search: PropTypes.func.isRequired,
      /**
       * Returns a section of a string.
       * @param start The index to the beginning of the specified portion of stringObj.
       * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
       * If this value is not specified, the substring continues to the end of stringObj.
       */
      slice: PropTypes.func.isRequired,
      /**
       * Returns a <small> HTML element
       */
      small: PropTypes.func.isRequired,
      /**
       * Split a string into substrings using the specified separator and return them as an array.
       * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
       * @param limit A value used to limit the number of elements returned in the array.
       */
      split: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * position. Otherwise returns false.
       */
      startsWith: PropTypes.func.isRequired,
      /**
       * Returns a <strike> HTML element
       */
      strike: PropTypes.func.isRequired,
      /**
       * Returns a <sub> HTML element
       */
      sub: PropTypes.func.isRequired,
      /**
       * Gets a substring beginning at the specified location and having the specified length.
       * @param from The starting position of the desired substring. The index of the first character in the string is zero.
       * @param length The number of characters to include in the returned substring.
       */
      substr: PropTypes.func.isRequired,
      /**
       * Returns the substring at the specified location within a String object.
       * @param start The zero-based index number indicating the beginning of the substring.
       * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
       * If end is omitted, the characters from start through the end of the original string are returned.
       */
      substring: PropTypes.func.isRequired,
      /**
       * Returns a <sup> HTML element
       */
      sup: PropTypes.func.isRequired,
      /**
       * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
       */
      toLocaleLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
       */
      toLocaleUpperCase: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to lowercase.
       */
      toLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string representation of a string.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to uppercase.
       */
      toUpperCase: PropTypes.func.isRequired,
      /**
       * Removes the leading and trailing white space and line terminator characters from a string.
       */
      trim: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimEnd: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimLeft: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimRight: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimStart: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  /**
   * The flex-direction CSS property specifies how flex items are placed in the flex container defining the main
   * axis and the direction (normal or reversed).
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/flex-direction)
   */
  flexDirection: PropTypes.oneOfType([
    PropTypes.oneOf([
      "-moz-initial",
      "column-reverse",
      "column",
      "inherit",
      "initial",
      "revert",
      "row-reverse",
      "row",
      "unset",
    ]),
    PropTypes.arrayOf(
      PropTypes.oneOf([
        "-moz-initial",
        "column-reverse",
        "column",
        "inherit",
        "initial",
        "revert",
        "row-reverse",
        "row",
        "unset",
        null,
      ])
    ),
    PropTypes.object,
  ]),
  /**
   * The flex-grow CSS property sets the flex grow factor of a flex item main size. It specifies how much of the
   * remaining space in the flex container should be assigned to the item (the flex grow factor).
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/flex-grow)
   */
  flexGrow: PropTypes.oneOfType([
    PropTypes.oneOf(["-moz-initial", "inherit", "initial", "revert", "unset"]),
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([
          "-moz-initial",
          "inherit",
          "initial",
          "revert",
          "unset",
          null,
        ]),
        PropTypes.shape({
          /**
           * Returns a string containing a number represented in exponential notation.
           * @param fractionDigits Number of digits after the decimal point. Must be in the range 0 - 20, inclusive.
           */
          toExponential: PropTypes.func.isRequired,
          /**
           * Returns a string representing a number in fixed-point notation.
           * @param fractionDigits Number of digits after the decimal point. Must be in the range 0 - 20, inclusive.
           */
          toFixed: PropTypes.func.isRequired,
          /**
           * Converts a number to a string by using the current or specified locale.
           * @param locales A locale string or array of locale strings that contain one or more language or locale tags. If you include more than one locale string, list them in descending order of priority so that the first entry is the preferred locale. If you omit this parameter, the default locale of the JavaScript runtime is used.
           * @param options An object that contains one or more properties that specify comparison options.
           */
          toLocaleString: PropTypes.func.isRequired,
          /**
           * Returns a string containing a number represented either in exponential or fixed-point notation with a specified number of digits.
           * @param precision Number of significant digits. Must be in the range 1 - 21, inclusive.
           */
          toPrecision: PropTypes.func.isRequired,
          /**
           * Returns a string representation of an object.
           * @param radix Specifies a radix for converting numeric values to strings. This value is only used for numbers.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
      ])
    ),
    PropTypes.object,
    PropTypes.shape({
      /**
       * Returns a string containing a number represented in exponential notation.
       * @param fractionDigits Number of digits after the decimal point. Must be in the range 0 - 20, inclusive.
       */
      toExponential: PropTypes.func.isRequired,
      /**
       * Returns a string representing a number in fixed-point notation.
       * @param fractionDigits Number of digits after the decimal point. Must be in the range 0 - 20, inclusive.
       */
      toFixed: PropTypes.func.isRequired,
      /**
       * Converts a number to a string by using the current or specified locale.
       * @param locales A locale string or array of locale strings that contain one or more language or locale tags. If you include more than one locale string, list them in descending order of priority so that the first entry is the preferred locale. If you omit this parameter, the default locale of the JavaScript runtime is used.
       * @param options An object that contains one or more properties that specify comparison options.
       */
      toLocaleString: PropTypes.func.isRequired,
      /**
       * Returns a string containing a number represented either in exponential or fixed-point notation with a specified number of digits.
       * @param precision Number of significant digits. Must be in the range 1 - 21, inclusive.
       */
      toPrecision: PropTypes.func.isRequired,
      /**
       * Returns a string representation of an object.
       * @param radix Specifies a radix for converting numeric values to strings. This value is only used for numbers.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
  ]),
  /**
   * The flex-shrink CSS property sets the flex shrink factor of a flex item. If the size of all flex items is larger
   * than the flex container, items shrink to fit according to flex-shrink.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/flex-shrink)
   */
  flexShrink: PropTypes.oneOfType([
    PropTypes.oneOf(["-moz-initial", "inherit", "initial", "revert", "unset"]),
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([
          "-moz-initial",
          "inherit",
          "initial",
          "revert",
          "unset",
          null,
        ]),
        PropTypes.shape({
          /**
           * Returns a string containing a number represented in exponential notation.
           * @param fractionDigits Number of digits after the decimal point. Must be in the range 0 - 20, inclusive.
           */
          toExponential: PropTypes.func.isRequired,
          /**
           * Returns a string representing a number in fixed-point notation.
           * @param fractionDigits Number of digits after the decimal point. Must be in the range 0 - 20, inclusive.
           */
          toFixed: PropTypes.func.isRequired,
          /**
           * Converts a number to a string by using the current or specified locale.
           * @param locales A locale string or array of locale strings that contain one or more language or locale tags. If you include more than one locale string, list them in descending order of priority so that the first entry is the preferred locale. If you omit this parameter, the default locale of the JavaScript runtime is used.
           * @param options An object that contains one or more properties that specify comparison options.
           */
          toLocaleString: PropTypes.func.isRequired,
          /**
           * Returns a string containing a number represented either in exponential or fixed-point notation with a specified number of digits.
           * @param precision Number of significant digits. Must be in the range 1 - 21, inclusive.
           */
          toPrecision: PropTypes.func.isRequired,
          /**
           * Returns a string representation of an object.
           * @param radix Specifies a radix for converting numeric values to strings. This value is only used for numbers.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
      ])
    ),
    PropTypes.object,
    PropTypes.shape({
      /**
       * Returns a string containing a number represented in exponential notation.
       * @param fractionDigits Number of digits after the decimal point. Must be in the range 0 - 20, inclusive.
       */
      toExponential: PropTypes.func.isRequired,
      /**
       * Returns a string representing a number in fixed-point notation.
       * @param fractionDigits Number of digits after the decimal point. Must be in the range 0 - 20, inclusive.
       */
      toFixed: PropTypes.func.isRequired,
      /**
       * Converts a number to a string by using the current or specified locale.
       * @param locales A locale string or array of locale strings that contain one or more language or locale tags. If you include more than one locale string, list them in descending order of priority so that the first entry is the preferred locale. If you omit this parameter, the default locale of the JavaScript runtime is used.
       * @param options An object that contains one or more properties that specify comparison options.
       */
      toLocaleString: PropTypes.func.isRequired,
      /**
       * Returns a string containing a number represented either in exponential or fixed-point notation with a specified number of digits.
       * @param precision Number of significant digits. Must be in the range 1 - 21, inclusive.
       */
      toPrecision: PropTypes.func.isRequired,
      /**
       * Returns a string representation of an object.
       * @param radix Specifies a radix for converting numeric values to strings. This value is only used for numbers.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
  ]),
  /**
   * The flex-wrap CSS property sets whether flex items are forced onto one line or can wrap onto multiple lines.
   * If wrapping is allowed, it sets the direction that lines are stacked.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/flex-wrap)
   */
  flexWrap: PropTypes.oneOfType([
    PropTypes.oneOf([
      "-moz-initial",
      "inherit",
      "initial",
      "nowrap",
      "revert",
      "unset",
      "wrap-reverse",
      "wrap",
    ]),
    PropTypes.arrayOf(
      PropTypes.oneOf([
        "-moz-initial",
        "inherit",
        "initial",
        "nowrap",
        "revert",
        "unset",
        "wrap-reverse",
        "wrap",
        null,
      ])
    ),
    PropTypes.object,
  ]),
  /**
   * If 'true' the children will not be visible
   */
  isLoading: PropTypes.bool,
  /**
   * The CSS justify-content property defines how the browser distributes space between and around content items
   * along the main-axis of a flex container, and the inline axis of a grid container.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/justify-content)
   */
  justifyContent: PropTypes.oneOfType([
    PropTypes.oneOf([
      "-moz-initial",
      "center",
      "end",
      "flex-end",
      "flex-start",
      "inherit",
      "initial",
      "left",
      "normal",
      "revert",
      "right",
      "space-around",
      "space-between",
      "space-evenly",
      "start",
      "stretch",
      "unset",
    ]),
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([
          "-moz-initial",
          "center",
          "end",
          "flex-end",
          "flex-start",
          "inherit",
          "initial",
          "left",
          "normal",
          "revert",
          "right",
          "space-around",
          "space-between",
          "space-evenly",
          "start",
          "stretch",
          "unset",
          null,
        ]),
        PropTypes.shape({
          /**
           * Iterator
           */
          "__@iterator": PropTypes.func.isRequired,
          /**
           * Returns an <a> HTML anchor element and sets the name attribute to the text value
           * @param name
           */
          anchor: PropTypes.func.isRequired,
          /**
           * Returns a <big> HTML element
           */
          big: PropTypes.func.isRequired,
          /**
           * Returns a <blink> HTML element
           */
          blink: PropTypes.func.isRequired,
          /**
           * Returns a <b> HTML element
           */
          bold: PropTypes.func.isRequired,
          /**
           * Returns the character at the specified index.
           * @param pos The zero-based index of the desired character.
           */
          charAt: PropTypes.func.isRequired,
          /**
           * Returns the Unicode value of the character at the specified location.
           * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
           */
          charCodeAt: PropTypes.func.isRequired,
          /**
           * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
           * value of the UTF-16 encoded code point starting at the string element at position pos in
           * the String resulting from converting this object to a String.
           * If there is no element at that position, the result is undefined.
           * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
           */
          codePointAt: PropTypes.func.isRequired,
          /**
           * Returns a string that contains the concatenation of two or more strings.
           * @param strings The strings to append to the end of the string.
           */
          concat: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * endPosition – length(this). Otherwise returns false.
           */
          endsWith: PropTypes.func.isRequired,
          /**
           * Returns a <tt> HTML element
           */
          fixed: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the color attribute value
           */
          fontcolor: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the size attribute value
           */
          fontsize: PropTypes.func.isRequired,
          /**
           * Returns true if searchString appears as a substring of the result of converting this
           * object to a String, at one or more positions that are
           * greater than or equal to position; otherwise, returns false.
           * @param searchString search string
           * @param position If position is undefined, 0 is assumed, so as to search all of the String.
           */
          includes: PropTypes.func.isRequired,
          /**
           * Returns the position of the first occurrence of a substring.
           * @param searchString The substring to search for in the string
           * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
           */
          indexOf: PropTypes.func.isRequired,
          /**
           * Returns an <i> HTML element
           */
          italics: PropTypes.func.isRequired,
          /**
           * Returns the last occurrence of a substring in the string.
           * @param searchString The substring to search for.
           * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
           */
          lastIndexOf: PropTypes.func.isRequired,
          /**
           * Returns the length of a String object.
           */
          length: PropTypes.number.isRequired,
          /**
           * Returns an <a> HTML element and sets the href attribute value
           */
          link: PropTypes.func.isRequired,
          /**
           * Determines whether two strings are equivalent in the current locale.
           * @param that String to compare to target string
           */
          localeCompare: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an array containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          match: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an iterable of matches
           * containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          matchAll: PropTypes.func.isRequired,
          /**
           * Returns the String value result of normalizing the string into the normalization form
           * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
           * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
           * is "NFC"
           */
          normalize: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the end (right) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padEnd: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the start (left) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padStart: PropTypes.func.isRequired,
          /**
           * Returns a String value that is made from count copies appended together. If count is 0,
           * the empty string is returned.
           * @param count number of copies to append
           */
          repeat: PropTypes.func.isRequired,
          /**
           * Replaces text in a string, using a regular expression or search string.
           * @param searchValue A string to search for.
           * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
           */
          replace: PropTypes.func.isRequired,
          /**
           * Finds the first substring match in a regular expression search.
           * @param regexp The regular expression pattern and applicable flags.
           */
          search: PropTypes.func.isRequired,
          /**
           * Returns a section of a string.
           * @param start The index to the beginning of the specified portion of stringObj.
           * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
           * If this value is not specified, the substring continues to the end of stringObj.
           */
          slice: PropTypes.func.isRequired,
          /**
           * Returns a <small> HTML element
           */
          small: PropTypes.func.isRequired,
          /**
           * Split a string into substrings using the specified separator and return them as an array.
           * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
           * @param limit A value used to limit the number of elements returned in the array.
           */
          split: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * position. Otherwise returns false.
           */
          startsWith: PropTypes.func.isRequired,
          /**
           * Returns a <strike> HTML element
           */
          strike: PropTypes.func.isRequired,
          /**
           * Returns a <sub> HTML element
           */
          sub: PropTypes.func.isRequired,
          /**
           * Gets a substring beginning at the specified location and having the specified length.
           * @param from The starting position of the desired substring. The index of the first character in the string is zero.
           * @param length The number of characters to include in the returned substring.
           */
          substr: PropTypes.func.isRequired,
          /**
           * Returns the substring at the specified location within a String object.
           * @param start The zero-based index number indicating the beginning of the substring.
           * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
           * If end is omitted, the characters from start through the end of the original string are returned.
           */
          substring: PropTypes.func.isRequired,
          /**
           * Returns a <sup> HTML element
           */
          sup: PropTypes.func.isRequired,
          /**
           * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
           */
          toLocaleLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
           */
          toLocaleUpperCase: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to lowercase.
           */
          toLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string representation of a string.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to uppercase.
           */
          toUpperCase: PropTypes.func.isRequired,
          /**
           * Removes the leading and trailing white space and line terminator characters from a string.
           */
          trim: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimEnd: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimLeft: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimRight: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimStart: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
      ])
    ),
    PropTypes.object,
    PropTypes.shape({
      /**
       * Iterator
       */
      "__@iterator": PropTypes.func.isRequired,
      /**
       * Returns an <a> HTML anchor element and sets the name attribute to the text value
       * @param name
       */
      anchor: PropTypes.func.isRequired,
      /**
       * Returns a <big> HTML element
       */
      big: PropTypes.func.isRequired,
      /**
       * Returns a <blink> HTML element
       */
      blink: PropTypes.func.isRequired,
      /**
       * Returns a <b> HTML element
       */
      bold: PropTypes.func.isRequired,
      /**
       * Returns the character at the specified index.
       * @param pos The zero-based index of the desired character.
       */
      charAt: PropTypes.func.isRequired,
      /**
       * Returns the Unicode value of the character at the specified location.
       * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
       */
      charCodeAt: PropTypes.func.isRequired,
      /**
       * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
       * value of the UTF-16 encoded code point starting at the string element at position pos in
       * the String resulting from converting this object to a String.
       * If there is no element at that position, the result is undefined.
       * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
       */
      codePointAt: PropTypes.func.isRequired,
      /**
       * Returns a string that contains the concatenation of two or more strings.
       * @param strings The strings to append to the end of the string.
       */
      concat: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * endPosition – length(this). Otherwise returns false.
       */
      endsWith: PropTypes.func.isRequired,
      /**
       * Returns a <tt> HTML element
       */
      fixed: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the color attribute value
       */
      fontcolor: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the size attribute value
       */
      fontsize: PropTypes.func.isRequired,
      /**
       * Returns true if searchString appears as a substring of the result of converting this
       * object to a String, at one or more positions that are
       * greater than or equal to position; otherwise, returns false.
       * @param searchString search string
       * @param position If position is undefined, 0 is assumed, so as to search all of the String.
       */
      includes: PropTypes.func.isRequired,
      /**
       * Returns the position of the first occurrence of a substring.
       * @param searchString The substring to search for in the string
       * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
       */
      indexOf: PropTypes.func.isRequired,
      /**
       * Returns an <i> HTML element
       */
      italics: PropTypes.func.isRequired,
      /**
       * Returns the last occurrence of a substring in the string.
       * @param searchString The substring to search for.
       * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
       */
      lastIndexOf: PropTypes.func.isRequired,
      /**
       * Returns the length of a String object.
       */
      length: PropTypes.number.isRequired,
      /**
       * Returns an <a> HTML element and sets the href attribute value
       */
      link: PropTypes.func.isRequired,
      /**
       * Determines whether two strings are equivalent in the current locale.
       * @param that String to compare to target string
       */
      localeCompare: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an array containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      match: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an iterable of matches
       * containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      matchAll: PropTypes.func.isRequired,
      /**
       * Returns the String value result of normalizing the string into the normalization form
       * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
       * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
       * is "NFC"
       */
      normalize: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the end (right) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padEnd: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the start (left) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padStart: PropTypes.func.isRequired,
      /**
       * Returns a String value that is made from count copies appended together. If count is 0,
       * the empty string is returned.
       * @param count number of copies to append
       */
      repeat: PropTypes.func.isRequired,
      /**
       * Replaces text in a string, using a regular expression or search string.
       * @param searchValue A string to search for.
       * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
       */
      replace: PropTypes.func.isRequired,
      /**
       * Finds the first substring match in a regular expression search.
       * @param regexp The regular expression pattern and applicable flags.
       */
      search: PropTypes.func.isRequired,
      /**
       * Returns a section of a string.
       * @param start The index to the beginning of the specified portion of stringObj.
       * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
       * If this value is not specified, the substring continues to the end of stringObj.
       */
      slice: PropTypes.func.isRequired,
      /**
       * Returns a <small> HTML element
       */
      small: PropTypes.func.isRequired,
      /**
       * Split a string into substrings using the specified separator and return them as an array.
       * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
       * @param limit A value used to limit the number of elements returned in the array.
       */
      split: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * position. Otherwise returns false.
       */
      startsWith: PropTypes.func.isRequired,
      /**
       * Returns a <strike> HTML element
       */
      strike: PropTypes.func.isRequired,
      /**
       * Returns a <sub> HTML element
       */
      sub: PropTypes.func.isRequired,
      /**
       * Gets a substring beginning at the specified location and having the specified length.
       * @param from The starting position of the desired substring. The index of the first character in the string is zero.
       * @param length The number of characters to include in the returned substring.
       */
      substr: PropTypes.func.isRequired,
      /**
       * Returns the substring at the specified location within a String object.
       * @param start The zero-based index number indicating the beginning of the substring.
       * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
       * If end is omitted, the characters from start through the end of the original string are returned.
       */
      substring: PropTypes.func.isRequired,
      /**
       * Returns a <sup> HTML element
       */
      sup: PropTypes.func.isRequired,
      /**
       * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
       */
      toLocaleLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
       */
      toLocaleUpperCase: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to lowercase.
       */
      toLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string representation of a string.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to uppercase.
       */
      toUpperCase: PropTypes.func.isRequired,
      /**
       * Removes the leading and trailing white space and line terminator characters from a string.
       */
      trim: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimEnd: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimLeft: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimRight: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimStart: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
  ]),
  /**
   * The CSS justify-items property defines the default justify-self for all items of the box, giving them all
   * a default way of justifying each box along the appropriate axis.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/justify-items)
   */
  justifyItems: PropTypes.oneOfType([
    PropTypes.oneOf([
      "-moz-initial",
      "baseline",
      "center",
      "end",
      "flex-end",
      "flex-start",
      "inherit",
      "initial",
      "left",
      "legacy",
      "normal",
      "revert",
      "right",
      "self-end",
      "self-start",
      "start",
      "stretch",
      "unset",
    ]),
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([
          "-moz-initial",
          "baseline",
          "center",
          "end",
          "flex-end",
          "flex-start",
          "inherit",
          "initial",
          "left",
          "legacy",
          "normal",
          "revert",
          "right",
          "self-end",
          "self-start",
          "start",
          "stretch",
          "unset",
          null,
        ]),
        PropTypes.shape({
          /**
           * Iterator
           */
          "__@iterator": PropTypes.func.isRequired,
          /**
           * Returns an <a> HTML anchor element and sets the name attribute to the text value
           * @param name
           */
          anchor: PropTypes.func.isRequired,
          /**
           * Returns a <big> HTML element
           */
          big: PropTypes.func.isRequired,
          /**
           * Returns a <blink> HTML element
           */
          blink: PropTypes.func.isRequired,
          /**
           * Returns a <b> HTML element
           */
          bold: PropTypes.func.isRequired,
          /**
           * Returns the character at the specified index.
           * @param pos The zero-based index of the desired character.
           */
          charAt: PropTypes.func.isRequired,
          /**
           * Returns the Unicode value of the character at the specified location.
           * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
           */
          charCodeAt: PropTypes.func.isRequired,
          /**
           * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
           * value of the UTF-16 encoded code point starting at the string element at position pos in
           * the String resulting from converting this object to a String.
           * If there is no element at that position, the result is undefined.
           * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
           */
          codePointAt: PropTypes.func.isRequired,
          /**
           * Returns a string that contains the concatenation of two or more strings.
           * @param strings The strings to append to the end of the string.
           */
          concat: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * endPosition – length(this). Otherwise returns false.
           */
          endsWith: PropTypes.func.isRequired,
          /**
           * Returns a <tt> HTML element
           */
          fixed: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the color attribute value
           */
          fontcolor: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the size attribute value
           */
          fontsize: PropTypes.func.isRequired,
          /**
           * Returns true if searchString appears as a substring of the result of converting this
           * object to a String, at one or more positions that are
           * greater than or equal to position; otherwise, returns false.
           * @param searchString search string
           * @param position If position is undefined, 0 is assumed, so as to search all of the String.
           */
          includes: PropTypes.func.isRequired,
          /**
           * Returns the position of the first occurrence of a substring.
           * @param searchString The substring to search for in the string
           * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
           */
          indexOf: PropTypes.func.isRequired,
          /**
           * Returns an <i> HTML element
           */
          italics: PropTypes.func.isRequired,
          /**
           * Returns the last occurrence of a substring in the string.
           * @param searchString The substring to search for.
           * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
           */
          lastIndexOf: PropTypes.func.isRequired,
          /**
           * Returns the length of a String object.
           */
          length: PropTypes.number.isRequired,
          /**
           * Returns an <a> HTML element and sets the href attribute value
           */
          link: PropTypes.func.isRequired,
          /**
           * Determines whether two strings are equivalent in the current locale.
           * @param that String to compare to target string
           */
          localeCompare: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an array containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          match: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an iterable of matches
           * containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          matchAll: PropTypes.func.isRequired,
          /**
           * Returns the String value result of normalizing the string into the normalization form
           * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
           * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
           * is "NFC"
           */
          normalize: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the end (right) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padEnd: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the start (left) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padStart: PropTypes.func.isRequired,
          /**
           * Returns a String value that is made from count copies appended together. If count is 0,
           * the empty string is returned.
           * @param count number of copies to append
           */
          repeat: PropTypes.func.isRequired,
          /**
           * Replaces text in a string, using a regular expression or search string.
           * @param searchValue A string to search for.
           * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
           */
          replace: PropTypes.func.isRequired,
          /**
           * Finds the first substring match in a regular expression search.
           * @param regexp The regular expression pattern and applicable flags.
           */
          search: PropTypes.func.isRequired,
          /**
           * Returns a section of a string.
           * @param start The index to the beginning of the specified portion of stringObj.
           * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
           * If this value is not specified, the substring continues to the end of stringObj.
           */
          slice: PropTypes.func.isRequired,
          /**
           * Returns a <small> HTML element
           */
          small: PropTypes.func.isRequired,
          /**
           * Split a string into substrings using the specified separator and return them as an array.
           * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
           * @param limit A value used to limit the number of elements returned in the array.
           */
          split: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * position. Otherwise returns false.
           */
          startsWith: PropTypes.func.isRequired,
          /**
           * Returns a <strike> HTML element
           */
          strike: PropTypes.func.isRequired,
          /**
           * Returns a <sub> HTML element
           */
          sub: PropTypes.func.isRequired,
          /**
           * Gets a substring beginning at the specified location and having the specified length.
           * @param from The starting position of the desired substring. The index of the first character in the string is zero.
           * @param length The number of characters to include in the returned substring.
           */
          substr: PropTypes.func.isRequired,
          /**
           * Returns the substring at the specified location within a String object.
           * @param start The zero-based index number indicating the beginning of the substring.
           * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
           * If end is omitted, the characters from start through the end of the original string are returned.
           */
          substring: PropTypes.func.isRequired,
          /**
           * Returns a <sup> HTML element
           */
          sup: PropTypes.func.isRequired,
          /**
           * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
           */
          toLocaleLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
           */
          toLocaleUpperCase: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to lowercase.
           */
          toLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string representation of a string.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to uppercase.
           */
          toUpperCase: PropTypes.func.isRequired,
          /**
           * Removes the leading and trailing white space and line terminator characters from a string.
           */
          trim: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimEnd: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimLeft: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimRight: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimStart: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
      ])
    ),
    PropTypes.object,
    PropTypes.shape({
      /**
       * Iterator
       */
      "__@iterator": PropTypes.func.isRequired,
      /**
       * Returns an <a> HTML anchor element and sets the name attribute to the text value
       * @param name
       */
      anchor: PropTypes.func.isRequired,
      /**
       * Returns a <big> HTML element
       */
      big: PropTypes.func.isRequired,
      /**
       * Returns a <blink> HTML element
       */
      blink: PropTypes.func.isRequired,
      /**
       * Returns a <b> HTML element
       */
      bold: PropTypes.func.isRequired,
      /**
       * Returns the character at the specified index.
       * @param pos The zero-based index of the desired character.
       */
      charAt: PropTypes.func.isRequired,
      /**
       * Returns the Unicode value of the character at the specified location.
       * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
       */
      charCodeAt: PropTypes.func.isRequired,
      /**
       * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
       * value of the UTF-16 encoded code point starting at the string element at position pos in
       * the String resulting from converting this object to a String.
       * If there is no element at that position, the result is undefined.
       * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
       */
      codePointAt: PropTypes.func.isRequired,
      /**
       * Returns a string that contains the concatenation of two or more strings.
       * @param strings The strings to append to the end of the string.
       */
      concat: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * endPosition – length(this). Otherwise returns false.
       */
      endsWith: PropTypes.func.isRequired,
      /**
       * Returns a <tt> HTML element
       */
      fixed: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the color attribute value
       */
      fontcolor: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the size attribute value
       */
      fontsize: PropTypes.func.isRequired,
      /**
       * Returns true if searchString appears as a substring of the result of converting this
       * object to a String, at one or more positions that are
       * greater than or equal to position; otherwise, returns false.
       * @param searchString search string
       * @param position If position is undefined, 0 is assumed, so as to search all of the String.
       */
      includes: PropTypes.func.isRequired,
      /**
       * Returns the position of the first occurrence of a substring.
       * @param searchString The substring to search for in the string
       * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
       */
      indexOf: PropTypes.func.isRequired,
      /**
       * Returns an <i> HTML element
       */
      italics: PropTypes.func.isRequired,
      /**
       * Returns the last occurrence of a substring in the string.
       * @param searchString The substring to search for.
       * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
       */
      lastIndexOf: PropTypes.func.isRequired,
      /**
       * Returns the length of a String object.
       */
      length: PropTypes.number.isRequired,
      /**
       * Returns an <a> HTML element and sets the href attribute value
       */
      link: PropTypes.func.isRequired,
      /**
       * Determines whether two strings are equivalent in the current locale.
       * @param that String to compare to target string
       */
      localeCompare: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an array containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      match: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an iterable of matches
       * containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      matchAll: PropTypes.func.isRequired,
      /**
       * Returns the String value result of normalizing the string into the normalization form
       * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
       * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
       * is "NFC"
       */
      normalize: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the end (right) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padEnd: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the start (left) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padStart: PropTypes.func.isRequired,
      /**
       * Returns a String value that is made from count copies appended together. If count is 0,
       * the empty string is returned.
       * @param count number of copies to append
       */
      repeat: PropTypes.func.isRequired,
      /**
       * Replaces text in a string, using a regular expression or search string.
       * @param searchValue A string to search for.
       * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
       */
      replace: PropTypes.func.isRequired,
      /**
       * Finds the first substring match in a regular expression search.
       * @param regexp The regular expression pattern and applicable flags.
       */
      search: PropTypes.func.isRequired,
      /**
       * Returns a section of a string.
       * @param start The index to the beginning of the specified portion of stringObj.
       * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
       * If this value is not specified, the substring continues to the end of stringObj.
       */
      slice: PropTypes.func.isRequired,
      /**
       * Returns a <small> HTML element
       */
      small: PropTypes.func.isRequired,
      /**
       * Split a string into substrings using the specified separator and return them as an array.
       * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
       * @param limit A value used to limit the number of elements returned in the array.
       */
      split: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * position. Otherwise returns false.
       */
      startsWith: PropTypes.func.isRequired,
      /**
       * Returns a <strike> HTML element
       */
      strike: PropTypes.func.isRequired,
      /**
       * Returns a <sub> HTML element
       */
      sub: PropTypes.func.isRequired,
      /**
       * Gets a substring beginning at the specified location and having the specified length.
       * @param from The starting position of the desired substring. The index of the first character in the string is zero.
       * @param length The number of characters to include in the returned substring.
       */
      substr: PropTypes.func.isRequired,
      /**
       * Returns the substring at the specified location within a String object.
       * @param start The zero-based index number indicating the beginning of the substring.
       * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
       * If end is omitted, the characters from start through the end of the original string are returned.
       */
      substring: PropTypes.func.isRequired,
      /**
       * Returns a <sup> HTML element
       */
      sup: PropTypes.func.isRequired,
      /**
       * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
       */
      toLocaleLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
       */
      toLocaleUpperCase: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to lowercase.
       */
      toLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string representation of a string.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to uppercase.
       */
      toUpperCase: PropTypes.func.isRequired,
      /**
       * Removes the leading and trailing white space and line terminator characters from a string.
       */
      trim: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimEnd: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimLeft: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimRight: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimStart: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
  ]),
  /**
   * The CSS justify-self property set the way a box is justified inside its alignment container along
   * the appropriate axis.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/justify-self)
   */
  justifySelf: PropTypes.oneOfType([
    PropTypes.oneOf([
      "-moz-initial",
      "auto",
      "baseline",
      "center",
      "end",
      "flex-end",
      "flex-start",
      "inherit",
      "initial",
      "left",
      "normal",
      "revert",
      "right",
      "self-end",
      "self-start",
      "start",
      "stretch",
      "unset",
    ]),
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([
          "-moz-initial",
          "auto",
          "baseline",
          "center",
          "end",
          "flex-end",
          "flex-start",
          "inherit",
          "initial",
          "left",
          "normal",
          "revert",
          "right",
          "self-end",
          "self-start",
          "start",
          "stretch",
          "unset",
          null,
        ]),
        PropTypes.shape({
          /**
           * Iterator
           */
          "__@iterator": PropTypes.func.isRequired,
          /**
           * Returns an <a> HTML anchor element and sets the name attribute to the text value
           * @param name
           */
          anchor: PropTypes.func.isRequired,
          /**
           * Returns a <big> HTML element
           */
          big: PropTypes.func.isRequired,
          /**
           * Returns a <blink> HTML element
           */
          blink: PropTypes.func.isRequired,
          /**
           * Returns a <b> HTML element
           */
          bold: PropTypes.func.isRequired,
          /**
           * Returns the character at the specified index.
           * @param pos The zero-based index of the desired character.
           */
          charAt: PropTypes.func.isRequired,
          /**
           * Returns the Unicode value of the character at the specified location.
           * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
           */
          charCodeAt: PropTypes.func.isRequired,
          /**
           * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
           * value of the UTF-16 encoded code point starting at the string element at position pos in
           * the String resulting from converting this object to a String.
           * If there is no element at that position, the result is undefined.
           * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
           */
          codePointAt: PropTypes.func.isRequired,
          /**
           * Returns a string that contains the concatenation of two or more strings.
           * @param strings The strings to append to the end of the string.
           */
          concat: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * endPosition – length(this). Otherwise returns false.
           */
          endsWith: PropTypes.func.isRequired,
          /**
           * Returns a <tt> HTML element
           */
          fixed: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the color attribute value
           */
          fontcolor: PropTypes.func.isRequired,
          /**
           * Returns a <font> HTML element and sets the size attribute value
           */
          fontsize: PropTypes.func.isRequired,
          /**
           * Returns true if searchString appears as a substring of the result of converting this
           * object to a String, at one or more positions that are
           * greater than or equal to position; otherwise, returns false.
           * @param searchString search string
           * @param position If position is undefined, 0 is assumed, so as to search all of the String.
           */
          includes: PropTypes.func.isRequired,
          /**
           * Returns the position of the first occurrence of a substring.
           * @param searchString The substring to search for in the string
           * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
           */
          indexOf: PropTypes.func.isRequired,
          /**
           * Returns an <i> HTML element
           */
          italics: PropTypes.func.isRequired,
          /**
           * Returns the last occurrence of a substring in the string.
           * @param searchString The substring to search for.
           * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
           */
          lastIndexOf: PropTypes.func.isRequired,
          /**
           * Returns the length of a String object.
           */
          length: PropTypes.number.isRequired,
          /**
           * Returns an <a> HTML element and sets the href attribute value
           */
          link: PropTypes.func.isRequired,
          /**
           * Determines whether two strings are equivalent in the current locale.
           * @param that String to compare to target string
           */
          localeCompare: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an array containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          match: PropTypes.func.isRequired,
          /**
           * Matches a string with a regular expression, and returns an iterable of matches
           * containing the results of that search.
           * @param regexp A variable name or string literal containing the regular expression pattern and flags.
           */
          matchAll: PropTypes.func.isRequired,
          /**
           * Returns the String value result of normalizing the string into the normalization form
           * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
           * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
           * is "NFC"
           */
          normalize: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the end (right) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padEnd: PropTypes.func.isRequired,
          /**
           * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
           * The padding is applied from the start (left) of the current string.
           *
           * @param maxLength The length of the resulting string once the current string has been padded.
           *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
           *
           * @param fillString The string to pad the current string with.
           *        If this string is too long, it will be truncated and the left-most part will be applied.
           *        The default value for this parameter is " " (U+0020).
           */
          padStart: PropTypes.func.isRequired,
          /**
           * Returns a String value that is made from count copies appended together. If count is 0,
           * the empty string is returned.
           * @param count number of copies to append
           */
          repeat: PropTypes.func.isRequired,
          /**
           * Replaces text in a string, using a regular expression or search string.
           * @param searchValue A string to search for.
           * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
           */
          replace: PropTypes.func.isRequired,
          /**
           * Finds the first substring match in a regular expression search.
           * @param regexp The regular expression pattern and applicable flags.
           */
          search: PropTypes.func.isRequired,
          /**
           * Returns a section of a string.
           * @param start The index to the beginning of the specified portion of stringObj.
           * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
           * If this value is not specified, the substring continues to the end of stringObj.
           */
          slice: PropTypes.func.isRequired,
          /**
           * Returns a <small> HTML element
           */
          small: PropTypes.func.isRequired,
          /**
           * Split a string into substrings using the specified separator and return them as an array.
           * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
           * @param limit A value used to limit the number of elements returned in the array.
           */
          split: PropTypes.func.isRequired,
          /**
           * Returns true if the sequence of elements of searchString converted to a String is the
           * same as the corresponding elements of this object (converted to a String) starting at
           * position. Otherwise returns false.
           */
          startsWith: PropTypes.func.isRequired,
          /**
           * Returns a <strike> HTML element
           */
          strike: PropTypes.func.isRequired,
          /**
           * Returns a <sub> HTML element
           */
          sub: PropTypes.func.isRequired,
          /**
           * Gets a substring beginning at the specified location and having the specified length.
           * @param from The starting position of the desired substring. The index of the first character in the string is zero.
           * @param length The number of characters to include in the returned substring.
           */
          substr: PropTypes.func.isRequired,
          /**
           * Returns the substring at the specified location within a String object.
           * @param start The zero-based index number indicating the beginning of the substring.
           * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
           * If end is omitted, the characters from start through the end of the original string are returned.
           */
          substring: PropTypes.func.isRequired,
          /**
           * Returns a <sup> HTML element
           */
          sup: PropTypes.func.isRequired,
          /**
           * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
           */
          toLocaleLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
           */
          toLocaleUpperCase: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to lowercase.
           */
          toLowerCase: PropTypes.func.isRequired,
          /**
           * Returns a string representation of a string.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Converts all the alphabetic characters in a string to uppercase.
           */
          toUpperCase: PropTypes.func.isRequired,
          /**
           * Removes the leading and trailing white space and line terminator characters from a string.
           */
          trim: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimEnd: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimLeft: PropTypes.func.isRequired,
          /**
           * Removes the trailing white space and line terminator characters from a string.
           */
          trimRight: PropTypes.func.isRequired,
          /**
           * Removes the leading white space and line terminator characters from a string.
           */
          trimStart: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
      ])
    ),
    PropTypes.object,
    PropTypes.shape({
      /**
       * Iterator
       */
      "__@iterator": PropTypes.func.isRequired,
      /**
       * Returns an <a> HTML anchor element and sets the name attribute to the text value
       * @param name
       */
      anchor: PropTypes.func.isRequired,
      /**
       * Returns a <big> HTML element
       */
      big: PropTypes.func.isRequired,
      /**
       * Returns a <blink> HTML element
       */
      blink: PropTypes.func.isRequired,
      /**
       * Returns a <b> HTML element
       */
      bold: PropTypes.func.isRequired,
      /**
       * Returns the character at the specified index.
       * @param pos The zero-based index of the desired character.
       */
      charAt: PropTypes.func.isRequired,
      /**
       * Returns the Unicode value of the character at the specified location.
       * @param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.
       */
      charCodeAt: PropTypes.func.isRequired,
      /**
       * Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point
       * value of the UTF-16 encoded code point starting at the string element at position pos in
       * the String resulting from converting this object to a String.
       * If there is no element at that position, the result is undefined.
       * If a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.
       */
      codePointAt: PropTypes.func.isRequired,
      /**
       * Returns a string that contains the concatenation of two or more strings.
       * @param strings The strings to append to the end of the string.
       */
      concat: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * endPosition – length(this). Otherwise returns false.
       */
      endsWith: PropTypes.func.isRequired,
      /**
       * Returns a <tt> HTML element
       */
      fixed: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the color attribute value
       */
      fontcolor: PropTypes.func.isRequired,
      /**
       * Returns a <font> HTML element and sets the size attribute value
       */
      fontsize: PropTypes.func.isRequired,
      /**
       * Returns true if searchString appears as a substring of the result of converting this
       * object to a String, at one or more positions that are
       * greater than or equal to position; otherwise, returns false.
       * @param searchString search string
       * @param position If position is undefined, 0 is assumed, so as to search all of the String.
       */
      includes: PropTypes.func.isRequired,
      /**
       * Returns the position of the first occurrence of a substring.
       * @param searchString The substring to search for in the string
       * @param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.
       */
      indexOf: PropTypes.func.isRequired,
      /**
       * Returns an <i> HTML element
       */
      italics: PropTypes.func.isRequired,
      /**
       * Returns the last occurrence of a substring in the string.
       * @param searchString The substring to search for.
       * @param position The index at which to begin searching. If omitted, the search begins at the end of the string.
       */
      lastIndexOf: PropTypes.func.isRequired,
      /**
       * Returns the length of a String object.
       */
      length: PropTypes.number.isRequired,
      /**
       * Returns an <a> HTML element and sets the href attribute value
       */
      link: PropTypes.func.isRequired,
      /**
       * Determines whether two strings are equivalent in the current locale.
       * @param that String to compare to target string
       */
      localeCompare: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an array containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      match: PropTypes.func.isRequired,
      /**
       * Matches a string with a regular expression, and returns an iterable of matches
       * containing the results of that search.
       * @param regexp A variable name or string literal containing the regular expression pattern and flags.
       */
      matchAll: PropTypes.func.isRequired,
      /**
       * Returns the String value result of normalizing the string into the normalization form
       * named by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.
       * @param form Applicable values: "NFC", "NFD", "NFKC", or "NFKD", If not specified default
       * is "NFC"
       */
      normalize: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the end (right) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padEnd: PropTypes.func.isRequired,
      /**
       * Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.
       * The padding is applied from the start (left) of the current string.
       *
       * @param maxLength The length of the resulting string once the current string has been padded.
       *        If this parameter is smaller than the current string's length, the current string will be returned as it is.
       *
       * @param fillString The string to pad the current string with.
       *        If this string is too long, it will be truncated and the left-most part will be applied.
       *        The default value for this parameter is " " (U+0020).
       */
      padStart: PropTypes.func.isRequired,
      /**
       * Returns a String value that is made from count copies appended together. If count is 0,
       * the empty string is returned.
       * @param count number of copies to append
       */
      repeat: PropTypes.func.isRequired,
      /**
       * Replaces text in a string, using a regular expression or search string.
       * @param searchValue A string to search for.
       * @param replaceValue A string containing the text to replace for every successful match of searchValue in this string.
       */
      replace: PropTypes.func.isRequired,
      /**
       * Finds the first substring match in a regular expression search.
       * @param regexp The regular expression pattern and applicable flags.
       */
      search: PropTypes.func.isRequired,
      /**
       * Returns a section of a string.
       * @param start The index to the beginning of the specified portion of stringObj.
       * @param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
       * If this value is not specified, the substring continues to the end of stringObj.
       */
      slice: PropTypes.func.isRequired,
      /**
       * Returns a <small> HTML element
       */
      small: PropTypes.func.isRequired,
      /**
       * Split a string into substrings using the specified separator and return them as an array.
       * @param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.
       * @param limit A value used to limit the number of elements returned in the array.
       */
      split: PropTypes.func.isRequired,
      /**
       * Returns true if the sequence of elements of searchString converted to a String is the
       * same as the corresponding elements of this object (converted to a String) starting at
       * position. Otherwise returns false.
       */
      startsWith: PropTypes.func.isRequired,
      /**
       * Returns a <strike> HTML element
       */
      strike: PropTypes.func.isRequired,
      /**
       * Returns a <sub> HTML element
       */
      sub: PropTypes.func.isRequired,
      /**
       * Gets a substring beginning at the specified location and having the specified length.
       * @param from The starting position of the desired substring. The index of the first character in the string is zero.
       * @param length The number of characters to include in the returned substring.
       */
      substr: PropTypes.func.isRequired,
      /**
       * Returns the substring at the specified location within a String object.
       * @param start The zero-based index number indicating the beginning of the substring.
       * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
       * If end is omitted, the characters from start through the end of the original string are returned.
       */
      substring: PropTypes.func.isRequired,
      /**
       * Returns a <sup> HTML element
       */
      sup: PropTypes.func.isRequired,
      /**
       * Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.
       */
      toLocaleLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.
       */
      toLocaleUpperCase: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to lowercase.
       */
      toLowerCase: PropTypes.func.isRequired,
      /**
       * Returns a string representation of a string.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Converts all the alphabetic characters in a string to uppercase.
       */
      toUpperCase: PropTypes.func.isRequired,
      /**
       * Removes the leading and trailing white space and line terminator characters from a string.
       */
      trim: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimEnd: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimLeft: PropTypes.func.isRequired,
      /**
       * Removes the trailing white space and line terminator characters from a string.
       */
      trimRight: PropTypes.func.isRequired,
      /**
       * Removes the leading white space and line terminator characters from a string.
       */
      trimStart: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
  ]),
  /**
   * Color scheme of navigation component
   */
  navigationType: PropTypes.oneOf(["black", "dark", "light", "white"]),
  /**
   * Defines the offset of navigation bar
   */
  offset: PropTypes.string,
  /**
   * The order CSS property sets the order to lay out an item in a flex or grid container. Items in a container
   * are sorted by ascending order value and then by their source code order.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/order)
   */
  order: PropTypes.oneOfType([
    PropTypes.oneOf(["-moz-initial", "inherit", "initial", "revert", "unset"]),
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([
          "-moz-initial",
          "inherit",
          "initial",
          "revert",
          "unset",
          null,
        ]),
        PropTypes.shape({
          /**
           * Returns a string containing a number represented in exponential notation.
           * @param fractionDigits Number of digits after the decimal point. Must be in the range 0 - 20, inclusive.
           */
          toExponential: PropTypes.func.isRequired,
          /**
           * Returns a string representing a number in fixed-point notation.
           * @param fractionDigits Number of digits after the decimal point. Must be in the range 0 - 20, inclusive.
           */
          toFixed: PropTypes.func.isRequired,
          /**
           * Converts a number to a string by using the current or specified locale.
           * @param locales A locale string or array of locale strings that contain one or more language or locale tags. If you include more than one locale string, list them in descending order of priority so that the first entry is the preferred locale. If you omit this parameter, the default locale of the JavaScript runtime is used.
           * @param options An object that contains one or more properties that specify comparison options.
           */
          toLocaleString: PropTypes.func.isRequired,
          /**
           * Returns a string containing a number represented either in exponential or fixed-point notation with a specified number of digits.
           * @param precision Number of significant digits. Must be in the range 1 - 21, inclusive.
           */
          toPrecision: PropTypes.func.isRequired,
          /**
           * Returns a string representation of an object.
           * @param radix Specifies a radix for converting numeric values to strings. This value is only used for numbers.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
      ])
    ),
    PropTypes.object,
    PropTypes.shape({
      /**
       * Returns a string containing a number represented in exponential notation.
       * @param fractionDigits Number of digits after the decimal point. Must be in the range 0 - 20, inclusive.
       */
      toExponential: PropTypes.func.isRequired,
      /**
       * Returns a string representing a number in fixed-point notation.
       * @param fractionDigits Number of digits after the decimal point. Must be in the range 0 - 20, inclusive.
       */
      toFixed: PropTypes.func.isRequired,
      /**
       * Converts a number to a string by using the current or specified locale.
       * @param locales A locale string or array of locale strings that contain one or more language or locale tags. If you include more than one locale string, list them in descending order of priority so that the first entry is the preferred locale. If you omit this parameter, the default locale of the JavaScript runtime is used.
       * @param options An object that contains one or more properties that specify comparison options.
       */
      toLocaleString: PropTypes.func.isRequired,
      /**
       * Returns a string containing a number represented either in exponential or fixed-point notation with a specified number of digits.
       * @param precision Number of significant digits. Must be in the range 1 - 21, inclusive.
       */
      toPrecision: PropTypes.func.isRequired,
      /**
       * Returns a string representation of an object.
       * @param radix Specifies a radix for converting numeric values to strings. This value is only used for numbers.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
  ]),
  /**
   * Defines whether the navigation bar should be positioned top or bottom
   */
  orientation: PropTypes.oneOf(["bottom", "top"]),
  /**
   * Padding on top, left, bottom and right
   */
  p: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          "__@toStringTag": PropTypes.string.isRequired,
          /**
           * Expose the [[Description]] internal slot of a symbol directly.
           */
          description: PropTypes.string,
          /**
           * Returns a string representation of an object.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      "__@toStringTag": PropTypes.string.isRequired,
      /**
       * Expose the [[Description]] internal slot of a symbol directly.
       */
      description: PropTypes.string,
      /**
       * Returns a string representation of an object.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  /**
   * Padding on top, left, bottom and right
   */
  padding: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          "__@toStringTag": PropTypes.string.isRequired,
          /**
           * Expose the [[Description]] internal slot of a symbol directly.
           */
          description: PropTypes.string,
          /**
           * Returns a string representation of an object.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      "__@toStringTag": PropTypes.string.isRequired,
      /**
       * Expose the [[Description]] internal slot of a symbol directly.
       */
      description: PropTypes.string,
      /**
       * Returns a string representation of an object.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  /**
   * Padding on bottom
   */
  paddingBottom: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          "__@toStringTag": PropTypes.string.isRequired,
          /**
           * Expose the [[Description]] internal slot of a symbol directly.
           */
          description: PropTypes.string,
          /**
           * Returns a string representation of an object.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      "__@toStringTag": PropTypes.string.isRequired,
      /**
       * Expose the [[Description]] internal slot of a symbol directly.
       */
      description: PropTypes.string,
      /**
       * Returns a string representation of an object.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  /**
   * Padding on left
   */
  paddingLeft: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          "__@toStringTag": PropTypes.string.isRequired,
          /**
           * Expose the [[Description]] internal slot of a symbol directly.
           */
          description: PropTypes.string,
          /**
           * Returns a string representation of an object.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      "__@toStringTag": PropTypes.string.isRequired,
      /**
       * Expose the [[Description]] internal slot of a symbol directly.
       */
      description: PropTypes.string,
      /**
       * Returns a string representation of an object.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  /**
   * Padding on right
   */
  paddingRight: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          "__@toStringTag": PropTypes.string.isRequired,
          /**
           * Expose the [[Description]] internal slot of a symbol directly.
           */
          description: PropTypes.string,
          /**
           * Returns a string representation of an object.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      "__@toStringTag": PropTypes.string.isRequired,
      /**
       * Expose the [[Description]] internal slot of a symbol directly.
       */
      description: PropTypes.string,
      /**
       * Returns a string representation of an object.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  /**
   * Padding on top
   */
  paddingTop: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          "__@toStringTag": PropTypes.string.isRequired,
          /**
           * Expose the [[Description]] internal slot of a symbol directly.
           */
          description: PropTypes.string,
          /**
           * Returns a string representation of an object.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      "__@toStringTag": PropTypes.string.isRequired,
      /**
       * Expose the [[Description]] internal slot of a symbol directly.
       */
      description: PropTypes.string,
      /**
       * Returns a string representation of an object.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  /**
   * Padding on left and right
   */
  paddingX: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          "__@toStringTag": PropTypes.string.isRequired,
          /**
           * Expose the [[Description]] internal slot of a symbol directly.
           */
          description: PropTypes.string,
          /**
           * Returns a string representation of an object.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      "__@toStringTag": PropTypes.string.isRequired,
      /**
       * Expose the [[Description]] internal slot of a symbol directly.
       */
      description: PropTypes.string,
      /**
       * Returns a string representation of an object.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  /**
   * Padding on top and bottom
   */
  paddingY: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          "__@toStringTag": PropTypes.string.isRequired,
          /**
           * Expose the [[Description]] internal slot of a symbol directly.
           */
          description: PropTypes.string,
          /**
           * Returns a string representation of an object.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      "__@toStringTag": PropTypes.string.isRequired,
      /**
       * Expose the [[Description]] internal slot of a symbol directly.
       */
      description: PropTypes.string,
      /**
       * Returns a string representation of an object.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  /**
   * Padding on bottom
   */
  pb: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          "__@toStringTag": PropTypes.string.isRequired,
          /**
           * Expose the [[Description]] internal slot of a symbol directly.
           */
          description: PropTypes.string,
          /**
           * Returns a string representation of an object.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      "__@toStringTag": PropTypes.string.isRequired,
      /**
       * Expose the [[Description]] internal slot of a symbol directly.
       */
      description: PropTypes.string,
      /**
       * Returns a string representation of an object.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  /**
   * Padding on left
   */
  pl: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          "__@toStringTag": PropTypes.string.isRequired,
          /**
           * Expose the [[Description]] internal slot of a symbol directly.
           */
          description: PropTypes.string,
          /**
           * Returns a string representation of an object.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      "__@toStringTag": PropTypes.string.isRequired,
      /**
       * Expose the [[Description]] internal slot of a symbol directly.
       */
      description: PropTypes.string,
      /**
       * Returns a string representation of an object.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  /**
   * Defines whether the navigation bar should be positioned fixed or sticky
   */
  position: PropTypes.oneOf(["fixed", "sticky"]),
  /**
   * Padding on right
   */
  pr: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          "__@toStringTag": PropTypes.string.isRequired,
          /**
           * Expose the [[Description]] internal slot of a symbol directly.
           */
          description: PropTypes.string,
          /**
           * Returns a string representation of an object.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      "__@toStringTag": PropTypes.string.isRequired,
      /**
       * Expose the [[Description]] internal slot of a symbol directly.
       */
      description: PropTypes.string,
      /**
       * Returns a string representation of an object.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  /**
   * Padding on top
   */
  pt: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          "__@toStringTag": PropTypes.string.isRequired,
          /**
           * Expose the [[Description]] internal slot of a symbol directly.
           */
          description: PropTypes.string,
          /**
           * Returns a string representation of an object.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      "__@toStringTag": PropTypes.string.isRequired,
      /**
       * Expose the [[Description]] internal slot of a symbol directly.
       */
      description: PropTypes.string,
      /**
       * Returns a string representation of an object.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  /**
   * Padding on left and right
   */
  px: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          "__@toStringTag": PropTypes.string.isRequired,
          /**
           * Expose the [[Description]] internal slot of a symbol directly.
           */
          description: PropTypes.string,
          /**
           * Returns a string representation of an object.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      "__@toStringTag": PropTypes.string.isRequired,
      /**
       * Expose the [[Description]] internal slot of a symbol directly.
       */
      description: PropTypes.string,
      /**
       * Returns a string representation of an object.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  /**
   * Padding on top and bottom
   */
  py: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.number,
        PropTypes.shape({
          "__@toStringTag": PropTypes.string.isRequired,
          /**
           * Expose the [[Description]] internal slot of a symbol directly.
           */
          description: PropTypes.string,
          /**
           * Returns a string representation of an object.
           */
          toString: PropTypes.func.isRequired,
          /**
           * Returns the primitive value of the specified object.
           */
          valueOf: PropTypes.func.isRequired,
        }),
        PropTypes.string,
      ])
    ),
    PropTypes.number,
    PropTypes.object,
    PropTypes.shape({
      "__@toStringTag": PropTypes.string.isRequired,
      /**
       * Expose the [[Description]] internal slot of a symbol directly.
       */
      description: PropTypes.string,
      /**
       * Returns a string representation of an object.
       */
      toString: PropTypes.func.isRequired,
      /**
       * Returns the primitive value of the specified object.
       */
      valueOf: PropTypes.func.isRequired,
    }),
    PropTypes.string,
  ]),
  /**
   * Defines the offset of sticky navigation bar
   */
  stickyOffset: PropTypes.string,
  /**
   * Defines the position of sticky navigation bar
   */
  stickyPosition: PropTypes.oneOf(["bottom", "top"]),
};

export { NavigationBar };
export default NavigationBar;
